import React, { useState, useEffect, useRef } from "react";
import AddressAutocompleteInput from "../components/AddressAutocompleteInput";
import { useSearchParams, useNavigate } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import AuthModal from "../components/AuthModal";
import ProductQuickViewModal from "../components/ProductQuickViewModal";
import {
  BlockerChipsPanel,
  NotesPanel,
  CustomerNoteField,
  ServicePricingSelector,
} from "../components/BookingFlowPanels";
import SectionStatusHeader from "../components/SectionStatusHeader";
import {
  saveBookingDraft,
  loadBookingDraft,
  clearBookingDraft,
  wasPackagePurchasedDuringBooking,
} from "../utils/bookingDraft";
import SummaryReturnPrompt from "../components/SummaryReturnPrompt";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import { useCart } from "../hooks/useCart";
import { formatCurrency } from "../utils/displayHelpers";
import CancellationPolicyModal from "../components/CancellationPolicyModal";
import { NON_REFUNDABLE_PROCESSING_FEE_NOTICE } from "../utils/transactionPolicyText";
import {
  clearDirectBookingHold,
  getDirectBookingHold,
  saveDirectBookingHold,
} from "../utils/bookingReservationHold";
import { buildSlotUnavailableNotice } from "../utils/slotAvailabilityNotice";
import {
  reservePaymentTab,
  sendReservedTabToPayment,
} from "../utils/paymentTab";
import {
  BOOKING_BILLING_ADDRESS_RETURN_TARGET,
  buildSummaryBlockers,
  buildSummarySoftWarnings,
  getCollapsibleSectionStyle,
  getBillingAddressReturnTarget,
  getSectionBlockStyle,
  isBillingAddressRequired,
  shouldShowBillingAddressReturnPrompt,
} from "../utils/bookingFlowRules";
// F1: service pricing engine (display mirror - server remains authoritative)
import {
  isSelectionDrivenService,
  resolveServicePricing,
  resolveServiceLines,
} from "../utils/servicePricing";
import "./CustomerBooking.css";

const BOOKING_SLOT_HOLD_NOTICE =
    "we confirm this time again when you continue to payment. If it has been taken, you'll be asked to pick another time - you will not be charged.";
// F1: derive an end time from a start time + duration (mirrors the backend helper).
// Only used for per-unit quantities; every other booking keeps the slot's own endTime.
const addMinutesToTime24 = (time24, minutes) => {
  const [h, m] = String(time24 || "")
    .split(":")
    .map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const total = h * 60 + m + (Number(minutes) || 0);
  const wrapped = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(
    wrapped % 60,
  ).padStart(2, "0")}`;
};

/**
 * The guest's scoped package token, or null when there is not a usable one.
 *
 * A guest token lives **10 minutes**. The page used to send whatever was in sessionStorage on every
 * mount, so ten minutes after a guest verified, EVERY page load fired a request that could only fail:
 * a 401 in the console, a failed request in devtools and in any monitoring, and a pointless round trip.
 *
 * The expiry is read from the token itself to decide whether it is worth presenting — a freshness check
 * only. The SERVER still verifies the signature, the scope and the maxAge; nothing here is trusted. An
 * expired token is dropped rather than sent, which is also the honest state: the guest is a guest again.
 */
const GUEST_PACKAGE_TOKEN_KEY = "ogestyle_guest_package_token";
const GUEST_PACKAGE_TOKEN_SKEW_MS = 30 * 1000;

function readGuestPackageToken() {
  let token = null;
  try {
    token = sessionStorage.getItem(GUEST_PACKAGE_TOKEN_KEY);
  } catch (err) {
    return null;
  }
  if (!token) return null;

  try {
    const base64 = String(token.split(".")[1] || "")
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    if (
      payload?.exp &&
      payload.exp * 1000 <= Date.now() + GUEST_PACKAGE_TOKEN_SKEW_MS
    ) {
      sessionStorage.removeItem(GUEST_PACKAGE_TOKEN_KEY);
      return null;
    }
  } catch (err) {
    // Unreadable means unusable. Dropping it is better than presenting it to be refused.
    sessionStorage.removeItem(GUEST_PACKAGE_TOKEN_KEY);
    return null;
  }

  return token;
}

/**
 * ── WHAT THIS PACKAGE SAVED THEM (plan item 6, 2026-10-18) ─────────────────────────────────────────
 *
 * *"the package tile showing the likely saving"*. The purchase already carries both numbers: `pricePaid`, and the
 * FROZEN snapshot of its lines, each with `lineTotal` — the regular price of the sessions inside it. So this is a
 * subtraction of what the purchase itself recorded, not a second pricing rule; it is the same shape the for-sale
 * cards already use (`pkg.regularPrice - pkg.packagePrice`).
 *
 * `null` when it cannot be stated honestly — an older purchase whose snapshot carries no line values, or a package
 * that was not sold at a discount. A guessed saving is worse than no saving.
 *
 * Module scope on purpose: it is a pure reading of one document, and it is used both by the package card in the
 * booking flow and by any other surface that shows a purchase.
 */
function packageSavingAmount(pkg) {
  const lines = Array.isArray(pkg?.packageSnapshot?.services) ? pkg.packageSnapshot.services : [];
  const regular = lines.reduce((sum, line) => {
    const lineTotal = Number(line?.lineTotal);
    if (Number.isFinite(lineTotal) && lineTotal > 0) return sum + lineTotal;
    const price = Number(line?.sessionPrice);
    const quantity = Number(line?.quantity);
    return Number.isFinite(price) && Number.isFinite(quantity) ? sum + price * quantity : sum;
  }, 0);
  const paid = Number(pkg?.pricePaid) || 0;
  if (!(regular > 0) || !(paid > 0) || regular <= paid) return null;
  return Math.round((regular - paid) * 100) / 100;
}

/**
 * Money to two decimals, so a sum of resolved line prices cannot drift into a floating-point tail that the
 * summary then shows beside a total computed the other way.
 */
function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/**
 * ── WHICH SERVICES A PURCHASED PACKAGE COVERS, FOR THE PRICE THE CUSTOMER IS SHOWN (2026-10-27) ────────
 *
 * Split payment means a package can pay for SOME of the booked lines and the card for the rest, so the summary
 * has to know which is which before it states a figure. A package line names either the shop's template service
 * or, on some purchases, the service itself — and a booked service carries its own `_id` plus a `templateId`
 * pointing at the template. Both sides are therefore matched, exactly as `bookedServiceIds` does for the booking
 * as a whole.
 *
 * This is a DISPLAY rule only. The server decides the split and charges from it; a disagreement here can show a
 * stale figure but can never move money, and the Stripe page itemises the real lines.
 */
function packageCoveredServiceIdSet(pkg) {
  const lines = Array.isArray(pkg?.packageSnapshot?.services)
    ? pkg.packageSnapshot.services
    : Array.isArray(pkg?.services)
      ? pkg.services
      : [];
  const ids = new Set();
  for (const line of lines) {
    const raw = line?.serviceId;
    const id = String(raw?._id || raw || "");
    if (id) ids.add(id);
    const templateId = String(raw?.templateId || "");
    if (templateId) ids.add(templateId);
  }
  return ids;
}

/** Is this booked service one the package pays for? Both sides of the id match, as the set carries both. */
function serviceIsCoveredByPackage(coveredIds, svc) {
  if (!coveredIds || !svc) return false;
  return (
    coveredIds.has(String(svc._id || "")) ||
    (svc.templateId ? coveredIds.has(String(svc.templateId)) : false)
  );
}

/**
 * ── "YOUR PACKAGES ARE DOWN THERE" (plan item 6, 2026-10-18) ─────────────────────────────────────────
 *
 * The package section sits below every step, and only the tile at the very top of the page had a working link to
 * it — so a customer reading Your Information, Date & Time or Group Booking had to scroll and hunt. Each of those
 * steps now carries the same REAL button (not a label: a plain `div` reading "see below" was the original defect)
 * and it scrolls to the section that the id belongs to.
 *
 * `show` is the same expression that gates the section, so the link cannot point at nothing.
 */
function PackageJumpLink({ show, isMobile }) {
  if (!show) return null;
  return (
    <div style={{ margin: "0 0 12px 0" }}>
      <button
        type="button"
        onClick={() => {
          document
            .getElementById("packages-for-this-service")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        style={{
          background: "#ecfdf5",
          border: "1px solid #10b981",
          borderRadius: "8px",
          padding: "8px 12px",
          color: "#065f46",
          fontSize: isMobile ? "12px" : "13px",
          fontWeight: "600",
          cursor: "pointer",
        }}
      >
        💎 Save with a package for this booking — see below ⬇️
      </button>
    </div>
  );
}

/**
 * ── A DRAFTED VALUE, OR THIS PAGE'S OWN DEFAULT FOR IT (2026-10-27) ──────────────────────────────────
 *
 * The draft is JSON read back from sessionStorage, so the only thing known about a field is what the build that
 * wrote it put there. Reading it through these means a field that arrives in the wrong shape falls back to the
 * default instead of reaching arithmetic as `undefined` and rendering NaN in a price, a duration or a tip.
 */
function draftedNumber(value, fallback, { min = -Infinity } = {}) {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n >= min ? n : fallback;
}

function draftedText(value, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function draftedBoolean(value, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

const CustomerBooking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const shopId = searchParams.get("shopId");
  const staffId = searchParams.get("staffId");
  const serviceId = searchParams.get("serviceId");
  const lookId = searchParams.get("lookId");
  /**
   * THE PACKAGE THE LINK CAME FROM (plan item 2.12, 2026-10-23).
   *
   * "Book with this package" opens `/booking?shopId&staffId&serviceId&packageId=<PURCHASE id>`. The purchase id
   * — not the catalogue package id — because a package is only spendable once bought, and the booking has to
   * be paid from THAT purchase. Read here and applied once, below, after the owned packages arrive.
   */
  const packageIdParam = searchParams.get("packageId");
  // Phase 0.6B: Detect freelancer bookings (no shopId provided)
  const isFreelancerBooking =
    searchParams.get("isFreelancer") === "true" || (!shopId && staffId);

  /**
   * ── THE BOOKING DRAFT, READ BEFORE ANYTHING CONSUMES IT (2026-10-26, moved here 2026-10-27) ──────────
   *
   * A customer may click "Buy & save" on a package at any step. That leaves the page, so everything they have
   * typed and chosen is written to a session-scoped draft first, and restored when they come back. It is scoped
   * to this booking attempt (shop + professional + service) so two bookings in two tabs never overwrite each
   * other, and cleared the moment the booking is confirmed.
   *
   * READ HERE, BEFORE THE STATE HOOKS, AND THAT IS NOT STYLE. What the customer entered has to be in the FIRST
   * render, not applied afterwards from an effect: a restore that lands one render late prices the booking at the
   * DEFAULTS first, and the availability check can then judge their restored time slot against the wrong length
   * and drop it — the customer arrives back and is told their time is gone. Lazy `useState` initialisers below
   * read this same value, so there is exactly one read of the draft per visit.
   */
  const draftScope = { shopId, staffId, serviceId };
  const [restoredDraft] = useState(() => loadBookingDraft(draftScope));

  const [shop, setShop] = useState(null);
  const [staff, setStaff] = useState(null);
  const [freelancerBusiness, setFreelancerBusiness] = useState(null); // Phase 0.6B: For freelancer's virtual location
  const [service, setService] = useState(null);
  const [serviceBundle, setServiceBundle] = useState([]);
  // F1/F2: customer selection for the PRIMARY engine-priced service
  const [serviceQuantity, setServiceQuantity] = useState(() =>
    draftedNumber(restoredDraft?.serviceQuantity, 1, { min: 1 }),
  );
  const [serviceHours, setServiceHours] = useState(() =>
    draftedNumber(restoredDraft?.serviceHours, 1, { min: 1 }),
  );
  const [serviceTierId, setServiceTierId] = useState(() =>
    typeof restoredDraft?.serviceTierId === "string"
      ? restoredDraft.serviceTierId
      : null,
  );
  // P1/P2 (F3): per-ADD-ON selection, keyed by add-on service id. Each add-on is
  // its own line, so it owns its units/hours/option - it must never inherit the
  // primary's, and changing one line must not disturb another.
  const [addOnSelections, setAddOnSelections] = useState(() => {
    const saved = restoredDraft?.addOnSelections;
    return saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
  });
  // Optional note the customer can leave for the professional
  const [customerNote, setCustomerNote] = useState(() =>
    draftedText(restoredDraft?.customerNote),
  );
  const [productPitchIndex] = useState(() => Math.floor(Math.random() * 5));
  const [shopServices, setShopServices] = useState([]); // All services at this shop for group booking
  const [availableSlots, setAvailableSlots] = useState(null); // null = not fetched, [] = no slots, [...] = has slots
  const [availableDayClosingTime, setAvailableDayClosingTime] = useState("");
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { itemCount } = useCart();

  // Form state
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [customerAddress, setCustomerAddress] = useState({
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "US",
  });

  // Group booking state
  const [groupSize, setGroupSize] = useState(1);
  const [groupMembers, setGroupMembers] = useState([]);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const MAX_ADDITIONAL_GROUP_MEMBERS = 2;
  const GROUP_RELATIONSHIP_OPTIONS = [
    "Son",
    "Daughter",
    "Spouse",
    "Partner",
    "Parent",
    "Sibling",
    "Friend",
  ];
  const [showServiceSelector, setShowServiceSelector] = useState(false);

  const [nextAvailableSlots, setNextAvailableSlots] = useState([]);
  const [nextAvailableError, setNextAvailableError] = useState("");

  // Waitlist join state (no-slot flow)
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistMessage, setWaitlistMessage] = useState({ type: "", text: "" });
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [pendingWaitlistReplacement, setPendingWaitlistReplacement] = useState(null);
  const [activeWaitlistConflict, setActiveWaitlistConflict] = useState(null);
  const waitlistDurationDays = 7;

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login"); // 'login' or 'register'
  const [hasChosenGuestCheckout, setHasChosenGuestCheckout] = useState(false);

  // Product selection state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const contactSectionRef = useRef(null);
  const phoneInputRef = useRef(null);
  const billingAddressSectionRef = useRef(null);
  const billingStreetInputRef = useRef(null);
  const summarySectionRef = useRef(null);
  const customerInfoSectionRef = useRef(null);
  const dateTimeSectionRef = useRef(null);
  // F2: the service card holds the option picker, so a pending-choice blocker
  // can send the customer back to it.
  const serviceCardRef = useRef(null);
  const productsSectionRef = useRef(null);
  const policySectionRef = useRef(null);
  const [expandedSection, setExpandedSection] = useState("customerInfo");
  // F2: set when a service change invalidated the chosen time slot.
  const [slotInvalidatedNotice, setSlotInvalidatedNotice] = useState("");
  const [billingAddressReturnTarget, setBillingAddressReturnTarget] = useState(
    BOOKING_BILLING_ADDRESS_RETURN_TARGET.PRODUCTS,
  );
  const [summaryReturnSection, setSummaryReturnSection] = useState("");
  // Shown once when a draft was restored, so a form that fills itself in is explained rather than mysterious.
  const [draftRestoredNotice, setDraftRestoredNotice] = useState(false);

  /**
   * Restores the draft exactly once, for the object-shaped values — the ones that are merged into or replace state
   * rather than seeding it. The scalars were already seeded by their `useState` initialisers above, so what the
   * customer entered is live in the first render and this cannot be a second, competing source for it.
   */
  const draftRestoredRef = useRef(false);
  // Agreement 2.8a: a package bought during THIS booking stops the "you can buy" offers for the rest of it. Read
  // once on mount, because the marker is written by the confirmation page before the customer is sent back here.
  const [packageBoughtDuringFlow] = useState(() =>
    wasPackagePurchasedDuringBooking({ shopId, staffId, serviceId }),
  );

  // Restore once, after the state hooks exist and before the customer can type over it.
  useEffect(() => {
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    if (!restoredDraft) return;
    if (restoredDraft.customerInfo) setCustomerInfo((prev) => ({ ...prev, ...restoredDraft.customerInfo }));
    if (restoredDraft.customerAddress) setCustomerAddress((prev) => ({ ...prev, ...restoredDraft.customerAddress }));
    if (restoredDraft.selectedDate) setSelectedDate(restoredDraft.selectedDate);
    if (restoredDraft.selectedSlot) setSelectedSlot(restoredDraft.selectedSlot);
    if (Array.isArray(restoredDraft.groupMembers)) setGroupMembers(restoredDraft.groupMembers);
    if (typeof restoredDraft.groupSize === "number") setGroupSize(restoredDraft.groupSize);
    if (Array.isArray(restoredDraft.selectedProducts)) setSelectedProducts(restoredDraft.selectedProducts);
    // The step they left, now that the payment step can never be blank: it shows the blocker list when something
    // is still missing, so coming back to it is informative rather than empty.
    if (restoredDraft.expandedSection) setExpandedSection(restoredDraft.expandedSection);
    // Tell them, so a form that fills itself in is not mistaken for a bug.
    setDraftRestoredNotice(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * AND THE ADD-ONS THEY HAD ADDED COME BACK, ONCE THE SHOP'S SERVICE LIST DOES (2026-10-27).
   *
   * The draft can only carry add-on ids, because an add-on line is a service document from the shop's list and a
   * stale copy of it would be worse than none. So the ids wait here and are resolved the moment `shopServices`
   * arrives — adding an extra service changes the price and the length of the booking, which is exactly the kind
   * of thing a customer would not notice had been dropped.
   *
   * Their per-line choices are applied in the same pass rather than by the restore effect: the pruning effect below
   * empties `addOnSelections` whenever the bundle is empty, which it is until this runs.
   */
  const pendingBundleRestoreRef = useRef(
    Array.isArray(restoredDraft?.bundleServiceIds) && restoredDraft.bundleServiceIds.length
      ? {
          ids: restoredDraft.bundleServiceIds.map(String),
          selections: restoredDraft.addOnSelections || {},
        }
      : null,
  );
  useEffect(() => {
    const pending = pendingBundleRestoreRef.current;
    if (!pending || !shopServices.length || !service?._id) return;
    pendingBundleRestoreRef.current = null;
    const resolved = pending.ids
      .filter((id) => id !== String(service._id))
      .map((id) => shopServices.find((svc) => String(svc?._id) === id))
      .filter(Boolean);
    if (!resolved.length) return;
    setServiceBundle(resolved);
    const liveIds = new Set(resolved.map((svc) => String(svc._id)));
    setAddOnSelections((prev) => {
      const next = { ...prev };
      for (const [id, selection] of Object.entries(pending.selections)) {
        if (liveIds.has(String(id))) next[String(id)] = selection;
      }
      return next;
    });
  }, [shopServices, service]); // eslint-disable-line react-hooks/exhaustive-deps

  // Package state
  const [customerPackages, setCustomerPackages] = useState([]);
  /**
   * OWNED PACKAGES THAT CANNOT PAY FOR THIS BOOKING — active but exhausted for these services, expired,
   * finished, or from a shop/professional whose rules refuse them. Classified on the SERVER's answer in
   * `fetchCustomerPackages` and kept here so the page can show them greyed instead of letting them vanish
   * (user, 2026-10-11: "at least let it show among the packages owned").
   */
  const [ownedUnusablePackages, setOwnedUnusablePackages] = useState([]);
  /**
   * 22G — PACKAGES HOLDING SESSIONS A LATE CANCELLATION LOST, AND WHAT IT COSTS TO GET THEM BACK.
   *
   * A SEPARATE LIST, not part of the owned-package lists, and for a reason the user gave twice: *"If they have
   * more sessions remaining and would like to book, they should not be barred from using the remaining
   * package"* — so this is an OFFER beside the package, never a barrier in front of it.
   *
   * It is also the ONLY list built WITHOUT the "used up means gone" rule. A package whose every session was
   * spent can hold a lost session that is still redeemable, and hiding it would hide the one place the customer
   * could get it back — the same "my package is gone" reading that rule was written to avoid goods.
   */
  const [redeemablePackageOffers, setRedeemablePackageOffers] = useState([]);
  /**
   * 22G — THE PAYMENT LINK, WHEN A POPUP BLOCKER REFUSES TO OPEN IT.
   *
   * Reserved tabs are never blocked, so this is the rare case — but the fallback must not be "navigate this tab",
   * which destroyed the booking the customer was building (reported 2026-10-14). A link they can click keeps
   * both the page AND the payment.
   */
  const [paymentUrl, setPaymentUrl] = useState("");
  const [availableShopPackages, setAvailableShopPackages] = useState([]); // All packages (for guests)
  // A plain statement of what happened, shown where the guest entered the flow — not an error in a
  // re-opened modal that asks for the email and phone again after they were already accepted.
  const [guestPackageNotice, setGuestPackageNotice] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  // A booking that completed WITHOUT a Stripe step (package credit). The server returns no checkout
  // URL for those, and a guest cannot fetch /api/bookings/:id (it needs a session), so the outcome is
  // held here and rendered from the response the server already sent.
  // The inline "paid with your package" panel was removed on 2026-10-12: a package booking now navigates to
  // `/booking-confirmation`, the same page a card booking lands on (user: *"do the confirmation page matching
  // the card page as well"*). Its state went with it — a page that can no longer be shown needs no state.
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // ── Phase 18 / D57: a GUEST using a package ────────────────────────────────────────────────
  //
  // A guest purchase has no account, so the page proves it with a short-lived, scoped token:
  // email OTP + phone last-4. The token lives in SESSION storage ONLY (never a URL, never
  // localStorage) and is sent as the Bearer header on the booking. It expires in 10 minutes, so the
  // guest may need to re-verify mid-booking — the booking selections are state, so they survive.
  const [showGuestPackageAccess, setShowGuestPackageAccess] = useState(false);
  const [guestChallenge, setGuestChallenge] = useState({
    step: "identity", // "identity" → "code"
    email: "",
    phoneLast4: "",
    challengeId: "",
    code: "",
    error: "",
    busy: false,
  });

  // Tip selection state (0%, 12%, 15%, 18%, 20%)
  const [tipPercentage, setTipPercentage] = useState(() =>
    draftedNumber(restoredDraft?.tipPercentage, 15, { min: 0 }),
  );

  // A customer may want to give a flat amount they choose, or nothing at all - the
  // presets alone decide FOR them. `tipIsCustom` switches the maths; the backend already
  // accepts a flat `tipAmount` and only derives from a percentage when one is given.
  const [tipIsCustom, setTipIsCustom] = useState(() =>
    draftedBoolean(restoredDraft?.tipIsCustom),
  );
  const [customTipAmount, setCustomTipAmount] = useState(() =>
    draftedText(restoredDraft?.customTipAmount),
  );

  // How much of the service value a tip may reach. 1 = 100%, i.e. a tip cannot exceed the
  // service itself. Change this one number to loosen or tighten the cap.
  //
  // A cap is not stinginess, it is dispute prevention: a mistyped "500" on a $40 service
  // is a chargeback, and by then the money is already in the professional's payout. This
  // matches the common booking-platform practice of capping a custom tip at the service.
  const MAX_TIP_MULTIPLE = 1;
  // A free service has nothing to take 100% of, and a $0 cap would make the field dead -
  // you could not tip at all on a free consultation. Fall back instead.
  const TIP_CEILING_FALLBACK = 100;
  const maxCustomTip = (serviceTotalForTip) => {
    const base = Number(serviceTotalForTip) || 0;
    return base > 0 ? base * MAX_TIP_MULTIPLE : TIP_CEILING_FALLBACK;
  };
  const parseCustomTip = () => {
    const raw = String(customTipAmount).trim();
    if (raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  // Promo code state
  const [promoCode, setPromoCode] = useState(() =>
    draftedText(restoredDraft?.promoCode),
  );
  // `{ code, discountAmount }` — the code and the figure the server quoted for this cart. Restored together: the
  // figure is only shown, never sent as the charge (the booking route re-validates the code and recalculates the
  // discount itself), so a code that lapsed while they were buying is refused with its own message instead of
  // being charged at the stale amount.
  const [appliedPromo, setAppliedPromo] = useState(() => {
    const saved = restoredDraft?.appliedPromo;
    if (!saved || typeof saved !== "object" || Array.isArray(saved)) return null;
    const code = draftedText(saved.code).trim();
    if (!code) return null;
    return { code, discountAmount: draftedNumber(saved.discountAmount, 0, { min: 0 }) };
  });
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  // Payment state
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [showCartWarningModal, setShowCartWarningModal] = useState(false);
  const [hasAutoShownCartWarning, setHasAutoShownCartWarning] = useState(false);
  const [cartWarningAcknowledged, setCartWarningAcknowledged] = useState(false);

  // SMS consent state (TCPA compliance)
  //
  // RESTORED FROM THE DRAFT (2026-10-27). See the save effect: this tick was made by this customer, for this
  // booking, in this tab, and `customerInfoComplete` will not pass without it — leaving it out is what left a
  // returning customer on a summary that refused to be ready.
  const [smsConsent, setSmsConsent] = useState(() =>
    draftedBoolean(restoredDraft?.smsConsent),
  );

  // Cancellation policy state
  const [policyAccepted, setPolicyAccepted] = useState(() =>
    draftedBoolean(restoredDraft?.policyAccepted),
  );
  /**
   * THE CUSTOMER KNOWS THEIR PACKAGE WILL NOT PAY FOR EVERYTHING (2026-10-27).
   *
   * Asked for by the user: *"We need to get an acknowledgement from the users when a package will not be able to
   * pay for a service that's uncovered."* Deliberately NOT drafted and NOT restored — unlike the SMS and policy
   * ticks, this one is about a figure that can change while they are on the next step (a member added, a service
   * swapped), and an acknowledgement of a superseded figure is worse than none.
   *
   * Stored as the SIGNATURE of what they acknowledged (`package:amount`) rather than as `true`, so a changed
   * amount silently un-acknowledges itself without an effect — and it can be read after the early returns, where
   * the totals live.
   */
  const [splitAckSignature, setSplitAckSignature] = useState("");
  const splitAcknowledgementRef = useRef(null);

  /**
   * ── KEEP THE DRAFT CURRENT (2026-10-26, completed 2026-10-27) ────────────────────────────────────────
   *
   * Cheap (a few hundred bytes in sessionStorage) and always newer than any restore.
   *
   * EVERYTHING THEY ENTERED GOES IN, INCLUDING THE CONSENT BOXES. The first version left SMS consent out on
   * principle — a consent box must be ticked by a person, not re-ticked by a stored value. The principle was right
   * and the application was wrong: this box was ticked by that person, for this booking, in this tab, and it is
   * what `customerInfoComplete` requires — so coming back from buying a package put the customer on a summary that
   * refused to be ready with no visible reason. The draft is `sessionStorage`, it dies with the tab, and it already
   * carries their name, email and phone; asking for the tick again protects nothing that the rest of the draft does
   * not already hold. Same for the cancellation-policy acknowledgement.
   *
   * ONE BUILDER, USED BY BOTH WRITERS. `startPackagePurchase` also writes a draft before it navigates away, and a
   * second copy of this list is a second place to forget a field — which is precisely how the consent box and the
   * tip went missing: they were added to the page and never to the inventory.
   *
   * DECLARED BELOW EVERY VALUE IT READS, AND THAT IS NOT STYLE. The dependency array is evaluated DURING RENDER, so
   * naming a `const` declared further down this function throws "Cannot access … before initialization" on the
   * first render — before the customer sees anything. That has now happened three times in this file; this effect
   * lives here, under `policyAccepted`, because that is the last value it lists.
   */
  const draftValues = () => ({
    selectedDate,
    selectedSlot,
    customerInfo,
    customerAddress,
    groupSize,
    groupMembers,
    selectedProducts,
    expandedSection,
    // The primary service's own choices: units, hours and option change BOTH the price and the length, so a
    // return that lost them would quote — and book — something other than what they had been shown.
    serviceQuantity,
    serviceHours,
    serviceTierId,
    // Which add-ons they added, and each add-on's own choices. Saved as ids: an add-on is a service OBJECT taken
    // from the shop's list, so it can only be re-resolved once that list is back.
    addOnSelections,
    bundleServiceIds: (serviceBundle || []).map((svc) => String(svc?._id || "")).filter(Boolean),
    customerNote,
    tipPercentage,
    tipIsCustom,
    customTipAmount,
    promoCode,
    appliedPromo,
    smsConsent,
    policyAccepted,
  });

  useEffect(() => {
    if (!draftRestoredRef.current) return;
    saveBookingDraft(draftScope, draftValues());
  }, [
    selectedDate,
    selectedSlot,
    customerInfo,
    customerAddress,
    groupSize,
    groupMembers,
    selectedProducts,
    expandedSection,
    serviceQuantity,
    serviceHours,
    serviceTierId,
    addOnSelections,
    serviceBundle,
    customerNote,
    tipPercentage,
    tipIsCustom,
    customTipAmount,
    promoCode,
    appliedPromo,
    smsConsent,
    policyAccepted,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [waitlistCompatibility, setWaitlistCompatibility] = useState(null);
  const [waitlistCompatibilityLoading, setWaitlistCompatibilityLoading] =
    useState(false);
  const [waitlistMismatchAcknowledged, setWaitlistMismatchAcknowledged] =
    useState(false);
  const waitlistMismatchSectionRef = useRef(null);

  // Mobile responsiveness
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Recurring booking state (Phase E)
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState("weekly"); // daily, weekly, biweekly, monthly
  const [recurringInterval, setRecurringInterval] = useState(1);
  const [recurringEndType, setRecurringEndType] = useState("occurrences"); // occurrences or date
  const [recurringOccurrences, setRecurringOccurrences] = useState(10);
  const [recurringEndDate, setRecurringEndDate] = useState("");

  // Tax preview state
  const [taxPreview, setTaxPreview] = useState({
    taxAmount: 0,
    taxRate: 0,
    calculatedAtCheckout: true,
  });
  const [taxLoading, setTaxLoading] = useState(false);
  const taxPreviewRequestIdRef = useRef(0);

  const selectedProductsTaxSignature = JSON.stringify(
    selectedProducts.map((product) => ({
      productId: product.productId,
      quantity: product.quantity,
      price: product.price,
      stripeTaxCode: product.stripeTaxCode || "",
    })),
  );

  // Preview mode state (for test bookings)
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  useEffect(() => {
    // Check for preview token in sessionStorage
    const previewToken = sessionStorage.getItem("ogestyle_preview_token");
    if (previewToken) {
      setIsPreviewMode(true);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Phase 0.6B: Support freelancer bookings (shopId optional for freelancers)
    if ((shopId || isFreelancerBooking) && staffId && serviceId) {
      fetchBookingData();
      return;
    }

    // A link missing one of the booking's identity inputs used to mean NOTHING ran: `fetchBookingData`
    // (the only thing that clears `loading`) was never called, so the page sat on "Loading booking
    // information..." forever with no way forward. A dead end that looks like a broken app — and it was
    // mistaken for "the packages aren't showing" while this was being tested.
    //
    // Say what is missing instead, and clear `loading` so the message can actually be seen.
    const missing = [
      !(shopId || isFreelancerBooking) ? "shop" : null,
      !staffId ? "professional" : null,
      !serviceId ? "service" : null,
    ].filter(Boolean);

    setLoading(false);
    setError(
      `This booking link is missing a ${missing.join(" and a ")}. Start again from the shop's page and choose what you want to book.`,
    );
      // Intentionally keyed to booking identity inputs; fetch function is local flow logic.
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, staffId, serviceId, isFreelancerBooking]);

  useEffect(() => {
    // Phase C11.5: Check authentication and auto-fill customer info
    const checkAuth = async () => {
      const token = localStorage.getItem("ogestyle_token");

      if (token) {
        // User is authenticated - fetch their profile
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/users/profile`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (response.ok) {
            const user = await response.json();
            setIsAuthenticated(true);
            setCustomerInfo({
              firstName: user.firstName || user.name?.split(" ")[0] || "",
              lastName: user.lastName || user.name?.split(" ")[1] || "",
              email: user.email || "",
              phone: user.phone || "",
            });

            if (user.billingAddress) {
              setCustomerAddress({
                street: user.billingAddress.street || "",
                city: user.billingAddress.city || "",
                state: user.billingAddress.state || "",
                zipCode:
                  user.billingAddress.zipCode || user.billingAddress.zip || "",
                country: user.billingAddress.country || "US",
              });
            }
          } else {
            // Token invalid, show auth modal
            localStorage.removeItem("ogestyle_token");
            setShowAuthModal(true);
          }
        } catch (error) {
          setShowAuthModal(true);
        }
      } else {
        // No token, show auth modal
        setShowAuthModal(true);
      }

    };

    checkAuth();
  }, []);

  // Fetch cancellation policy on mount
  useEffect(() => {
    const baseURL =
      process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
    fetch(`${baseURL}/api/platform-settings/cancellation-policy`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setCancellationPolicy(data);
      })
      .catch(() => {
        // Non-critical — checkbox still works without loaded data
      });
  }, []);

  useEffect(() => {
    if (itemCount > 0 && !cartWarningAcknowledged && !hasAutoShownCartWarning) {
      setShowCartWarningModal(true);
      setHasAutoShownCartWarning(true);
    }
  }, [itemCount, cartWarningAcknowledged, hasAutoShownCartWarning]);

  // Fetch real-time tax estimate when products + billing address are ready
  useEffect(() => {
    const fetchTaxPreview = async () => {
      const currentRequestId = ++taxPreviewRequestIdRef.current;

      if (selectedProducts.length === 0) {
        setTaxPreview({ taxAmount: 0, taxRate: 0, calculatedAtCheckout: true });
        setTaxLoading(false);
        return;
      }

      const hasAddress =
        customerAddress.street &&
        customerAddress.city &&
        customerAddress.state &&
        customerAddress.zipCode;

      if (!hasAddress) {
        setTaxPreview({ taxAmount: 0, taxRate: 0, calculatedAtCheckout: true });
        setTaxLoading(false);
        return;
      }

      setTaxLoading(true);

      try {
        const items = selectedProducts.map((p) => ({
          productId: p.productId,
          quantity: p.quantity,
          price: p.price,
          stripeTaxCode: p.stripeTaxCode,
        }));
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/tax/estimate`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items,
              billingAddress: {
                street: customerAddress.street,
                city: customerAddress.city,
                state: customerAddress.state,
                zipCode: customerAddress.zipCode,
                country: customerAddress.country || "US",
              },
            }),
          },
        );

        if (currentRequestId !== taxPreviewRequestIdRef.current) {
          return;
        }

        if (response.ok) {
          const data = await response.json();
          if (currentRequestId !== taxPreviewRequestIdRef.current) {
            return;
          }

          setTaxPreview({
            taxAmount: data.taxAmount,
            taxRate: data.taxRate,
            calculatedAtCheckout: false,
          });
        } else {
          setTaxPreview({
            taxAmount: 0,
            taxRate: 0,
            calculatedAtCheckout: true,
          });
        }
      } catch {
        if (currentRequestId !== taxPreviewRequestIdRef.current) {
          return;
        }

        setTaxPreview({ taxAmount: 0, taxRate: 0, calculatedAtCheckout: true });
      } finally {
        if (currentRequestId === taxPreviewRequestIdRef.current) {
          setTaxLoading(false);
        }
      }
    };

    fetchTaxPreview();
  }, [
    selectedProducts,
    selectedProductsTaxSignature,
    customerAddress.street,
    customerAddress.city,
    customerAddress.state,
    customerAddress.zipCode,
    customerAddress.country,
  ]);

  // ── P1/P2 (F3): every selected service is its own LINE ─────────────────────
  // The primary's choice lives in the three primitives below; each add-on carries
  // its own in `addOnSelections`. Resolving them TOGETHER is the only source of
  // price/length from here on, so a line is never valued at its catalog default
  // just because another line exists. Declared before the availability effect so
  // the effect can depend on the resolved length without a temporal-dead-zone
  // reference.
  const addOnLineItems = (serviceBundle || []).map((svc) => ({
    service: svc,
    selection: addOnSelections[String(svc?._id)] || {},
  }));
  const resolvedBundleLines = resolveServiceLines([
    ...(service
      ? [
          {
            service,
            selection: {
              quantity: serviceQuantity,
              hours: serviceHours,
              tierId: serviceTierId,
            },
          },
        ]
      : []),
    ...addOnLineItems,
  ]);
  const primaryPricingLine = service ? resolvedBundleLines.lines[0] || null : null;

  // A line that still needs a choice has NO price and NO length yet. For a range
  // service the stored `durationMinutes` is the LONGEST option, so deriving a
  // length from that default would offer slots the customer cannot get.
  const pendingSelectionLines = resolvedBundleLines.lines.filter(
    (line) => line && line.requiresSelection,
  );
  const primarySelectionPending = pendingSelectionLines.length > 0;
  const primaryNeedsChoice = Boolean(
    primaryPricingLine && primaryPricingLine.requiresSelection,
  );
  const pendingAddOnNames = (serviceBundle || [])
    .filter((svc) =>
      pendingSelectionLines.some(
        (line) => String(line.serviceId) === String(svc?._id),
      ),
    )
    .map((svc) => svc.name);
  const selectionPendingLabel = primaryNeedsChoice
    ? primaryPricingLine?.label || service?.name || "your service"
    : pendingAddOnNames[0] || "your service";

  // The resolved length to reserve, or the catalog length when nothing resolved
  // (e.g. a line is still waiting for its option).
  const resolvedTotalMinutes =
    resolvedBundleLines.totalMinutes > 0
      ? resolvedBundleLines.totalMinutes
      : Number(service?.durationMinutes || service?.duration || 0) || 0;

  // P5: every group member gets their OWN resolved line. A member's service can be
  // per-unit / hourly / tiered exactly like the primary, so their length and price
  // must come from the engine - never from the catalog `serviceDuration`/`price`
  // stored on the member row. Indexed by member order so the slot chain can read
  // `resolvedMemberLines[index]` for the member AND for the one before them.
  const resolvedMemberLines = (groupMembers || []).map((member) => {
    const memberService = (shopServices || []).find(
      (svc) => String(svc?._id) === String(member?.serviceId),
    );
    if (!memberService) return null;
    return resolveServicePricing(memberService, member.selection || {});
  });

  /**
   * DISPLAY-ONLY group totals for the service card header.
   *
   * The customer sees the members listed directly beneath that header, so it must
   * account for them or the card contradicts itself ($130/105 min above $40/60 min of
   * members). Deliberately NOT folded into `effectiveBundleDurationMinutes`: that value
   * drives the availability request and the booking WINDOW, and the members are chained
   * AFTER it - widening it would book the member's time twice.
   */
  const resolvedMemberTotals = resolvedMemberLines.reduce(
    (totals, line) => {
      if (!line || line.requiresSelection) return totals;
      return {
        price: totals.price + (Number(line.totalPrice) || 0),
        minutes: totals.minutes + (Number(line.totalMinutes) || 0),
        count: totals.count + 1,
      };
    },
    { price: 0, minutes: 0, count: 0 },
  );

  useEffect(() => {
    if (selectedDate && staffId && serviceId && !primarySelectionPending) {
      fetchAvailableSlots();
      setWaitlistJoined(false);
      setWaitlistMessage({ type: "", text: "" });
      setPendingWaitlistReplacement(null);
      setActiveWaitlistConflict(null);
      setNextAvailableSlots([]);
      setNextAvailableError("");
    } else if (primarySelectionPending) {
      // A line still needs a choice: clear the grid rather than offer times
      // computed from a catalog default.
      setAvailableSlots([]);
      setAvailableDayClosingTime("");
      setSelectedSlot(null);
    }
      // Intentionally runs on date/identity changes only.
      // F1/F2: the pricing selection also changes the appointment length, so it
      // must refetch availability (quantity, hours and chosen option alike).
      // F2/P2: and it must not fetch at all until every required choice is made.
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDate,
    staffId,
    serviceId,
    serviceBundle,
    service,
    serviceQuantity,
    serviceHours,
    serviceTierId,
    addOnSelections,
    primarySelectionPending,
  ]);

  // F2/P1: everything that decides how long the appointment occupies the calendar.
  // The resolved total already includes the primary's own selection AND each
  // add-on's, so any change to any line invalidates the chosen slot.
  const schedulingSignature = String(resolvedTotalMinutes);

  // F2: any change that alters the appointment length invalidates the chosen
  // slot, and any group member times (the group is anchored to the primary
  // start time), so the customer must pick a time again. Name/email/phone, the
  // address, the note and the product selection are untouched - only the
  // schedule is affected.
  useEffect(() => {
    if (!selectedSlot && groupMembers.length === 0) {
      return; // nothing to invalidate (also covers the initial mount)
    }
    setSelectedSlot(null);
    // Keep the members - their names, services, units and options are all still
    // valid. Only their TIMES are invalid, because the primary customer's length
    // changed and every chained time moved with it. Wiping the members outright
    // (as this used to do) threw away work the customer had already done.
    setGroupMembers((prev) =>
      prev.map((member) => ({
        ...member,
        preferredTime: "",
        preferredTime12: "",
      })),
    );
    setSlotInvalidatedNotice(
      "Your time slot was cleared because the service changed. Please pick a new time.",
    );
    // Send them back to the slot picker only if they had already moved past it.
    setExpandedSection((prev) =>
      ["group", "products", "summary"].includes(prev) ? "dateTime" : prev,
    );
    // Intentionally keyed to the schedule alone: depending on the slot or group
    // state would clear the slot the moment it is chosen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulingSignature]);

  // F2 safety net on top of the rule above: if the chosen time is no longer
  // offered for any other reason (service switched, slot taken in the meantime),
  // drop it rather than letting a stale slot reach checkout.
  useEffect(() => {
    if (!selectedSlot || availableSlots === null) {
      return;
    }
    const stillAvailable = (availableSlots || []).some(
      (slot) =>
        (slot?.time24 || slot?.startTime) ===
          (selectedSlot?.time24 || selectedSlot?.startTime) &&
        slot?.available !== false,
    );
    if (!stillAvailable) {
      setSelectedSlot(null);
      // THE EXISTING AVAILABILITY CHECK, NOW ALSO TELLING THE CUSTOMER (2026-10-26).
      //
      // This dropped the slot silently, which was enough when the customer was watching the picker. A restored
      // draft can put them straight back on the summary, where a vanished slot is invisible - so the same check
      // now says why and sends them to the picker, exactly as the service-change path above already does. No
      // second availability test: this is the one that already runs on every slot refresh.
      setSlotInvalidatedNotice(
        "Your chosen time is no longer available. Please pick a new time.",
      );
      setExpandedSection((prev) =>
        ["group", "products", "summary"].includes(prev) ? "dateTime" : prev,
      );
    }
  }, [availableSlots, selectedSlot]);

  // P1/F3: reset the primary's selection ONLY when the PRIMARY service changes.
  // It must not reset when an add-on is added or removed - doing that is what made
  // the stepper look "locked" once a bundle existed (it silently snapped back
  // to 1 unit).
  //
  // AND NOT ON MOUNT EITHER (2026-10-27). On the first render nothing has changed, so resetting threw away the
  // units, hours and option a returning customer had already chosen — they were restored into state above, and
  // were then wiped before they could be used, quoting a different price and length from the one they had been
  // shown. The ref makes this a handler for an actual change rather than something that also fires on birth.
  const serviceSelectionResetReadyRef = useRef(false);
  useEffect(() => {
    if (!serviceSelectionResetReadyRef.current) {
      serviceSelectionResetReadyRef.current = true;
      return;
    }
    setServiceQuantity(1);
    setServiceHours(1);
    setServiceTierId(null);
  }, [serviceId]);

  // P1/F3: an add-on that left the bundle must not keep a stale selection.
  useEffect(() => {
    setAddOnSelections((prev) => {
      const liveIds = new Set(
        (serviceBundle || []).map((svc) => String(svc?._id)),
      );
      const next = {};
      let changed = false;
      Object.keys(prev).forEach((id) => {
        if (liveIds.has(id)) next[id] = prev[id];
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [serviceBundle]);

  useEffect(() => {
    if (
      selectedDate &&
      !slotsLoading &&
      availableSlots !== null &&
      availableSlots.length === 0 &&
      !selectedSlot &&
      staffId &&
      serviceId
    ) {
      fetchNextAvailableSlots(selectedDate, 5);
    }
    // Intentionally follows the selected-day availability fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, slotsLoading, availableSlots, selectedSlot, staffId, serviceId, serviceBundle, service]);

  useEffect(() => {
    if (serviceId && shop?._id && !isPreviewMode) {
      // Skip product fetching in preview mode - booking only
      fetchRecommendedProducts();
      fetchCustomerPackages(); // Fetch customer's purchased packages
      // NOTE: the shop's packages for sale are fetched by a SEPARATE effect keyed on the shop, not
      // here — see below. Gating them on the URL's `serviceId` is what made them invisible when the
      // service was chosen on this page.
    }
      // Intentionally runs when booking context becomes resolvable.
      //
      // `serviceBundle` is NOT here: the packages for sale are fetched by the effect below and
      // filtered at render, so they no longer need a dependency that changes as the booking changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, shop?._id, isPreviewMode]);

  /**
   * The shop's packages for sale — fetched as soon as the SHOP is known, deliberately not gated on
   * the service.
   *
   * This used to run only when the URL carried a `serviceId`, alongside the product and
   * owned-package fetches. But a booking can be made in two orders — service first, or professional
   * first — and in both, the service may be chosen ON THIS PAGE rather than passed in the URL. On
   * those paths the URL `serviceId` is empty, this never ran, and the customer saw no packages at
   * all. Fetching the shop's list independently means relevance is decided per render (see
   * `relevantAvailablePackages`) and follows whatever is selected, with no refetch.
   */
  useEffect(() => {
    if (shop?.businessId && !isPreviewMode) {
      fetchAvailableShopPackages();
    }
    // Intentionally keyed on the shop: the list is the same for every service, and the filtering
    // happens at render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop?.businessId, isPreviewMode]);

  const fetchBookingData = async () => {
    try {
      setLoading(true);

      // Phase 0.6B: Handle freelancer bookings differently
      if (isFreelancerBooking) {
        const [staffRes, serviceRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/staff/${staffId}`),
          fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/services/${serviceId}`,
          ),
        ]);

        if (!staffRes.ok || !serviceRes.ok) {
          throw new Error("Failed to load booking information");
        }

        const [staffData, serviceData] = await Promise.all([
          staffRes.json(),
          serviceRes.json(),
        ]);
        setStaff(staffData);
        setService(serviceData);
        setServiceBundle([]);

        // Get freelancer's virtual location for currency/timezone
        if (staffData.shopIds && staffData.shopIds.length > 0) {
          const virtualShopId = staffData.shopIds[0];
          try {
            const locationRes = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/shops/${virtualShopId}`,
            );
            if (locationRes.ok) {
              const locationData = await locationRes.json();
              setFreelancerBusiness(locationData);
              // Set shop for compatibility with existing code
              setShop(locationData);
            }
          } catch (err) {
          }
        }

        setLoading(false);
        return;
      }

      // Existing shop booking flow
      const [shopRes, staffRes, serviceRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/shops/${shopId}`),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/staff/${staffId}`),
        fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/services/${serviceId}`,
        ),
      ]);

      if (!shopRes.ok || !staffRes.ok || !serviceRes.ok) {
        throw new Error("Failed to load booking information");
      }

      const [shopData, staffData, serviceData] = await Promise.all([
        shopRes.json(),
        staffRes.json(),
        serviceRes.json(),
      ]);
      setShop(shopData);
      setStaff(staffData);
      setService(serviceData);
      setServiceBundle([]);

      // Fetch all services for this specific staff member for group booking
      fetchShopServices(shopId, staffId);

      // Fetch all services for this shop location for group booking
      // Use staff services if available, otherwise try to fetch from shop
      if (staffData.services && staffData.services.length > 0) {
        setShop((prev) => ({ ...prev, services: staffData.services }));
      } else if (shopData._id) {
        try {
          const servicesRes = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/services?shopId=${shopData._id}`,
          );
          if (servicesRes.ok) {
            const servicesData = await servicesRes.json();
            setShop((prev) => ({
              ...prev,
              services: servicesData.services || [],
            }));
          }
        } catch (err) {
          // Fallback: use services from staff if available
          if (staffData.services) {
            setShop((prev) => ({ ...prev, services: staffData.services }));
          }
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async () => {
    setSlotsLoading(true);
    try {
      const queryServiceDuration =
        Number(effectiveBundleDurationMinutes) ||
        normalizeServiceDuration(service) ||
        0;
      if (!queryServiceDuration) {
        throw new Error(
          "Selected service is missing a valid duration. Please refresh and try again.",
        );
      }
      const formatTime12 = (time24) => {
        const [h, m] = String(time24 || "").split(":").map(Number);
        if (!Number.isFinite(h) || !Number.isFinite(m)) return String(time24 || "");
        const ampm = h >= 12 ? "PM" : "AM";
        const h12 = h % 12 || 12;
        return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
      };

      const locationId =
        shop?._id ||
        staff?.activeLocationId?._id ||
        staff?.activeLocationId ||
        shopId;

      if (!locationId) {
        throw new Error("Failed to resolve booking location for availability");
      }

      const monthKey = String(selectedDate || "").slice(0, 7);
      const monthResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/available-slots-month?staffId=${staffId}&locationId=${locationId}&month=${monthKey}&serviceDuration=${queryServiceDuration}&fresh=1`,
      );

      if (!monthResponse.ok) {
        throw new Error("Failed to load available slots");
      }

      const monthData = await monthResponse.json();
      const dayAvailability = monthData?.days?.[selectedDate] || {};
      const daySlots = dayAvailability?.slots || [];
      const slots = daySlots.map((slot) => {
        const time24 = slot.time24 || slot.start || "";
        const endTime = slot.endTime || slot.end || "";
        return {
          time24,
          endTime,
          time12: slot.time12 || formatTime12(time24),
          available: slot.available !== false,
        };
      });

      setError("");
      setAvailableDayClosingTime(dayAvailability?.closingTime || "");
      setAvailableSlots(slots);
    } catch (err) {
      setError(err.message || "Failed to load available slots");
      setAvailableDayClosingTime("");
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const fetchRecommendedProducts = async () => {
    try {
      // Use shop._id if shop is loaded, otherwise use shopId from URL
      const resolvedShopId = shop?._id || shopId;
      if (!resolvedShopId || !serviceId) {
        return;
      }

      const url = `${process.env.REACT_APP_API_BASE_URL}/api/bookings/recommended-products/${serviceId}?shopId=${resolvedShopId}`;
      const response = await fetch(url);
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      setRecommendedProducts(data.products || []);
    } catch (err) {
    }
  };

  const fetchCustomerPackages = async () => {
    const token = localStorage.getItem("ogestyle_token");
    const guestToken = readGuestPackageToken();

    // A SIGNED-IN customer is identified by their session; a GUEST by the scoped token D57 issued
    // after the challenge. Neither → no owned packages.
    const authToken = token || guestToken;
    if (!authToken) {
      setCustomerPackages([]);
      return { eligible: [], activeCount: 0, usedUp: [] };
    }

    try {
      const url = token
        ? `${process.env.REACT_APP_API_BASE_URL}/api/packages/customer/purchases`
        : `${process.env.REACT_APP_API_BASE_URL}/api/packages/guest/purchases`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!response.ok) {
        // A refused guest token is not worth keeping: it would be presented again on the next mount and
        // refused again. Dropping it makes the page behave as the fresh guest it now is.
        if (!token && (response.status === 401 || response.status === 403)) {
          sessionStorage.removeItem(GUEST_PACKAGE_TOKEN_KEY);
        }
        throw new Error("Failed to load packages");
      }

      const data = await response.json();
      // The endpoint also reports what is no longer usable, so the page can explain WHY nothing came
      // back: a finished package and a package that does not include this service are different
      // problems with different answers. Passed to the caller rather than set as state, so the verify
      // handler can use it in this same tick.

      // ── EVERY OWNED PACKAGE IS CLASSIFIED, NOT JUST THE SPENDABLE ONES ──────────────────────────
      //
      // This list used to keep only `status === "active" && sessionsRemaining > 0`, and everything else was
      // dropped SILENTLY. A customer who had paid for a package and could not see it read that as the package
      // being gone: *"the Mother & Daughter's Day package is not showing as parts of the package the
      // customer/guest owns even though it can't pay for the booking. At least let it show among the packages
      // owned"* (user, 2026-10-11). Hiding a package that a customer cannot use removes the one place they
      // could have learned WHY — and the same filter was hiding a finished package from the customer who had
      // finished it, which is the moment they most want to see it.
      //
      // What is kept is relevance to THIS booking: a package for services the customer is not booking here is
      // not news, and listing every purchase from every shop would bury the four cards that matter.
      const allPurchases = data.purchases || [];
      const usedUpRaw = data.usedUpPurchases || [];

      const relates = (pkg) =>
        (pkg.packageSnapshot?.services || pkg.services || []).some((s) =>
          // `bookedServiceIds` carries the booked services AND their template ids, so an owned package
          // (which stores template ids) matches even though the booking names a staff copy.
          bookedServiceIds.has(String(s.serviceId?._id || s.serviceId)),
        );

      const eligiblePackages = allPurchases.filter(
        (pkg) => pkg.status === "active" && pkg.sessionsRemaining > 0 && relates(pkg),
      );

      const spendableIds = new Set(
        eligiblePackages.map((pkg) => String(pkg._id || pkg.id)),
      );

      const whyNotUsable = (pkg) => {
        if (pkg.validUntil && new Date(pkg.validUntil) < new Date()) {
          return "This package has expired.";
        }
        if (pkg.status && pkg.status !== "active") {
          return `This package is ${pkg.status}, so it can no longer be used.`;
        }
        return "This package is not eligible for this booking.";
      };

      const normalise = (pkg) => ({
        key: String(pkg._id || pkg.id || pkg.name),
        // The CATALOGUE package this purchase was bought from. Kept so the for-sale list can stop offering a
        // package the customer already owns (2026-10-26) — a purchase's own id is no use for that comparison.
        packageId: String(pkg.packageId?._id || pkg.packageId || ""),
        name: pkg.packageSnapshot?.name || pkg.name || "Package",
        sessionsRemaining: Math.max(
          0,
          Number(pkg.sessionsRemaining) ||
            (Number(pkg.totalSessions) || 0) - (Number(pkg.sessionsUsed) || 0),
        ),
        validUntil: pkg.validUntil,
        quota: pkg.quota,
        redeemTotal: Number(pkg.redeemTotal) || 0,
        redeemUnits: Number(pkg.redeemUnits) || 0,
        reason: whyNotUsable(pkg),
      });

      // A Map, so a package reported by BOTH lists cannot become two cards. A guest's used-up list is a
      // separate query and can overlap the main one around an expiry boundary.
      const notUsableById = new Map();
      for (const pkg of allPurchases) {
        if (spendableIds.has(String(pkg._id || pkg.id))) continue;
        if (!relates(pkg)) continue;
        // ── USED UP MEANS GONE (user, 2026-10-11) ──────────────────────────────────────────────
        //
        // *"Any package that's used up should be hidden completely when booking."* A finished package is not
        // a decision the customer can act on and not a package they still hold — leaving it on the page is
        // the clutter the first version of this rule was written to remove. What they DO still hold is an
        // ACTIVE package that simply cannot pay for the booking in front of them, and that one stays
        // visible: *"as long as a ticket is still active, even though it may not cover the booking in focus
        // it should at least show it but might say not eligible"*. Nothing left to spend → not shown.
        if (!(Number(pkg.sessionsRemaining) > 0)) continue;
        notUsableById.set(String(pkg._id || pkg.id), normalise(pkg));
      }
      for (const pkg of usedUpRaw) {
        // Same rule from the other list: the guest endpoint reports finished purchases separately, and they
        // are hidden for the same reason. `usedUp` is still used by the caller to EXPLAIN an empty list.
        if (!(Number(pkg.sessionsRemaining) > 0)) continue;
        const key = String(pkg._id || pkg.id || pkg.name);
        if (!notUsableById.has(key) && relates(pkg)) {
          notUsableById.set(key, normalise(pkg));
        }
      }

      setOwnedUnusablePackages([...notUsableById.values()]);
      // ── THE REDEEMABLE OFFERS, FROM EVERY OWNED PURCHASE ──────────────────────────────────────
      //
      // BOTH LISTS are read, and that is not belt-and-braces: the two endpoints answer differently about a
      // package that is finished. `/customer/purchases` returns every purchase, while the GUEST route returns
      // only `status: "active"` purchases and reports the rest separately as used up — so a guest whose last
      // session was lost to a cancellation would find their only redeemable session reported in the second list
      // and nowhere else. A lost session is not "used up"; it is money they are holding.
      //
      // Not filtered by `relates()` either: the offer is about the package, not about the service being booked
      // on this screen, and hiding it because today's booking happens not to match would hide the one place the
      // customer can get the session back.
      const offerById = new Map();

      /**
       * ── WOULD RESTORING THIS PACKAGE'S LOST SESSIONS COVER THIS WHOLE APPOINTMENT? (user, 2026-10-16) ──
       *
       * The user's rule, in their words: if the held sessions are ELIGIBLE — *"contains all the services they're
       * trying to book"* — the customer should be offered paying for them and using the package. If the held
       * sessions are on a package that CANNOT be used for these services, then the offer must not imply it helps
       * with this appointment: buy a package as normal, or pay by card.
       *
       * `relates()` (ANY service) is not enough for that promise. A father-and-son appointment against a package
       * covering only the adult's service is "related" and still cannot pay for the boy, so this asks the
       * stronger question: every service being booked, matched into the package's own lines by the same
       * two-sided id rule `bookedServiceIds` uses (a package stores the TEMPLATE id; a booking names a staff
       * copy carrying that template id).
       */
      const packageLineIds = (pkg) => {
        const lines = pkg?.packageSnapshot?.services || pkg?.services || [];
        const ids = new Set();
        for (const line of lines) {
          for (const raw of [line?.serviceId?._id, line?.serviceId, line?.templateId]) {
            if (raw) ids.add(String(raw));
          }
        }
        return ids;
      };
      const bookedServices = [service, ...(serviceBundle || [])].filter(Boolean);
      const coversWholeBooking = (pkg) => {
        if (bookedServices.length === 0) return false;
        const lines = packageLineIds(pkg);
        return bookedServices.every((svc) =>
          [svc?._id, svc?.templateId]
            .filter(Boolean)
            .some((id) => lines.has(String(id))),
        );
      };

      for (const pkg of [...allPurchases, ...usedUpRaw]) {
        const id = String(pkg?._id || pkg?.id || "");
        if (!id) continue;
        if (!(Number(pkg.redeemUnits) > 0) || !(Number(pkg.redeemTotal) > 0)) continue;
        // A purchase reported by both lists is ONE offer, not two buttons for the same thing.
        if (offerById.has(id)) continue;
        offerById.set(id, {
          key: id,
          name: pkg.packageSnapshot?.name || pkg.name || "Package",
          units: Number(pkg.redeemUnits) || 0,
          amount: Number(pkg.redeemTotal) || 0,
          sessionsRemaining: Math.max(
            0,
            Number(pkg.sessionsRemaining) ||
              (Number(pkg.totalSessions) || 0) - (Number(pkg.sessionsUsed) || 0),
          ),
          validUntil: pkg.validUntil,
          // Read by the card, so the customer is told whether paying helps THIS appointment or only a future one.
          coversThisBooking: coversWholeBooking(pkg),
        });
      }
      setRedeemablePackageOffers([...offerById.values()]);
      setCustomerPackages(eligiblePackages);
      // Returned so the caller can ACT on the result: a guest who has just proved a purchase must end
      // up with a package selected, not merely with a longer list further up the page. Returned rather
      // than left in state because the caller runs in the same tick and state has not updated yet.
      //
      // `heldUnits` IS PART OF THE ANSWER (user, 2026-10-19). A package can have nothing left to SPEND and still
      // hold sessions from a late cancellation that the customer can pay to get back. Telling them it "has been
      // fully used" while an offer to restore it is on the same page is a contradiction — and the customer
      // reported it as one.
      return {
        eligible: eligiblePackages,
        activeCount: allPurchases.length,
        usedUp: usedUpRaw,
        heldUnits: [...offerById.values()].reduce(
          (sum, offer) => sum + (Number(offer.units) || 0),
          0,
        ),
      };
    } catch (err) {
      return { eligible: [], activeCount: 0, usedUp: [] };
    } finally {
    }
  };

  /**
   * 22G — COME BACK FROM THE PAYMENT TAB AND SEE IT DONE.
   *
   * The customer asked for exactly this: *"open the stripe payment page in a new tab and after payment close it
   * and silently update the session to be usable."* The sessions are returned by the WEBHOOK, so the moment they
   * close the payment tab and this one regains focus, the package has already changed — this just re-reads it,
   * with every selection on the page untouched.
   *
   * A ref rather than the function itself as the effect's dependency: `fetchCustomerPackages` is rebuilt on every
   * render (it closes over the current selection), and subscribing with it directly would re-register the
   * listener constantly to gain nothing. The ref always calls the LATEST one.
   */
  const refreshCustomerPackagesRef = useRef(null);
  refreshCustomerPackagesRef.current = fetchCustomerPackages;
  useEffect(() => {
    const onFocus = () => {
      const refresh = refreshCustomerPackagesRef.current;
      if (typeof refresh === "function") refresh();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  /**
   * 22G — PAY TO GET BACK THE SESSIONS A LATE CANCELLATION LOST.
   *
   * The server decides the amount: this sends the purchase id and NOTHING about money. A screen that calculated
   * the figure itself would be a second implementation of a money rule, and the two would drift.
   *
   * STRIPE OPENS IN A NEW TAB (the customer's own request: *"open the stripe payment page in a new tab and after
   * payment close it and silently update the session to be usable"*). That is why the page they were building
   * survives: they come back to a booking with every selection still made, and the focus refresh picks up the
   * returned sessions without a reload.
   *
   * THE TAB IS RESERVED BEFORE THE REQUEST. `window.open` after an `await` is not user-initiated and gets blocked;
   * the old fallback then navigated THIS tab to Stripe, which is the page they had to rebuild (reported
   * 2026-10-14). Now the window is opened inside the click and its location is set when the server answers, so no
   * await sits between the gesture and the window. If a blocker refuses even that, the URL is OFFERED rather than
   * followed — losing the page is the worse failure.
   */
  const redeemHeldPackageSessions = async (purchaseId) => {
    // RESERVED FIRST, synchronously, before anything is awaited.
    const reservedTab = reservePaymentTab();
    setPaymentUrl("");
    try {
      setError("");
      setPaymentProcessing(true);

      const token = localStorage.getItem("ogestyle_token");
      const guestToken = token ? null : readGuestPackageToken();
      const authToken = token || guestToken;

      if (!authToken) {
        setError("Please sign in or verify the package before redeeming these sessions.");
        setPaymentProcessing(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/packages/purchase/${purchaseId}/redeem-held`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // A refused proof is answered the same way the booking path answers it: drop the token and ask them
        // to verify again, keeping everything they had selected.
        if (
          data.error === "invalid_guest_token" ||
          data.error === "missing_guest_token"
        ) {
          sessionStorage.removeItem(GUEST_PACKAGE_TOKEN_KEY);
          setShowGuestPackageAccess(true);
          setGuestChallenge((prev) => ({
            ...prev,
            error: "Verify again to redeem these sessions.",
          }));
          setPaymentProcessing(false);
          return;
        }
        // `nothing_held` is not a failure the customer caused: the sessions came back already (the webhook from
        // a previous attempt, or another tab). Refresh so the card reflects the record instead of leaving a
        // stale offer on screen.
        if (data.error === "nothing_held") {
          await fetchCustomerPackages();
          setError("");
          setPaymentProcessing(false);
          return;
        }
        throw new Error(data.message || "Could not start the payment");
      }

      const url = String(data.stripeCheckoutUrl || "");
      // Stripe's own domain or nothing: a tampered response must not turn this into an open redirect.
      if (!/\.stripe\.com\//.test(url)) {
        throw new Error("The payment page could not be opened");
      }

      if (!sendReservedTabToPayment(reservedTab, url)) {
        // Refused by a popup blocker: OFFER the link instead of navigating away. Navigating is what destroyed
        // the booking they were building, and a payment they can still reach beats a page they never get back.
        setPaymentUrl(url);
        setError(
          "Your browser blocked the payment window. Use the payment link below — this page stays exactly as it is, and your sessions return as soon as you have paid.",
        );
      }
      setPaymentProcessing(false);
    } catch (err) {
      // A reserved window left blank would be a dead tab, so it is closed on any failure.
      try {
        if (reservedTab && !reservedTab.closed) reservedTab.close();
      } catch {
        // Nothing to do; the window will time out on its own.
      }
      setError(err.message || "Could not start the payment");
      setPaymentProcessing(false);
    }
  };

  const fetchAvailableShopPackages = async () => {
    if (!shop?.businessId) {
      return;
    }

    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/packages/business/${shop.businessId}/public`;
      const response = await fetch(url);
      if (!response.ok) {
        return;
      }

      const data = await response.json();

      // ── Phase 15C: relevance, not just availability ────────────────────────────────────────────
      //
      // This list was filtered ONLY by whether a professional is subscribed to the package, so a
      // shop's entire catalogue appeared whichever service was being booked — the user's report.
      // The authenticated list has always applied the rule below (see the
      // `packageSnapshot?.services?.some(...)` filter in fetchCustomerPackages), and the two paths
      // disagreeing about the same question is the defect shape Phases 2 and 3 were both caused by.
      //
      // A package is relevant when it covers ANY service in THIS booking, not all of them: a bundle
      // that contains the service being booked can genuinely pay for it.
      // The WHOLE for-sale list is stored, unfiltered. Relevance is decided at render (see
      // `relevantAvailablePackages`) so it follows the current selection instead of being frozen at
      // whatever was known when this happened to run.
      //
      // Deliverability is not checked here: this endpoint applies
      // `packageSellabilityFilter({ requireDeliverable: true })`, so every package it returns ALREADY
      // has a subscribed professional. A per-professional check used to sit here, asking whether the
      // SELECTED professional was a subscriber — a question this component often cannot answer, and
      // when it could not, every package was dropped.
      setAvailableShopPackages(data.packages || []);
        // DELIVERABILITY IS NOT CHECKED HERE, and that is deliberate.
        //
        // This endpoint already applies `packageSellabilityFilter({ requireDeliverable: true })`, so
        // every package it returns already has a subscribed professional. Re-checking that adds
        // nothing — and the check that used to sit here asked whether the SELECTED professional was
        // a subscriber. This runs before a professional is often chosen, so the list came back empty
        // for guests AND for signed-in customers, on every service, including services a package
        // genuinely covers. Deliverability is the server's guarantee; this side decides relevance.

        // …and it must cover something being booked. With no service chosen there is no booking for
        // a package to be relevant to, and showing none is better than showing the wrong ones - the
        // section that renders this list hides itself when the list is empty.
        //
        // This is now decided by `relevantAvailablePackages` at render time, not here.
    } catch (err) {
    }
  };

  const fetchShopServices = async (shopId, staffId) => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/staff/shop/${shopId}/services?staffId=${staffId}`,
        { headers },
      );

      if (response.ok) {
        const data = await response.json();
        setShopServices(data.services || []);
      } else {
        // Fallback: use current service only
        setShopServices([]);
      }
    } catch (err) {
      setShopServices([]);
    }
  };

  const handlePackageSelection = (pkg) => {
    setSelectedPackage(pkg);
  };

  /**
   * BUY A PACKAGE FROM INSIDE THE BOOKING (2026-10-26).
   *
   * The for-sale cards were informational only, because buying them meant leaving the page and losing everything
   * typed so far. The draft is saved FIRST — synchronously — so the purchase page can be opened without any
   * state being at risk, and `returnTo` brings the customer back to this exact URL (same shop, professional and
   * service), where the draft is restored.
   *
   * NOTHING IS AUTO-APPLIED when they return: the new package simply appears among their packages, and the
   * summary's own "Use my package" button applies it — the same rule as any other package they own.
   */
  const startPackagePurchase = (pkg) => {
    if (!pkg?._id) return;
    // THE SAME INVENTORY THE CONTINUOUS SAVE USES, not a hand-written subset: this write REPLACES the stored
    // draft, so a field missing here is a field the customer loses. The panel they were working in is part of it,
    // so buying from the summary comes back to the summary — safe to restore because the page already drops a slot
    // that is no longer offered and sends them back to the picker when it does, with no second availability check.
    saveBookingDraft(draftScope, draftValues());

    const returnTo = `${window.location.pathname}${window.location.search}`;
    const purchaseShopId =
      (pkg.shopId && (pkg.shopId._id || pkg.shopId)) || shopId || "";
    const query = new URLSearchParams();
    if (purchaseShopId) query.set("shopId", String(purchaseShopId));
    query.set("returnTo", returnTo);
    navigate(`/packages/${pkg._id}/purchase?${query.toString()}`);
  };

  const handleClearPackage = () => {
    setSelectedPackage(null);
  };

  /**
   * AND IF THE LINK NAMED ONE, SELECT IT (plan item 2.12, 2026-10-23).
   *
   * Applied ONCE, from the purchases the server returned — never from the URL alone. A purchase id in a link
   * proves nothing: the package has to be in `customerPackages`, which is the server's answer for THIS customer
   * (or a guest who passed the challenge). An id that is not there is simply ignored, so a stale link or a
   * package that has since been transferred away leaves the page working normally instead of erroring.
   *
   * NOTHING IS SPENT HERE. Selecting a package only flips `selectedPackage`; the session is claimed when the
   * booking is confirmed. That is the whole guard on this button — an abandoned booking must cost nothing.
   */
  const packagePreselectApplied = useRef(false);
  useEffect(() => {
    if (!packageIdParam || packagePreselectApplied.current) return;
    if (!Array.isArray(customerPackages) || customerPackages.length === 0) return;
    const match = customerPackages.find(
      (pkg) => String(pkg?._id) === String(packageIdParam),
    );
    if (!match) return;
    packagePreselectApplied.current = true;
    handlePackageSelection(match);
    // Keyed to the parameter and the server's list; the handler is a plain setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageIdParam, customerPackages]);

  // ── Phase 18 / D57: a GUEST proves a package purchase is theirs ────────────────────────────
  //
  // Two factors a stranger lacks: control of the mailbox (the code) and the phone's last 4 digits.
  // The page never asks for, or trusts, a purchase id the guest typed.
  // ── LEAVING THE CODE STEP (added 2026-10-07) ──────────────────────────────────────────────
  //
  // Reported: a WRONG email was typed into "Use my package", the code screen appeared, and the
  // email was then unreachable. There was no way back to it, and `Cancel` reopened on the SAME step
  // because nothing reset `step` — so a typo in the email became a dead end.
  //
  // Two exits, deliberately different:
  //   • backToGuestIdentity()      — stays in the modal, KEEPS the typed identity so it can be
  //                                  corrected rather than retyped
  //   • closeGuestPackageAccess()  — leaves entirely AND resets the step, so reopening starts clean.
  //     Without the reset, Cancel and close were the same dead end.
  const backToGuestIdentity = () => {
    setGuestChallenge((prev) => ({
      ...prev,
      step: "identity",
      code: "",
      error: "",
      busy: false,
    }));
  };

  const closeGuestPackageAccess = () => {
    backToGuestIdentity();
    setShowGuestPackageAccess(false);
  };

  const startGuestPackageChallenge = async () => {
    setGuestChallenge((prev) => ({ ...prev, error: "", busy: true }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/packages/guest/challenge/start`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: guestChallenge.email,
            phoneLast4: guestChallenge.phoneLast4,
            // THE THIRD FACTOR (user, 2026-10-09). Sent exactly as typed minus the spaces: the server strips
            // separators too, so the grouped form a person reads back off the email is the same number.
            accountNumber: guestChallenge.accountNumber || "",
          }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        setGuestChallenge((prev) => ({
          ...prev,
          busy: false,
          error: data.message || "Unable to send a code.",
        }));
        return;
      }
      // THE MATCH IS NOW REPORTED (2026-10-07). A miss arrives as 404 and is handled above, which keeps
      // the guest on the identity step with their entry intact so a typo can be corrected — that path
      // used to advance to a code screen for a code that was never sent.
      //
      // A match whose email FAILED to send must not advance either: the guest would sit on the code
      // screen waiting for something that is never coming. Say so and stay put.
      if (data.emailDelivered === false) {
        setGuestChallenge((prev) => ({
          ...prev,
          busy: false,
          error:
            data.message ||
            "We found your package but couldn't send the code. Please try again.",
        }));
        return;
      }
      setGuestChallenge((prev) => ({
        ...prev,
        busy: false,
        step: "code",
        challengeId: data.challengeId,
        error: "",
      }));
    } catch (err) {
      setGuestChallenge((prev) => ({
        ...prev,
        busy: false,
        error: "Unable to send a code. Please try again.",
      }));
    }
  };

  const verifyGuestPackageChallenge = async () => {
    setGuestChallenge((prev) => ({ ...prev, error: "", busy: true }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/packages/guest/challenge/verify`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            challengeId: guestChallenge.challengeId,
            otpCode: guestChallenge.code,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        setGuestChallenge((prev) => ({
          ...prev,
          busy: false,
          error: data.message || "Incorrect or expired code.",
        }));
        return;
      }

      // SESSION storage only: never a URL, never localStorage. The token is scoped and lasts 10 min.
      sessionStorage.setItem(
        "ogestyle_guest_package_token",
        data.guestAccessToken,
      );
      setGuestChallenge({
        step: "identity",
        email: "",
        phoneLast4: "",
        challengeId: "",
        code: "",
        error: "",
        busy: false,
      });
      setShowGuestPackageAccess(false);
      // The booking selections are untouched — only the PROOF changed.
      //
      // AND THE PROOF IS USELESS UNTIL A PACKAGE IS CHOSEN. Stopping at "verified" left the guest
      // looking at an unchanged page: the entry reappeared, nothing read as selected, and the button
      // still offered Stripe. They asked to use a package, so one is selected for them here; the
      // owned-packages list renders so they can change it, and `selectedPackage` is what switches the
      // Pay button over to the package and sends the scoped token as the Bearer on submit.
      const {
        eligible: owned,
        activeCount,
        usedUp,
        heldUnits,
      } = (await fetchCustomerPackages()) || {
        eligible: [],
        activeCount: 0,
        usedUp: [],
        heldUnits: 0,
      };
      if (owned.length > 0) {
        setSelectedPackage(owned[0]);
        setGuestPackageNotice("");
      } else if (heldUnits > 0) {
        // NOTHING LEFT TO SPEND, BUT SOMETHING LEFT TO GET BACK. "Fully used" would be false — the sessions
        // exist, held from a late cancellation and payable below — so the notice points at the offer instead.
        // (Reported 2026-10-19: the fully-used message showed while the restore offer was on the same page.)
        setSelectedPackage(null);
        setGuestPackageNotice(
          `Your package has ${heldUnits} session${heldUnits === 1 ? "" : "s"} held from a cancellation that you can pay to get back — see the offer below. You can also book this appointment by paying with your card.`,
        );
        setShowGuestPackageAccess(false);
      } else {
        // SAY WHAT ACTUALLY HAPPENED. "Check the service, or contact the shop" was useless advice for a
        // customer whose package is simply finished — and it read as though they had done something
        // wrong. The booking can always continue on a card, so every message ends by saying so.
        const finished = usedUp.find(
          (p) =>
            (Number(p.sessionsUsed) || 0) >= (Number(p.totalSessions) || 0) ||
            p.status === "completed",
        );
        if (finished) {
          setGuestPackageNotice(
            `Your ${finished.name} package has been fully used. You can still book this appointment by paying with your card.`,
          );
        } else if (usedUp.length > 0) {
          setGuestPackageNotice(
            `Your ${usedUp[0].name} package is no longer valid. You can still book this appointment by paying with your card.`,
          );
        } else if (activeCount > 0) {
          setGuestPackageNotice(
            "That package does not include this service. You can still book this appointment by paying with your card.",
          );
        } else {
          setGuestPackageNotice(
            "We could not find a package with those details. You can still book this appointment by paying with your card.",
          );
        }
        setShowGuestPackageAccess(false);
      }
    } catch (err) {
      setGuestChallenge((prev) => ({
        ...prev,
        busy: false,
        error: "Unable to verify that code. Please try again.",
      }));
    }
  };

  const fetchNextAvailableSlots = async (currentDate, days = 5) => {
    try {
      const queryServiceDuration =
        Number(effectiveBundleDurationMinutes) ||
        normalizeServiceDuration(service) ||
        0;
      if (!queryServiceDuration) {
        throw new Error(
          "Selected service is missing a valid duration. Please refresh and try again.",
        );
      }
      const locationId =
        shop?._id ||
        staff?.activeLocationId?._id ||
        staff?.activeLocationId ||
        shopId;

      if (!locationId) {
        throw new Error("Failed to resolve booking location for availability");
      }

      const startDate = new Date(`${currentDate}T12:00:00`);
      if (Number.isNaN(startDate.getTime())) {
        throw new Error("Invalid booking date");
      }

      const searchDays = Math.min(Math.max(Number(days) || 5, 1), 30);
      const searchEnd = new Date(startDate);
      searchEnd.setDate(searchEnd.getDate() + searchDays);

      const monthCache = new Map();
      const loadMonthAvailability = async (dateObj) => {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const monthKey = `${year}-${month}`;

        if (monthCache.has(monthKey)) {
          return monthCache.get(monthKey);
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/bookings/available-slots-month?staffId=${staffId}&locationId=${locationId}&month=${monthKey}&serviceDuration=${queryServiceDuration}&fresh=1`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch next available slots");
        }

        const monthData = await response.json();
        monthCache.set(monthKey, monthData);
        return monthData;
      };

      const collectedSlots = [];
      const cursor = new Date(startDate);

      while (cursor <= searchEnd && collectedSlots.length < 6) {
        const year = cursor.getFullYear();
        const month = String(cursor.getMonth() + 1).padStart(2, "0");
        const day = String(cursor.getDate()).padStart(2, "0");
        const dateKey = `${year}-${month}-${day}`;
        const monthData = await loadMonthAvailability(cursor);
        const daySlots = monthData?.days?.[dateKey]?.slots || [];

        for (const slot of daySlots) {
          const time24 = slot.time24 || slot.start || "";
          collectedSlots.push({
            date: dateKey,
            displayDate: slot.displayDate || formatDisplayDate(dateKey),
            time24,
            time12: slot.time12 || formatTime12(time24),
            endTime: slot.endTime || slot.end || "",
            available: true,
          });

          if (collectedSlots.length >= 6) {
            break;
          }
        }

        cursor.setDate(cursor.getDate() + 1);
      }

      setNextAvailableError("");
      setNextAvailableSlots(collectedSlots);
    } catch (err) {
      setNextAvailableError(err?.message || "Unable to fetch next available slots");
      setNextAvailableSlots([]);
    }
  };

  const handleJoinWaitlist = async ({ replaceExistingWaitlist = false } = {}) => {
    const token = localStorage.getItem("ogestyle_token");
    const customerName =
      `${customerInfo.firstName || ""} ${customerInfo.lastName || ""}`.trim();
    const sameDayWaitlistMessage =
      "Same day waitlist is not available. Please choose another date and time.";
    const today = new Date();
    const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    if (selectedDate === todayYmd) {
      setWaitlistMessage({
        type: "error",
        text: sameDayWaitlistMessage,
      });
      return;
    }

    if (!token && (!customerName || !customerInfo.email)) {
      setWaitlistMessage({
        type: "error",
        text: "Please provide your name and email to join the waitlist.",
      });
      return;
    }

    if (!shop?._id || !service?._id || !staff?._id) {
      setWaitlistMessage({
        type: "error",
        text: "Missing required booking context for waitlist.",
      });
      return;
    }

    if (!["R3", "R4", "R7"].includes(staff.role)) {
      setWaitlistMessage({
        type: "error",
        text: "Waitlist is currently available for active professionals only.",
      });
      return;
    }

    setWaitlistSubmitting(true);
    setWaitlistMessage({ type: "", text: "" });
    setActiveWaitlistConflict(null);
    if (replaceExistingWaitlist) {
      setPendingWaitlistReplacement(null);
    }

    try {
      // P4: the waitlist must describe what the customer would actually book, so
      // it carries the RESOLVED lines (per-unit quantity, hours, chosen option)
      // instead of the catalog values. `addOnLineItems` already pairs each add-on
      // with its own selection.
      const waitlistLineItems = [
        ...(service
          ? [
              {
                service,
                selection: {
                  quantity: serviceQuantity,
                  hours: serviceHours,
                  tierId: serviceTierId,
                },
              },
            ]
          : []),
        ...addOnLineItems,
      ];
      const waitlistResolution = resolveServiceLines(waitlistLineItems);
      const waitlistServiceBundle = waitlistResolution.lines.map(
        (line, index) => ({
          serviceId: String(line.serviceId || ""),
          name: String(line.label || line.name || "Service"),
          durationMinutes: Number(line.totalMinutes) || 0,
          price: Number(line.totalPrice) || 0,
          selection: waitlistLineItems[index]?.selection || {},
        }),
      );
      // The length the provider has to hold; falls back to the catalog length only
      // when nothing resolved (i.e. a required choice is still outstanding).
      const totalServiceDurationMinutes =
        waitlistResolution.totalMinutes > 0
          ? waitlistResolution.totalMinutes
          : Number(normalizeServiceDuration(service) || 0) || 0;
      const totalServicePrice =
        Number(effectiveServicePrice || normalizeServicePrice(service) || 0) || 0;

      const payload = {
        shopId: shop._id,
        staffId: staff._id,
        serviceId: service._id,
        preferredDates: selectedDate ? [selectedDate] : [],
        preferredTimeSlots: [],
        flexibleDates: false,
        flexibleStaff: false,
        customerName,
        customerFirstName: customerInfo.firstName,
        customerLastName: customerInfo.lastName,
        customerEmail: customerInfo.email,
        customerPhone: customerInfo.phone,
        serviceBundle: waitlistServiceBundle,
        totalServiceDurationMinutes,
        totalServicePrice,
        // F2: the server re-resolves this and owns the request's duration/price.
        selection: {
          quantity: serviceQuantity,
          hours: serviceHours,
          tierId: serviceTierId,
        },
        bookingContext: {
          source: "customer-booking",
          role: staff.role || null,
          selectedDate: selectedDate || null,
          groupSize: Number(groupSize) || 1,
        },
        notes: selectedDate
          ? `Requested from booking flow for preferred date ${selectedDate}`
          : "Requested from booking flow without a specific date",
      };

      payload.waitDurationDays = waitlistDurationDays;
      if (replaceExistingWaitlist) {
        payload.replaceExistingWaitlist = true;
      }

      const requestHeaders = {
        "Content-Type": "application/json",
      };

      if (token) {
        requestHeaders.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/waitlist`,
        {
          method: "POST",
          headers: requestHeaders,
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();
      if (!response.ok) {
        if (response.status === 409 && data?.error === "waitlist_replacement_required") {
          setPendingWaitlistReplacement(data.existingWaitlist || {});
          setWaitlistMessage({
            type: "warning",
            text:
              data.message ||
              "You already have a waitlist request for this provider and date.",
          });
          return;
        }
        if (response.status === 409 && data?.error === "waitlist_cooldown_active") {
          setPendingWaitlistReplacement(null);
          setActiveWaitlistConflict(data.existingWaitlist || {});
          setWaitlistMessage({
            type: "warning",
            text:
              data.message ||
              "You already have an active waitlist request within 21 days of this requested date.",
          });
          return;
        }
        throw new Error(data.message || data.error || "Failed to join waitlist");
      }

      setPendingWaitlistReplacement(null);
      setActiveWaitlistConflict(null);
      setWaitlistMessage({
        type: "success",
        text:
          data?.message ||
          "Waitlist request saved. Your waitlist is not active or guaranteed until you book an appointment.",
      });
      setWaitlistJoined(true);
    } catch (err) {
      setWaitlistMessage({
        type: "error",
        text: err.message || "Unable to join waitlist right now.",
      });
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const handleKeepExistingWaitlist = () => {
    setPendingWaitlistReplacement(null);
    setWaitlistMessage({
      type: "success",
      text: "Your existing waitlist request was kept unchanged. You can continue booking.",
    });
  };

  const today = new Date();
  const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const isSelectedDateToday = selectedDate === todayYmd;
  const sameDayWaitlistMessage =
    "Same day waitlist is not available. Please choose another date and time.";

  const handleSelectNextSlot = (slot) => {
    setSelectedDate(slot.date);
    setSelectedSlot(slot);
    setSlotInvalidatedNotice("");
    saveDirectBookingHold({
      shopId,
      staffId,
      serviceId,
      appointmentDate: slot.date,
      startTime: slot.time24 || slot.startTime,
    });

    // Only re-open auth for users who have not already chosen guest checkout.
    if (!isAuthenticated && !hasChosenGuestCheckout) {
      setAuthModalMode("register");
      setShowAuthModal(true);
    }
  };

  const handleManageAdditionalServices = () => {
    if (selectedSlot) {
      setSelectedSlot(null);
      setShowServiceSelector(true);
      scrollToSection(dateTimeSectionRef);
      return;
    }

    setShowServiceSelector((prev) => !prev);
  };

  const focusBillingAddressForProducts = (
    returnTarget = BOOKING_BILLING_ADDRESS_RETURN_TARGET.PRODUCTS,
  ) => {
    setBillingAddressReturnTarget(getBillingAddressReturnTarget(returnTarget));
    window.requestAnimationFrame(() => {
      if (billingAddressSectionRef.current) {
        billingAddressSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      window.setTimeout(() => {
        billingStreetInputRef.current?.focus();
      }, 150);
    });
  };

  const scrollToSection = (sectionRef) => {
    if (
      sectionRef === customerInfoSectionRef ||
      sectionRef === contactSectionRef
    ) {
      setExpandedSection("customerInfo");
    } else if (sectionRef === dateTimeSectionRef) {
      setExpandedSection("dateTime");
    } else if (sectionRef === serviceCardRef) {
      // The service card only renders outside the summary step, so leave the
      // summary to reveal the option picker before scrolling to it.
      setExpandedSection("dateTime");
    } else if (
      sectionRef === productsSectionRef ||
      sectionRef === billingAddressSectionRef
    ) {
      setExpandedSection("products");
    } else if (sectionRef === summarySectionRef) {
      setExpandedSection("summary");
      setSummaryReturnSection("");
    }

    sectionRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    if (selectedSlot && showServiceSelector) {
      setShowServiceSelector(false);
    }
  }, [selectedSlot, showServiceSelector]);

  const toggleProduct = (product, selectedVariant = null) => {
    const selectedProductId = selectedVariant?._id || product._id;
    const selectedPrice =
      typeof selectedVariant?.price === "number"
        ? selectedVariant.price
        : typeof product.price === "number"
          ? product.price
          : 0;
    const selectedVariantAttributes = Array.isArray(selectedVariant?.variantAttributes)
      ? selectedVariant.variantAttributes.map((attribute) => ({ ...attribute }))
      : [];
    const primaryVariantAttribute = selectedVariantAttributes[0] || null;
    const variantType = String(
      selectedVariant?.variantType ||
        primaryVariantAttribute?.dimensionName ||
        primaryVariantAttribute?.dimensionKey ||
        primaryVariantAttribute?.name ||
        primaryVariantAttribute?.type ||
        "",
    ).trim();
    const variantValue = String(
      primaryVariantAttribute?.label || primaryVariantAttribute?.value || "",
    ).trim();
    const variantDisplayName =
      selectedVariant?.variantName ||
      selectedVariant?.variantLabel ||
      variantValue ||
      "";
    const selectedName = variantDisplayName
      ? `${product.name} - ${variantDisplayName}`
      : product.name;
    const selectedStripeTaxCode =
      selectedVariant?.stripeTaxCode || product.stripeTaxCode;

    const existing = selectedProducts.find(
      (p) => p.productId === selectedProductId,
    );
    if (existing) {
      setSelectedProducts(
        selectedProducts.filter((p) => p.productId !== selectedProductId),
      );
    } else {
      const isFirstProduct = selectedProducts.length === 0;
      setSelectedProducts([
        ...selectedProducts,
        {
          productId: selectedProductId,
          familyProductId: product._id,
          quantity: 1,
          price: selectedPrice,
          name: selectedName,
          stripeTaxCode: selectedStripeTaxCode,
          variantKey: selectedVariant?._id || null,
          variantLabel: variantDisplayName || "",
          variantType,
          variantValue,
          variantAttributes: selectedVariantAttributes,
        },
      ]);
      if (isFirstProduct) {
        focusBillingAddressForProducts("products");
      }
    }
  };

  const updateProductQuantity = (productId, quantity) => {
    if (quantity < 1) return;
    setSelectedProducts(
      selectedProducts.map((p) =>
        p.productId === productId ? { ...p, quantity } : p,
      ),
    );
  };

  const calculateTotals = () => {
    // Calculate service total for primary customer
    // F1: `effectiveServicePrice` is the engine-resolved total (per-unit × qty)
    let serviceTotal = effectiveServicePrice || service?.price || 0;

    // Add group members' service costs (they may have different services).
    // P4: resolve each member's own line rather than reading the catalog `price`,
    // so a per-unit / hourly member service scales with that member's quantity.
    // A member whose line still needs a choice falls back to the catalog price -
    // P5 adds the member selector and the gate that stops an unchosen member
    // reaching checkout at all.
    if (groupSize > 1 && groupMembers.length > 0) {
      const memberServiceTotal = groupMembers.reduce(
        (sum, member, index) => {
          const memberService = shopServices.find(
            (s) => s._id === member.serviceId,
          );
          if (!memberService) return sum;
          // P5: the member's own resolved line - the same array the slot chain
          // reads, so the money and the times can never disagree.
          const memberLine = resolvedMemberLines[index] || null;
          const memberPrice =
            !memberLine || memberLine.requiresSelection
              ? Number(memberService.price) || 0
              : Number(memberLine.totalPrice) || 0;
          return sum + memberPrice;
        },
        0,
      );
      serviceTotal += memberServiceTotal;
    }

    // Apply package discount if selected
    //
    // `packageCoveredAmount` is the service value the package PAYS FOR, captured BEFORE it is zeroed.
    // The summary used to print `service.price * groupSize` — the raw catalog price — so a per-unit
    // line of 10 x $15 showed a "Package Credit -$15.00" against a $150.00 line, and the row
    // contradicted the total underneath it. The credit must be the same figure the service row charges.
    let packageCoveredAmount = 0;
    // What the CARD will pay of the services (split payment). Zero unless the package covers only part of them.
    let cardServiceTotal = 0;
    /**
     * THE TIP IS EARNED ON THE WHOLE APPOINTMENT, INCLUDING WHAT THE PACKAGE PAYS FOR (reported 2026-10-27).
     *
     * The professional delivers both services — the one the package covered and the one the card paid for — so the
     * gratuity is a percentage of BOTH, and the card carries it. That is what the server computes and what Stripe
     * charges. The page computed it from `serviceTotal` AFTER the package credit, so a $50 appointment showed a
     * $3.00 tip on the summary and $7.50 on Stripe: two figures for one gratuity.
     *
     * Captured here, before the credit reduces `serviceTotal`, and returned so the preset labels and the custom-tip
     * ceiling use the same base.
     */
    const tipBaseServiceTotal = serviceTotal;
    if (selectedPackage) {
      /**
       * THE WHOLE CREDIT NO LONGER DEPENDS ON COVERING EVERY PERSON (split payment, 2026-10-27).
       *
       * It used to require `groupSize <= sessionsRemaining`, so a Father & Son package with one session left and
       * a party of two fell through to "no credit at all" — the $30 the package paid for went onto the card
       * instead, beside a "$0.00" package credit row. That is the reported *"a customer is still being charged for
       * a covered service"*. The count now comes FROM the per-person decision, which is the same decision the
       * server makes.
       */
      const partyCheck = packagePartyCheck(selectedPackage);
      if (partyCheck.ok) {
        // WHICH PEOPLE THE PACKAGE TOOK (split payment, 2026-10-27). A party is no longer all-or-nothing: the
        // package pays for the primary and for each member it covers, and the rest of the party is charged to
        // the card — so the credit must be built person by person instead of zeroing the whole service total.
        const coveredMembers = new Set(
          (partyCheck.coveredMemberIndexes || []).map(Number),
        );
        const memberPriceAt = (index) => {
          const member = (groupMembers || [])[index - 1];
          if (!member) return 0;
          const memberService = shopServices.find(
            (s) => s._id === member.serviceId,
          );
          if (!memberService) return 0;
          const memberLine = resolvedMemberLines[index - 1] || null;
          return !memberLine || memberLine.requiresSelection
            ? Number(memberService.price) || 0
            : Number(memberLine.totalPrice) || 0;
        };

        if (groupSize > 1) {
          const primaryShare = Math.max(
            0,
            serviceTotal -
              (groupMembers || []).reduce(
                (sum, _member, position) => sum + memberPriceAt(position + 1),
                0,
              ),
          );
          const coveredMemberTotal = (groupMembers || []).reduce(
            (sum, _member, position) =>
              coveredMembers.has(position + 1) ? sum + memberPriceAt(position + 1) : sum,
            0,
          );
          const cardMemberTotal = (groupMembers || []).reduce(
            (sum, _member, position) =>
              coveredMembers.has(position + 1) ? sum : sum + memberPriceAt(position + 1),
            0,
          );
          packageCoveredAmount = roundMoney(primaryShare + coveredMemberTotal);
          cardServiceTotal = roundMoney(cardMemberTotal);
          serviceTotal = cardServiceTotal;
        } else {
          /**
           * ONE PERSON: THE PACKAGE PAYS FOR THE LINES IT INCLUDES, THE CARD FOR THE REST (split payment).
           *
           * The add-ons are the only lines that can fall outside it — the primary had to be covered for the
           * package to be usable at all. Each add-on's RESOLVED line is used, the same figure its row shows, so
           * the credit and the row cannot disagree about what the package took.
           */
          const coveredIds = packageCoveredServiceIdSet(selectedPackage);
          const uncoveredAddOnTotal = (serviceBundle || []).reduce((sum, svc) => {
            if (serviceIsCoveredByPackage(coveredIds, svc)) return sum;
            const line = resolveServicePricing(svc, addOnSelections[String(svc?._id)] || {});
            return sum + (line?.requiresSelection ? 0 : Number(line?.totalPrice) || 0);
          }, 0);
          cardServiceTotal = uncoveredAddOnTotal;
          packageCoveredAmount = Math.max(0, serviceTotal - uncoveredAddOnTotal);
          serviceTotal = uncoveredAddOnTotal;
        }
      }
    }

    const productsTotal = selectedProducts.reduce(
      (sum, p) => sum + p.price * p.quantity,
      0,
    );

    let subtotal = serviceTotal + productsTotal;

    // Apply promo code discount
    const promoDiscount = appliedPromo ? appliedPromo.discountAmount : 0;
    subtotal = Math.max(0, subtotal - promoDiscount);

    // Tip only on service total (before promo), NOT on products.
    // A custom tip is a flat amount the customer typed; a preset is a percentage of the
    // service. The backend takes whichever we send - percentage wins only if it is > 0.
    //
    // The base is `tipBaseServiceTotal` — the WHOLE appointment, before the package credit — because that is the
    // value the professional delivers and the figure the server charges.
    const tipAmount = tipIsCustom
      ? Math.max(0, parseCustomTip() || 0)
      : (tipBaseServiceTotal * tipPercentage) / 100;
    const taxAmount =
      taxPreview.calculatedAtCheckout === false
        ? Number(taxPreview.taxAmount || 0)
        : 0;
    const total = subtotal + tipAmount + taxAmount;

    return {
      serviceTotal,
      packageCoveredAmount,
      cardServiceTotal,
      tipBaseServiceTotal,
      productsTotal,
      subtotal: serviceTotal + productsTotal, // Original subtotal before promo
      promoDiscount,
      tipAmount,
      taxAmount,
      total,
    };
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }

    setPromoLoading(true);
    setPromoError("");

    try {
      const totals = calculateTotals();
      const bookingAmount = totals.subtotal; // Before promo

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/promo-codes/validate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code: promoCode.toUpperCase(),
            bookingAmount,
            serviceId,
            professionalId: staffId,
            customerId: isAuthenticated ? "current" : null,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.data.isValid) {
          setAppliedPromo({
            code: promoCode.toUpperCase(),
            discountAmount: data.data.discountAmount,
          });
          setPromoError("");
        } else {
          setPromoError(data.data.message || "Invalid promo code");
          setAppliedPromo(null);
        }
      } else {
        const error = await response.json();
        setPromoError(error.message || "Invalid promo code");
        setAppliedPromo(null);
      }
    } catch (error) {
      setPromoError("Failed to validate promo code");
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode("");
    setAppliedPromo(null);
    setPromoError("");
  };

  const focusContactSection = () => {
    if (contactSectionRef.current) {
      contactSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    if (phoneInputRef.current) {
      window.setTimeout(() => phoneInputRef.current?.focus(), 150);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // THE MODAL'S ENTER KEY MUST NOT BOOK A RESERVATION. The modal is rendered INSIDE this form, so
    // pressing Enter in it triggered the browser's IMPLICIT FORM SUBMISSION — the booking form — instead
    // of the modal's own action. Reported 2026-10-05: a guest typing their email and hitting Enter got
    // the underlying page's behaviour. The modal handles Enter itself (see below); this is the second
    // line of defence, for any other route into submitting while the modal is open.
    if (showGuestPackageAccess) {
      return;
    }

    if (itemCount > 0 && !cartWarningAcknowledged) {
      setShowCartWarningModal(true);
      return;
    }

    if (!policyAccepted) {
      setError("Please acknowledge the cancellation policy before proceeding.");
      return;
    }

    // The acknowledgement is a gate, not a decoration: the button is disabled on it, and a button is not a closed
    // API — the same reason every other guard here is repeated in the handler.
    if (splitNeedsAcknowledgement && !splitAcknowledged) {
      setError(
        "Please confirm you understand that the part of this booking your package does not cover is charged to your card.",
      );
      return;
    }

    if (
      waitlistCompatibility?.hasPendingWaitlist &&
      waitlistCompatibility.compatible === false &&
      !waitlistMismatchAcknowledged
    ) {
      setError(
        "Please acknowledge that continuing will cancel your pending waitlist request.",
      );
      waitlistMismatchSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    if (!selectedSlot) {
      setError("Please select a time slot");
      return;
    }

    const holdIdentity = {
      shopId,
      staffId,
      serviceId,
      appointmentDate: selectedDate,
      startTime: selectedSlot.time24,
    };
    // The client hold is ADVISORY. It is a sessionStorage timer in this browser only -
    // it reserves nothing on the server - so it must never block a booking the server
    // would accept. Blocking here refused to charge a customer for a slot that was
    // still open, purely because they took more than 10 minutes to fill the form.
    // The server is the authority: it re-checks availability and answers
    // `time_slot_unavailable` if the time really was taken.
    if (!getDirectBookingHold(holdIdentity)) {
      setSlotInvalidatedNotice(
        "This time may have been taken while you were completing the form. We will confirm it when you continue to payment.",
      );
    }

    // Validate recurring booking fields
    if (isRecurring) {
      if (recurringEndType === "date" && !recurringEndDate) {
        setError("Please select an end date for recurring bookings");
        return;
      }
      if (recurringEndType === "occurrences" && recurringOccurrences < 1) {
        setError("Please specify at least 1 occurrence");
        return;
      }
    }

    if (tipIsCustom) {
      const parsed = parseCustomTip();
      if (parsed === null || parsed < 0) {
        setError(
          "Enter a tip amount, or choose No tip if you would rather not leave one.",
        );
        return;
      }
      // Compute the ceiling from a fresh calculation rather than a `totals` from an outer
      // scope. The submit handler declares its own `totals` lower down, inside the try, so
      // relying on the enclosing one here would be a TDZ accident waiting to happen.
      const ceiling = maxCustomTip(calculateTotals().serviceTotal);
      if (parsed > ceiling) {
        setError(
          `A tip cannot be more than ${formatCurrency(
            ceiling,
            isFreelancerBooking
              ? staff?.currency || freelancerBusiness?.currency || "USD"
              : shop?.currency || "USD",
          )} (100% of the service). Please enter a smaller amount.`,
        );
        return;
      }
    }

    if (!customerInfo.phone.trim()) {
      setError(
        "A phone number is required to complete this booking. Please add it to continue.",
      );
      focusContactSection();
      return;
    }

    setPaymentProcessing(true);
    setError("");

    try {
      const requiresProductAddress = isBillingAddressRequired(selectedProducts);
      if (
        requiresProductAddress &&
        (!customerAddress.street ||
          !customerAddress.city ||
          !customerAddress.state ||
          !customerAddress.zipCode ||
          !customerAddress.country)
      ) {
        setError(
          "Billing address is required when products are included in booking checkout",
        );
        focusBillingAddressForProducts("summary");
        setPaymentProcessing(false);
        return;
      }

      const totals = calculateTotals();
      // ONE ANSWER TO "CAN THE PACKAGE PAY FOR THIS?", used for BOTH what the request claims and how the
      // payment is recorded — so the two can never disagree. Read here rather than from `packageCannotPay`
      // because this runs after render, when that const is long initialised either way.
      const usablePackage =
        Boolean(selectedPackage) && packagePartyCheck(selectedPackage).ok;
      const token = localStorage.getItem("ogestyle_token");
      const normalizedCustomerInfo = {
        firstName: customerInfo.firstName.trim(),
        lastName: customerInfo.lastName.trim(),
        email: customerInfo.email.trim(),
        phone: customerInfo.phone.trim(),
      };

      if (isAuthenticated && token) {
        const profileResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/profile`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(normalizedCustomerInfo),
          },
        );

        const profileData = await profileResponse.json();
        if (!profileResponse.ok) {
          throw new Error(
            profileData.message || "Failed to save your contact information",
          );
        }

        if (profileData.user) {
          setCustomerInfo({
            firstName: profileData.user.firstName || normalizedCustomerInfo.firstName,
            lastName: profileData.user.lastName || normalizedCustomerInfo.lastName,
            email: profileData.user.email || normalizedCustomerInfo.email,
            phone: profileData.user.phone || normalizedCustomerInfo.phone,
          });
        }
      }

      // Base booking data
      // P1/P2 (F3): every bundle line is resolved with its OWN selection, and the
      // resolved value is what gets sent so the server re-resolves to the same
      // numbers instead of falling back to a catalog default.
      const selectedBundle = getBundleServices();
      const bundleServices = selectedBundle.map((svc) => {
        const svcKey = String(svc._id);
        const isPrimary = svcKey === String(service?._id);
        const selection = isPrimary
          ? {
              quantity: serviceQuantity,
              hours: serviceHours,
              tierId: serviceTierId,
            }
          : addOnSelections[svcKey] || {};
        const line = resolveServicePricing(svc, selection);

        return {
          serviceId: svc._id,
          name: (line && line.label) || svc.name,
          durationMinutes:
            (line && line.totalMinutes) || normalizeServiceDuration(svc),
          price: (line && line.totalPrice) || normalizeServicePrice(svc),
          // P6: the server reads a per-line `selection`; legacy clients omit it.
          selection,
        };
      });
      const bundleDuration = bundleServices.reduce(
        (sum, svc) => sum + (Number(svc.durationMinutes) || 0),
        0,
      );
      const bundleServicePrice = bundleServices.reduce(
        (sum, svc) => sum + (Number(svc.price) || 0),
        0,
      );

      // P1/P2 (F3): the engine owns price + duration for the WHOLE bundle as soon
      // as any line is selection-driven - not just for a lone primary service.
      const engineDuration = anyLineSelectionDriven
        ? resolvedTotalMinutes
        : null;
      const enginePrice = anyLineSelectionDriven
        ? resolvedBundleLines.totalPrice
        : null;

      const baseBookingData = {
        shopId: isFreelancerBooking
          ? freelancerBusiness?._id || shop._id
          : shop._id,
        staffId,
        serviceId,
        appointmentDate: selectedDate,
        startTime: selectedSlot.time24,
        endTime: engineDuration
          ? addMinutesToTime24(selectedSlot.time24, engineDuration) ||
            selectedSlot.endTime
          : selectedSlot.endTime,
        customerTimezone: shop?.timezone,
        firstName: normalizedCustomerInfo.firstName,
        lastName: normalizedCustomerInfo.lastName,
        email: normalizedCustomerInfo.email,
        phone: normalizedCustomerInfo.phone,
        smsConsent: isAuthenticated ? true : smsConsent, // Authenticated users assumed opted-in, guests must check box
        customerName:
          `${normalizedCustomerInfo.firstName || ""} ${normalizedCustomerInfo.lastName || ""}`.trim() ||
          "Customer",
        customerAddress:
          selectedProducts.length > 0
            ? {
                street: customerAddress.street,
                city: customerAddress.city,
                state: customerAddress.state,
                zipCode: customerAddress.zipCode,
                country: customerAddress.country,
              }
            : null,
        products: selectedProducts.map((p) => ({
          productId: p.productId,
          quantity: p.quantity,
          variantKey: p.variantKey || null,
          variantLabel: p.variantLabel || "",
          variantType: p.variantType || "",
          variantValue: p.variantValue || "",
          variantAttributes: p.variantAttributes || [],
        })),
        tipPercentage: tipIsCustom ? 0 : tipPercentage,
        tipAmount: totals.tipAmount,
        // Optional note for the professional (validated server-side)
        customerNotes: customerNote.trim(),
        groupSize,
        isGroupBooking: groupSize > 1,
        groupMembers: groupSize > 1 ? groupMembers : [],
        packagePurchaseId: selectedPackage?._id || null,
        // NOT SENT WHEN THE PARTY IS NO LONGER COVERED (22D). A package is chosen before the party is known —
        // a member added afterwards can take the booking outside it — and sending the selection anyway asks the
        // server for a refusal, while sending it silently would charge by card behind the word SELECTED. The
        // summary shows the price either way, because the credit is only applied when the package really covers
        // the booking.
        //
        // AND THE PAYMENT METHOD FOLLOWS IT (2026-10-11). This was `selectedPackage ? "package" : "stripe"`,
        // computed from the SELECTION rather than from whether the package can be used — so a booking that had
        // given up on the package and was about to be CHARGED BY CARD was recorded as `paymentMethod: "package"`.
        usePackageCredit: usablePackage,
        // A SPLIT BOOKING RECORDS ITSELF (split payment, 2026-10-27). The server sets `package_and_card` for
        // these anyway — it is the one that knows the covered lines — but sending the honest value means a
        // request that somehow reached the package branch without a card half cannot be mislabelled here either.
        paymentMethod: usablePackage
          ? totals.cardServiceTotal > 0 ||
            totals.productsTotal > 0 ||
            totals.tipAmount > 0
            ? "package_and_card"
            : "package"
          : "stripe",
        promoCode: appliedPromo ? appliedPromo.code : null,
        promoDiscount: appliedPromo ? appliedPromo.discountAmount : 0,
        lookId: lookId || null,
        isFreelancerBooking,
        isTestBooking: isPreviewMode, // Mark as test booking if in preview mode
        serviceBundle: bundleServices,
        serviceBundleCount: bundleServices.length,
        serviceBundleDuration:
          engineDuration || bundleDuration || normalizeServiceDuration(service),
        serviceBundlePrice:
          enginePrice != null
            ? enginePrice
            : bundleServicePrice || normalizeServicePrice(service),
        // F1/F2: per-unit quantity / hours / chosen option (omitted for fixed)
        selection: selectorEnabled ? serviceSelection : undefined,
        waitlistMismatchAcknowledged:
          waitlistCompatibility?.hasPendingWaitlist &&
          waitlistCompatibility.compatible === false &&
          waitlistMismatchAcknowledged,
        // C1: audit - when the customer accepted the cancellation/refund policy
        policyAcceptedAt: policyAccepted ? new Date().toISOString() : null,
      };

      let endpoint = `${process.env.REACT_APP_API_BASE_URL}/api/bookings/customer`;
      let bookingData = baseBookingData;

      // Add recurring pattern if enabled
      if (isRecurring) {
        endpoint = `${process.env.REACT_APP_API_BASE_URL}/api/bookings/recurring`;
        bookingData = {
          ...baseBookingData,
          recurringPattern: {
            frequency: recurringFrequency,
            interval: recurringInterval,
            occurrences:
              recurringEndType === "occurrences" ? recurringOccurrences : null,
            endDate: recurringEndType === "date" ? recurringEndDate : null,
          },
          servicePriceAtBooking: service?.price || 0,
          duration: bundleDuration || service?.duration || 60,
        };
      }
      const headers = { "Content-Type": "application/json" };
      // CRITICAL FIX: Always send Authorization header if user is logged in (not just for recurring)
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      } else if (selectedPackage) {
        // Phase 18 / D57: a GUEST redeeming a package has no session — the scoped token issued after
        // the challenge IS the proof of ownership, and the server re-checks it before spending a session.
        // Read through the helper so an expired token is not presented: sending one that cannot work
        // earns a 401 and, worse, looks like a failed redemption in the logs when it never was.
        const guestToken = readGuestPackageToken();
        if (guestToken) {
          headers["Authorization"] = `Bearer ${guestToken}`;
        }
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(bookingData),
      });

      const data = await response.json();
      if (!response.ok) {
        // The server is the authority on the slot. If it says the time went while this
        // customer was completing the form, send them straight back to the picker with a
        // refreshed grid rather than leaving them on a stale one - and nothing has been
        // charged, because Stripe is only reached after this check passes.
        const slotUnavailable =
          data.error === "time_slot_unavailable" ||
          data.error === "slot_unavailable" ||
          // The pre-payment availability re-check on the booking endpoints. It used to
          // fall through to a bare red error, which told the customer nothing about
          // what had happened or what to do next.
          data.error === "time_not_available";

        if (slotUnavailable) {
          clearDirectBookingHold({
            shopId,
            staffId,
            serviceId,
            appointmentDate: selectedDate,
            startTime: selectedSlot?.time24,
          });
          setSelectedSlot(null);
          setSlotInvalidatedNotice(
            buildSlotUnavailableNotice(data.code, data.message),
          );
          setError("");
          await fetchAvailableSlots();
          scrollToSection(dateTimeSectionRef);
          setPaymentProcessing(false);
          return;
        }

        // 22G: a package that cannot pay for this booking is answered by the ordinary refusals below and the
        // page's own cards — there is no longer a package-specific branch here, because there is no longer a
        // package-specific BLOCK. The redeemable offer is rendered from the purchase itself, whether or not this
        // booking used it, so nothing about a refusal needs to point at it.

        // Phase 18 / D57: the guest's 10-minute token expired mid-booking. Throw them back into the
        // challenge WITHOUT losing the booking they built — every selection is component state and is
        // untouched — and keep the package selected, so re-verifying is all that is left to do.
        if (
          data.error === "invalid_guest_token" ||
          data.error === "guest_verification_required"
        ) {
          sessionStorage.removeItem(GUEST_PACKAGE_TOKEN_KEY);
          setShowGuestPackageAccess(true);
          setGuestChallenge((prev) => ({
            ...prev,
            error:
              "Your verification expired. Verify again, then confirm the booking.",
          }));
          setPaymentProcessing(false);
          return;
        }

        throw new Error(
          data.message || data.details || "Failed to create booking",
        );
      }

      // For recurring bookings, show success and redirect
      if (isRecurring) {
        alert(
          `Successfully created ${data.bookings?.length || 0} recurring bookings!`,
        );
        navigate("/dashboard/customer/appointments");
      } else if (data.isTestBooking) {
        // TEST BOOKING: No payment needed, redirect to confirmation page
        clearBookingDraft(draftScope);
        navigate(
          `/booking-confirmation?booking_id=${data.booking.id}&test=true`,
          {
            state: {
              testBooking: true,
              booking: data.booking,
              message: data.message,
            },
          },
        );
      } else {
        // Redirect to Stripe checkout for single bookings
        if (data.stripeCheckoutUrl) {
          window.location.href = data.stripeCheckoutUrl;
        } else if (data.booking) {
          // NO STRIPE STEP: the booking is already complete — a session was spent and the appointment
          // is confirmed. The server returns no checkout URL for that, and this branch used to do
          // NOTHING, so the button sat on "Processing..." forever with a confirmed appointment behind
          // it. That is the user-reported "the appointment doesn't get booked".
          //
          // AND THE PROOF IS DROPPED HERE (2026-10-11, shared-computer report). The guest token has just
          // spent a session; leaving it in `sessionStorage` is what let the next person at the same machine
          // see the package and spend another one with no verification. The server refuses a spent proof
          // regardless — this stops it being presented at all, and stops the package being offered again to
          // whoever is sitting here now.
          sessionStorage.removeItem(GUEST_PACKAGE_TOKEN_KEY);
          // ── THE SAME CONFIRMATION PAGE A CARD BOOKING GETS (user, 2026-10-11/12) ────────────────
          //
          // *"do the confirmation page matching the card page as well"*, asked twice. This used to set state
          // and render an INLINE PANEL on this screen instead — a different page, with different facts, that
          // no URL could reach and that vanished on reload. It now goes where a card booking goes.
          //
          // The server returns the booking DOCUMENT (`confirmationBooking`) because a guest cannot fetch one
          // (the endpoint needs a session); `packageMeta` carries the three things only a package knows — how
          // many sessions the claim spent, how many are left, and who was served.
          //
          // `returnTo` is this screen, so "Book another appointment" comes back to a FRESH booking page rather
          // than to a restored one — the restored page is what showed pre-booking package figures on
          // 2026-10-11 ("still has 1 kid's haircut left, though I just used it now").
          // The booking exists now: the draft must not come back to life on a later visit.
          clearBookingDraft(draftScope);
          navigate("/booking-confirmation", {
            state: {
              packageBooking: data.confirmationBooking || null,
              packageMeta: {
                sessionsUsed: data.booking?.sessionsUsed ?? 1,
                sessionsRemaining: data.sessionsRemaining,
                people: data.booking?.people,
                partyEndTime: data.booking?.partyEndTime,
                message: data.message,
              },
              returnTo: `${window.location.pathname}${window.location.search}`,
            },
          });
          // Kept even though the page is left immediately: this screen is what `returnTo` comes back to, and a
          // restore (not a reload) is how stale package figures were ever seen on it.
          fetchCustomerPackages();
          setPaymentProcessing(false);
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          // Never leave the button stuck: if the server returned neither a checkout URL nor a
          // booking, say so rather than spinning.
          setPaymentProcessing(false);
          setError(
            data.message ||
              "The booking was submitted. Please check your email for confirmation.",
          );
        }
      }
    } catch (err) {
      setError(err.message);
      setPaymentProcessing(false);
    }
  };

  useEffect(() => {
    const customerInfoReady = Boolean(
      customerInfo.firstName?.trim() &&
        customerInfo.lastName?.trim() &&
        customerInfo.email?.trim() &&
        customerInfo.phone?.trim() &&
        (isAuthenticated || smsConsent),
    );
    const billingReady =
      !isBillingAddressRequired(selectedProducts) ||
      Boolean(
        customerAddress.street?.trim() &&
          customerAddress.city?.trim() &&
          customerAddress.state?.trim() &&
          customerAddress.zipCode?.trim() &&
          customerAddress.country?.trim(),
      );

    if (
      expandedSection !== "summary" ||
      !customerInfoReady ||
      !selectedSlot ||
      !billingReady
    ) {
      setWaitlistCompatibility(null);
      setWaitlistMismatchAcknowledged(false);
      return;
    }

    const normalizeDuration = (svc) =>
      Number(svc?.durationMinutes || svc?.duration || 0) || 0;
    const normalizePrice = (svc) => Number(svc?.price || 0) || 0;
    const selectedBundle = [];
    if (service) {
      selectedBundle.push(service);
    }
    for (const bundleService of serviceBundle) {
      if (!bundleService?._id || String(bundleService._id) === String(service?._id)) {
        continue;
      }
      if (
        selectedBundle.some(
          (existing) => String(existing?._id) === String(bundleService._id),
        )
      ) {
        continue;
      }
      selectedBundle.push(bundleService);
    }

    const controller = new AbortController();
    const checkWaitlistCompatibility = async () => {
      setWaitlistCompatibilityLoading(true);
      try {
        const compatibilityBundle = selectedBundle.map((svc) => ({
          serviceId: svc._id,
          name: svc.name,
          durationMinutes: normalizeDuration(svc),
          price: normalizePrice(svc),
        }));
        const token = localStorage.getItem("ogestyle_token");
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/waitlist/checkout-compatibility`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal: controller.signal,
            body: JSON.stringify({
              shopId: isFreelancerBooking
                ? freelancerBusiness?._id || shop?._id
                : shop?._id,
              staffId,
              serviceId,
              serviceBundle: compatibilityBundle,
              serviceBundleDuration:
                compatibilityBundle.reduce(
                  (sum, svc) => sum + (Number(svc.durationMinutes) || 0),
                  0,
                ) || normalizeDuration(service),
              customerEmail: customerInfo.email,
            }),
          },
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || "Waitlist compatibility check failed");
        }
        setWaitlistCompatibility(data);
        setWaitlistMismatchAcknowledged(false);
      } catch (err) {
        if (err.name !== "AbortError") {
          setWaitlistCompatibility(null);
          setWaitlistMismatchAcknowledged(false);
        }
      } finally {
        if (!controller.signal.aborted) {
          setWaitlistCompatibilityLoading(false);
        }
      }
    };

    checkWaitlistCompatibility();
    return () => controller.abort();
  }, [
    expandedSection,
    selectedSlot,
    selectedDate,
    shop?._id,
    freelancerBusiness?._id,
    staffId,
    serviceId,
    serviceBundle,
    service,
    customerInfo.firstName,
    customerInfo.lastName,
    customerInfo.email,
    customerInfo.phone,
    customerAddress.street,
    customerAddress.city,
    customerAddress.state,
    customerAddress.zipCode,
    customerAddress.country,
    isAuthenticated,
    smsConsent,
    selectedProducts,
    isFreelancerBooking,
  ]);

  // ── The ids that identify "the service(s) being booked" for package matching ──────────────────
  //
  // DECLARED ABOVE THE EARLY RETURNS, and that placement is the fix for a real defect. This used to sit
  // ~350 lines below, after `if (loading) return …`. `fetchCustomerPackages` reads it from an effect that
  // fires while the page is still loading, and a `const` declared after an early return is still in its
  // TEMPORAL DEAD ZONE for the render that returns early — so the read threw `Cannot access
  // 'bookedServiceIds' before initialization`, the surrounding `catch` swallowed it, and the customer's
  // OWN packages came back as an empty list. The packages were there, paid for, and invisible: nothing
  // was selectable, so the appointment could never be booked with one.
  //
  // A package stores the SHOP TEMPLATE's service id, but a booking is made against a STAFF service —
  // a per-professional copy that carries `templateId` pointing at that template. They are different
  // documents, so comparing the ids directly NEVER matched: the package said `…ece7a` (the template)
  // while the booking page said `…ced9` (that professional's copy of it), and the package was
  // invisible. Adding each booked service's `templateId` makes the two sides comparable, and the
  // raw ids stay in the set so a package built from a staff service still matches too.
  const bookedServiceIds = new Set(
    [serviceId, service?._id, ...(serviceBundle || []).map((svc) => svc?._id)]
      .filter(Boolean)
      .map(String),
  );
  for (const booked of [service, ...(serviceBundle || [])]) {
    if (booked?.templateId) {
      bookedServiceIds.add(String(booked.templateId));
    }
  }

  const normalizeServiceDuration = (svc) =>
    Number(svc?.durationMinutes || svc?.duration || 0) || 0;
  const normalizeServicePrice = (svc) => Number(svc?.price || 0) || 0;

  const getBundleServices = () => {
    const orderedServices = [];
    if (service) {
      orderedServices.push(service);
    }
    for (const bundleService of serviceBundle) {
      if (!bundleService?._id || bundleService._id === service?._id) {
        continue;
      }
      if (
        orderedServices.some(
          (existing) => String(existing?._id) === String(bundleService._id),
        )
      ) {
        continue;
      }
      orderedServices.push(bundleService);
    }
    return orderedServices;
  };

  const getBundleDuration = () =>
    getBundleServices().reduce(
      (sum, svc) => sum + normalizeServiceDuration(svc),
      0,
    );
  const getBundlePrice = () =>
    getBundleServices().reduce((sum, svc) => sum + normalizeServicePrice(svc), 0);

  const bundleDurationMinutes = getBundleDuration();
  const bundlePrice = getBundlePrice();
  const bundleServices = getBundleServices();

  // The engine owns price + length as soon as ANY line is selection-driven; a
  // fixed-only bundle keeps the original catalog sums (unchanged behaviour).
  const anyLineSelectionDriven = bundleServices.some(isSelectionDrivenService);
  const effectiveBundleDurationMinutes = anyLineSelectionDriven
    ? resolvedTotalMinutes
    : bundleDurationMinutes;
  const effectiveServicePrice = anyLineSelectionDriven
    ? resolvedBundleLines.totalPrice || bundlePrice
    : bundlePrice;

  const formatTime12 = (time24) => {
    const [hours, minutes] = String(time24 || "").split(":").map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return String(time24 || "");
    }

    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    return `${h12}:${String(minutes).padStart(2, "0")} ${ampm}`;
  };

  const formatDisplayDate = (dateKey) => {
    const parsedDate = new Date(`${dateKey}T12:00:00`);
    if (Number.isNaN(parsedDate.getTime())) {
      return dateKey;
    }

    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(parsedDate);
  };

  if (loading) {
    return (
      <>
        <div className="booking-loading">Loading booking information...</div>
      </>
    );
  }

  if (error && !shop) {
    return (
      <>
        <div className="booking-error">{error}</div>
      </>
    );
  }

  const customerInfoComplete = Boolean(
    customerInfo.firstName?.trim() &&
      customerInfo.lastName?.trim() &&
      customerInfo.email?.trim() &&
      customerInfo.phone?.trim() &&
      (isAuthenticated || smsConsent),
  );
  const scheduleComplete = Boolean(selectedSlot);
  const billingAddressComplete =
    !isBillingAddressRequired(selectedProducts) ||
    Boolean(
      customerAddress.street?.trim() &&
        customerAddress.city?.trim() &&
        customerAddress.state?.trim() &&
        customerAddress.zipCode?.trim() &&
        customerAddress.country?.trim(),
    );
  const summaryReady =
    customerInfoComplete && scheduleComplete && billingAddressComplete;
  const billingAddressRequired = isBillingAddressRequired(selectedProducts);
  const waitlistOnlyPath = false;
  const waitlistContactReady = Boolean(
    `${customerInfo.firstName || ""} ${customerInfo.lastName || ""}`.trim() &&
      customerInfo.email?.trim(),
  );
  // F2: is a choice-based service (per-unit / hourly / options) still waiting for
  // the customer's choice? Computed from the selection primitives rather than the
  // display derivations below, because the submit blockers are built first and a
  // const referenced before its declaration would throw.
  const summaryBlockers = buildSummaryBlockers({
    customerInfoComplete,
    scheduleComplete,
    billingAddressRequired,
    billingAddressComplete,
    customerInfoSectionRef,
    dateTimeSectionRef,
    productsSectionRef,
  });
  const summarySoftWarnings = buildSummarySoftWarnings({
    selectedProducts,
    waitlistOnlyPath,
  });
  const showFinalCheckoutStep = summaryReady && expandedSection === "summary";
  const waitlistMismatchRequiresAcknowledgment = Boolean(
    waitlistCompatibility?.hasPendingWaitlist &&
      waitlistCompatibility.compatible === false,
  );
  const submitBlockers = [
    ...summaryBlockers,
    // F2: a choice-based service cannot be priced until the customer chooses.
    primarySelectionPending
      ? {
          key: "pricing-selection",
          label: `Choose an option for ${selectionPendingLabel}`,
          ref: serviceCardRef,
        }
      : null,
    showFinalCheckoutStep && !policyAccepted
      ? {
          key: "policy",
          label: "Acknowledge cancellation policy",
          ref: policySectionRef,
          requiresPolicyFocus: true,
        }
      : null,
    /**
     * The uncovered-service acknowledgement is deliberately NOT a blocker entry here: `submitBlockers` is built
     * above the totals, and the flag it would need (`cardServiceTotal`) does not exist yet at this point. It would
     * be a temporal-dead-zone read — the exact mistake this file has shipped three times. The tick sits directly
     * above the Pay button and holds it disabled, which is where the customer is looking anyway.
     */
    showFinalCheckoutStep &&
    waitlistMismatchRequiresAcknowledgment &&
    !waitlistMismatchAcknowledged
      ? {
          key: "waitlist-mismatch",
          label: "Acknowledge waitlist cancellation",
          ref: waitlistMismatchSectionRef,
        }
      : null,
  ].filter(Boolean);
  const hasProductsStep = Boolean(
    !isPreviewMode && recommendedProducts && recommendedProducts.length > 0,
  );
  const sectionSequence = [
    "customerInfo",
    "dateTime",
    "group",
    ...(hasProductsStep ? ["products"] : []),
    "summary",
  ];
  const groupMembersComplete =
    groupMembers.length > 0 &&
    groupMembers.every(
      (m, index) =>
        m.name.trim() &&
        m.serviceId &&
        m.preferredTime &&
        // P5: a member whose line still needs a choice has no price and no
        // length, so they cannot be booked yet - the same rule the primary and
        // add-on lines follow.
        resolvedMemberLines[index] &&
        resolvedMemberLines[index].requiresSelection !== true,
    );
  const groupConfirmationPending =
    showGroupForm || (groupMembers.length > 0 && groupSize === 1);
  const groupCanProceed = !groupConfirmationPending;
  const advanceToNextSection = (sectionKey) => {
    const currentIndex = sectionSequence.indexOf(sectionKey);
    if (currentIndex === -1 || currentIndex >= sectionSequence.length - 1) {
      return;
    }
    setExpandedSection(sectionSequence[currentIndex + 1]);
  };
  const getPreviousSectionKey = (sectionKey) => {
    const currentIndex = sectionSequence.indexOf(sectionKey);
    if (currentIndex <= 0) {
      return "";
    }
    return sectionSequence[currentIndex - 1];
  };
  const goToPreviousSection = (sectionKey) => {
    const previousKey = getPreviousSectionKey(sectionKey);
    if (previousKey) {
      setExpandedSection(previousKey);
    }
  };
  const nextButtonStyle = {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#1f2937",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  };
  const bookingCurrency = isFreelancerBooking
    ? staff?.currency || freelancerBusiness?.currency || "USD"
    : shop?.currency || "USD";



  const getProductVariantSummary = (product = {}) => {
    const attributes = Array.isArray(product.variantAttributes)
      ? product.variantAttributes
      : [];

    if (attributes.length > 0) {
      return attributes
        .map((attribute) => {
          const type = String(
            attribute?.dimensionName ||
              attribute?.dimensionKey ||
              attribute?.name ||
              attribute?.type ||
              "Variant",
          ).trim();
          const value = String(attribute?.label || attribute?.value || "").trim();
          return value ? `${type}: ${value}` : type;
        })
        .filter(Boolean)
        .join(" • ");
    }

    const fallbackType = String(
      product.variantType || product.variantLabel || product.variantName || "",
    ).trim();
    const fallbackValue = String(product.variantValue || "").trim();

    if (fallbackType && fallbackValue) {
      return `${fallbackType}: ${fallbackValue}`;
    }

    return fallbackType;
  };



  const getWaitlistDateLabels = (waitlistEntry) => {
    const dates = Array.isArray(waitlistEntry?.preferredDates)
      ? waitlistEntry.preferredDates
      : [];

    return dates
      .map((dateValue) => {
        const normalizedDate = String(dateValue || "").slice(0, 10);
        return normalizedDate ? formatDisplayDate(normalizedDate) : "";
      })
      .filter(Boolean);
  };

  const getWaitlistServiceLabels = (waitlistEntry) => {
    const bundle = Array.isArray(waitlistEntry?.serviceBundle)
      ? waitlistEntry.serviceBundle
      : [];
    const names = bundle
      .map((svc) => String(svc?.name || "").trim())
      .filter(Boolean);

    return names.length > 0 ? names : [service?.name || "Selected service"];
  };




  const bundleServiceCount = getBundleServices().length;

  /**
   * The shop's packages that relate to THIS booking.
   *
   * Derived at RENDER time rather than filtered when the list is fetched, because of how a booking is
   * actually made: the service can be chosen on this page (professional first) rather than passed in
   * the URL (service first), and in the first case the fetch used to run before anything was chosen —
   * or not at all. Filtering here means the list follows the current selection with no refetch.
   *
   * Relevance is ANY of the services in the booking, not all of them: a bundle that contains the
   * booked service can genuinely pay for it. With nothing chosen the list is EMPTY, so the section
   * hides itself rather than showing an unrelated catalogue.
   *
   * Deliverability is deliberately NOT checked: the endpoint applies
   * `packageSellabilityFilter({ requireDeliverable: true })`, so every package it returns already has
   * a subscribed professional. The per-professional check that used to exist here asked whether the
   * SELECTED professional was a subscriber — which this page often cannot answer — and emptied the
   * list for guests and signed-in customers alike.
   *
   * `bookedServiceIds` (the booking side of the comparison) is declared ABOVE the early returns — see
   * the note there. It must not be moved back down beside this filter.
   */
  /**
   * DOES THE PROFESSIONAL BEING BOOKED ACTUALLY OFFER THIS PACKAGE? (reported by the user 2026-10-11)
   *
   * *"a customer/guest selects a professional that did not subscribe to the package, the system is still
   * allowing them to book with a package. This is absolutely wrong in all dimensions… even if the professional
   * is the owner operator, as long as the R3 role has not subscribed, the customer/guest is not supposed to see
   * those package."*
   *
   * Subscribing is the shop's decision about WHO may deliver a package, and it is what this page already
   * showed: the for-sale list comes from `packageSellabilityFilter({ requireDeliverable: true })`, which means
   * "at least one professional subscribed" — never "the one you picked did". The owner-operator is not an
   * exception; `subscribedStaffIds` is a list a human put them on.
   *
   * Returns **true / false / null**, and `null` means CANNOT JUDGE — a deleted template, or a payload from
   * before this field existed. The caller must then OFFER it and let the server decide, because a client check
   * must never be stricter than the server: greying out a package the customer really holds is the same class
   * of defect as offering one that will be refused.
   *
   * The two id spaces are both accepted for the same reason the server accepts both: `subscribedStaffIds` holds
   * Staff ids, while the booking's professional has been seen as either the Staff id or the account's user id.
   *
   * DECLARED HERE, ABOVE ITS CALLERS, AND THAT IS NOT STYLE. `relevantAvailablePackages` and
   * `ownedPackagesHere` are computed in this same function body during render, so a `const` arrow declared
   * BELOW them is in its TEMPORAL DEAD ZONE at the moment they run — `ReferenceError: Cannot access
   * 'professionalOffersPackage' before initialization`, which is what happened. The same trap is why
   * `bookedServiceIds` sits above the early returns.
   */
  const professionalOffersPackage = (pkg) => {
    const subscribers =
      pkg?.packageId && typeof pkg.packageId === "object"
        ? pkg.packageId.subscribedStaffIds
        : pkg?.subscribedStaffIds;

    // Nothing to judge by: defer.
    if (!Array.isArray(subscribers) || subscribers.length === 0) return null;

    const candidates = [
      staff?._id,
      staff?.userId?._id,
      staff?.userId,
      staffId,
    ]
      .filter((id) => id && typeof id !== "object")
      .map(String);

    // No professional chosen yet: the list must still show, exactly as it does today.
    if (candidates.length === 0) return null;

    return subscribers.some((id) => candidates.includes(String(id)));
  };

  /**
   * 22D — WHAT THIS BOOKING WOULD DRAW FROM ONE PACKAGE, PER PERSON (§4.2 Option C, §6.6).
   *
   * THE POINT OF OPTION C: the resolution is a **filter on what is offered**, never a decision about payment.
   * A customer is never told "you must use your package" — they are shown only what the package CAN pay for,
   * and the card stays selectable at the summary either way. So this returns a reason, and the caller hides or
   * greys the package out; it never blocks the booking.
   *
   * WHY IT IS PER PERSON AND NOT A TOTAL. "3 sessions remaining" does not say whether those three are the
   * services in THIS booking — a father-and-son booking against a package holding three kids' cuts and no
   * adult cut has plenty of sessions and cannot pay for one of the two people. The server decides the same
   * question per attendee (`evaluatePackageParty`), and this mirrors it so the page cannot offer what the
   * server refuses.
   *
   * A member whose service failed to resolve is DROPPED here, exactly as the server drops it, so both sides
   * count the same people.
   *
   * DECLARED ABOVE `relevantAvailablePackages` AND `ownedPackagesHere`, which call it during render — a `const`
   * arrow used by anything above it is in its temporal dead zone at that moment (ReferenceError at runtime,
   * with a clean lint). This file has been bitten by that twice; the ordering is the whole of it.
   */
  const packagePartyCheck = (pkg) => {
    /**
     * 22G — AND IF RESTORING THE LOST SESSIONS WOULD FIX THIS BOOKING, SAY SO (user, 2026-10-16).
     *
     * The user: *"it simply activates pay with stripe (card), instead it should give them an option to either
     * redeem the package which is cost-saving or pay with card"*. So the card answer stays — it is honest — and
     * the COST-SAVING route is named beside it whenever the held sessions cover everything being booked here.
     * When they do not, nothing is added: promising it would help with an appointment it cannot pay for is the
     * same mistake in the other direction.
     */
    const restoreHint = (() => {
      const offer = redeemablePackageOffers.find(
        (entry) => String(entry.key) === String(pkg?._id || pkg?.id),
      );
      if (!offer || !(Number(offer.units) > 0) || !offer.coversThisBooking) return "";
      return ` Or restore the ${offer.units} lost session${
        offer.units === 1 ? "" : "s"
      } from this package below — $${Number(offer.amount).toFixed(2)} — and use it for this appointment.`;
    })();
    const splitNote =
      "Pay the rest of this appointment by card, or book each person separately." + restoreHint;

    /**
     * ── WHAT THE PACKAGE CAN PAY FOR, AND WHAT GOES TO THE CARD (split payment, 2026-10-27) ──────────
     *
     * The check used to be ALL OR NOTHING for a party: one attendee the package did not include, or one line
     * without a unit left, and the whole package was refused as "Not eligible" — leaving the customer with
     * nothing but "pay for everything by card". A package can now pay for the people it covers and the card for
     * the rest, so this answers PER PERSON:
     *
     *   • the PRIMARY must be covered — without that the package is not being used at all, and the whole booking
     *     is a card booking;
     *   • a member the package does not include, or cannot fit a unit for, is simply PAID BY CARD.
     *
     * The answers are returned rather than only reduced to a boolean, because the summary has to know which
     * people the package took and which the card did.
     */
    const people = [
      {
        isPrimary: true,
        label: null,
        serviceId: serviceId || service?._id || null,
      },
      ...(groupSize > 1 ? groupMembers || [] : []).map((member, index) => ({
        isPrimary: false,
        label: member?.name || `Guest ${index + 1}`,
        serviceId: member?.serviceId || null,
        memberIndex: index + 1,
      })),
    ].filter((person) => person.serviceId);

    const coveredMemberIndexes = [];
    const cardMemberIndexes = [];
    for (const person of people) {
      if (!person.isPrimary) cardMemberIndexes.push(person.memberIndex);
    }

    const quotaLines = (pkg?.quota?.applies && pkg.quota.lines) || [];

    // A BUNDLE KEEPS NO COUNTERS, and its unit is the VISIT — which is ONE person (§6.6, GC12). One visit
    // therefore covers ONE person: the primary. Anyone else in the party is paid by card.
    if (quotaLines.length === 0) {
      if (people.length === 1) {
        return {
          ok: true,
          message: "",
          coveredPrimary: true,
          coveredCount: 1,
          coveredMemberIndexes,
          cardMemberIndexes,
        };
      }
      return {
        ok: true,
        message: "",
        coveredPrimary: true,
        coveredCount: 1,
        coveredMemberIndexes: [],
        cardMemberIndexes: people
          .filter((person) => !person.isPrimary)
          .map((person) => person.memberIndex),
        // Nothing to warn about: the package is genuinely paying for one of the people.
        splitParty: true,
      };
    }

    // THE IDS THIS PERSON COULD BE COUNTED UNDER. A package stores the SHOP TEMPLATE's service id while a
    // booking names a staff copy pointing at it with `templateId` — the same two-sided match `bookedServiceIds`
    // makes for the booking as a whole, asked here once per PERSON.
    //
    // `null` MEANS "CANNOT JUDGE", and the caller must then DEFER rather than refuse. A package the customer
    // really holds must never be greyed out because an id could not be resolved on the client: the server
    // decides coverage, and this check exists only so the page does not OFFER what the server will refuse.
    const idsFor = (person) => {
      if (person.isPrimary) {
        // The primary's own document carries the template id, so no lookup is needed.
        return [person.serviceId, service?._id, service?.templateId]
          .filter(Boolean)
          .map(String);
      }

      const shopService = (shopServices || []).find(
        (candidate) => String(candidate?._id) === String(person.serviceId),
      );
      if (!shopService) return null;

      return [person.serviceId, shopService.templateId]
        .filter(Boolean)
        .map(String);
    };

    const unitsByLine = new Map();
    // THE PURCHASE'S TOTAL IS A SECOND LIMIT (split payment, 2026-10-27). A per-line counter can still have
    // room while the package as a whole has nothing left — a two-session package with one session front-loaded
    // on the father's line. Claiming more than `sessionsRemaining` would be refused by the server's atomic
    // guard, so the page must not promise it: the primary comes first and any member beyond the total pays by
    // card.
    const totalRemaining = Number(pkg?.sessionsRemaining) || 0;
    let coveredTotal = 0;
    for (const person of people) {
      const ids = idsFor(person);
      if (ids === null) {
        return {
          ok: true,
          message: "",
          coveredPrimary: true,
          coveredMemberIndexes,
          cardMemberIndexes: [],
        };
      }

      const line = quotaLines.find((candidate) =>
        ids.includes(String(candidate.serviceId)),
      );

      if (!line) {
        // "Not in the package" and "none left" are two different faults with different answers: the first never
        // gets better, the second does. For the PRIMARY either one means the package cannot be used at all; for
        // a member it means that person pays by card.
        if (person.isPrimary) {
          return {
            ok: false,
            message: `The service for this booking is not in this package. ${splitNote}`,
          };
        }
        continue;
      }

      const entry = unitsByLine.get(line) || { line, units: 0 };
      const remaining = Number(line.remaining) || 0;

      // EVERY line the party touches must have room for the units IT takes — two people on one line need two
      // from that line, not one each from two lines. The primary is served first, so a single remaining unit
      // always goes to the person the booking is for.
      const lineHasRoom = entry.units + 1 <= remaining;
      const purchaseHasRoom = coveredTotal + 1 <= totalRemaining;

      if (!lineHasRoom || !purchaseHasRoom) {
        if (person.isPrimary) {
          return {
            ok: false,
            message: `${
              remaining > 0
                ? `This package has ${remaining} ${
                    remaining === 1 ? "session" : "sessions"
                  } left for ${line.name || "a service in this booking"}.`
                : `This package has no sessions left for ${line.name || "a service in this booking"}.`
            } ${splitNote}`,
          };
        }
        continue;
      }

      entry.units += 1;
      unitsByLine.set(line, entry);
      coveredTotal += 1;
      if (!person.isPrimary) {
        coveredMemberIndexes.push(person.memberIndex);
        cardMemberIndexes.splice(cardMemberIndexes.indexOf(person.memberIndex), 1);
      }
    }

    return {
      ok: true,
      message: "",
      coveredPrimary: true,
      coveredCount: coveredTotal,
      coveredMemberIndexes,
      cardMemberIndexes,
    };
  };

  /**
   * THE SHOP'S PACKAGES THAT RELATE TO THIS BOOKING, AND THE PROFESSIONAL MUST OFFER THEM.
   *
   * Derived at RENDER time rather than filtered when the list was fetched, because of how a booking is made:
   * the service and the professional can both be chosen on this page. `null` from `professionalOffersPackage`
   * means cannot judge, and the package is OFFERED — the safe direction, because a client check must never be
   * stricter than the server.
   */
  /**
   * WHICH CATALOGUE PACKAGES THEY ALREADY HOLD, ACTIVE AND USABLE HERE — declared BEFORE the for-sale list that
   * reads it (a `const` read by anything above it is in its temporal dead zone; that mistake has shipped twice
   * in this file already).
   *
   * Only ACTIVE-and-usable purchases count: an expired or exhausted package must stay on sale, because buying
   * another is exactly what that customer needs.
   */
  const ownedPackageIds = new Set(
    (customerPackages || [])
      .map((pkg) => String(pkg?.packageId || ""))
      .filter(Boolean),
  );

  const relevantAvailablePackages =
    bookedServiceIds.size === 0
      ? []
      : availableShopPackages.filter(
          (pkg) =>
            professionalOffersPackage(pkg) !== false &&
            /**
             * ALREADY BOUGHT? DON'T OFFER IT AGAIN (2026-10-26).
             *
             * A customer can now buy a package from inside the booking and come straight back. The for-sale list
             * is a catalogue, so it kept offering the very package they had just paid for, on every step that
             * followed - reading as though the purchase had not happened. Anything they already hold an active
             * purchase of is dropped from the offers and shows up in "Your packages" instead, which is where the
             * summary's "Use my package" button reads from. A different package is still offered: the point is
             * not to stop selling, it is to stop selling them what they own.
             */
            !ownedPackageIds.has(String(pkg._id)) &&
            (pkg.services || []).some((line) => {
              // The route populates `services.serviceId`, so it arrives as a document; a bare id is
              // accepted too, because that is what the package document itself stores.
              const id = line?.serviceId?._id || line?.serviceId;
              return Boolean(id) && bookedServiceIds.has(String(id));
            }),
        );

  /**
   * ── ONCE A PACKAGE IS IN PLAY, STOP SELLING PACKAGES (agreement 2.8a, 2026-10-27) ───────────────────
   *
   * Two ways a package is "in play", and the customer's own words covered both:
   *
   *   • they BOUGHT one during this booking — the confirmation page marks the attempt, so every later step knows;
   *   • they APPLIED one they already owned — `selectedPackage`, set by "Use my package" on the summary.
   *
   * A purchase marker cannot see the second case, which is why both are here. Either one hides the offers: a
   * customer who has already covered this appointment must not be invited to buy something for it, on the summary
   * or on any step after.
   *
   * Other packages stay visible in "Your packages" — this governs the FOR-SALE list only.
   */
  const packageOffersClosed = packageBoughtDuringFlow || Boolean(selectedPackage);
  const offerablePackages = packageOffersClosed ? [] : relevantAvailablePackages;

  /**
   * THE PROFESSIONAL, NAMED, DECLARED BEFORE THE LISTS THAT NAME IT.
   *
   * It is needed by the reason sentences below, which are built inside the loop — and a `const` read by
   * anything declared ABOVE it is in its temporal dead zone. That exact mistake (a helper called by a list
   * declared above it) has already shipped twice in this file today, so the order is stated here rather than
   * discovered in the console.
   */
  const professionalDisplayName =
    [staff?.firstName, staff?.lastName].filter(Boolean).join(" ").trim() ||
    staff?.businessName ||
    "this professional";

  /**
   * THE CUSTOMER'S OWN PACKAGES HERE, AND WHAT IS LEFT OUT OF THE USABLE LIST.
   *
   * TWO REASONS, DELIBERATELY SEPARATE: the professional does not offer it (choose another professional), or
   * the package has nothing left for this service (book something else, or pay by card). They have different
   * answers, so they get different sentences.
   */
  const ownedPackagesHere = [];;
  /**
   * OWNED AND STILL ACTIVE, BUT NOT USABLE FOR THIS BOOKING — SHOWN ANYWAY, GREYED, WITH ITS REASON.
   *
   * The rule, as the user put it (2026-10-11): *"as long as a ticket is still active, even though it may not
   * cover the booking in focus it should at least show it but might say not eligible or something"* — and
   * *"any package that's used up should be hidden completely when booking"*.
   *
   * Both halves matter and they are not the same rule. A USED-UP package is gone: nothing to spend, nothing to
   * decide, and leaving it on the page is the clutter this was first written to remove. An ACTIVE package that
   * merely cannot cover THIS booking is still something the customer holds and paid for, and hiding it made
   * them read it as lost: *"the Mother & Daughter's Day package is not showing as parts of the package the
   * customer/guest owns even though it can't pay for the booking. At least let it show among the packages
   * owned."* So: unselectable, carrying its reason, and nothing to press.
   */
  const ownedPackagesBlocked = [];
  let ownedPackagesWithheld = 0;
  let ownedPackagesUnusable = 0;
  let ownedPackageUnusableReason = "";
  for (const pkg of customerPackages) {
    const entry = {
      key: String(pkg._id),
      name: pkg.packageSnapshot?.name || "Package",
      sessionsRemaining: pkg.sessionsRemaining,
      validUntil: pkg.validUntil,
      quota: pkg.quota,
      // 22G — the sessions this package is HOLDING: lost to a late cancellation and still buyable back. Carried
      // so the card can name them and offer the payment, from the same payload the package itself arrives on.
      redeemTotal: Number(pkg.redeemTotal) || 0,
      redeemUnits: Number(pkg.redeemUnits) || 0,
    };
    if (professionalOffersPackage(pkg) === false) {
      ownedPackagesWithheld += 1;
      ownedPackagesBlocked.push({
        ...entry,
        reason: `${professionalDisplayName} does not offer this package, and a package can only be used with a professional who subscribed to it.`,
      });
      continue;
    }
    const check = packagePartyCheck(pkg);
    if (!check.ok) {
      ownedPackagesUnusable += 1;
      ownedPackageUnusableReason = ownedPackageUnusableReason || check.message;
      ownedPackagesBlocked.push({ ...entry, reason: check.message });
      continue;
    }
    ownedPackagesHere.push(pkg);
  }

  // AND THE ONES THE FETCH ITSELF SET ASIDE — expired, finished, or a status that is no longer active.
  // Same card, same shape, one render path. Nothing a customer owns is invisible.
  ownedPackagesBlocked.push(...ownedUnusablePackages);

  /**
   * IS THERE A PACKAGE SECTION TO JUMP TO? (plan item 6, 2026-10-18) — one expression, used by the section
   * itself and by the anchor on every step, so an anchor can never point at a section that is not rendered.
   */
  const hasPackageSection =
    ownedPackagesHere.length > 0 ||
    ownedPackagesBlocked.length > 0 ||
    offerablePackages.length > 0;

  /*
   * WHETHER THE SECTION HAS ANYTHING TO SHOW **ON THIS STEP** (reported 2026-10-19: *"what's the empty tile on
   * summary page for"*).
   *
   * The offers are the only thing the section can contain on the summary — the owned cards are listed there too,
   * but the for-sale group is deliberately held back on that step, because the payment step directly beside the
   * Pay button already carries it. So a customer who owned nothing and could only buy was left with a panel, a
   * border and a background and not one character inside it. The gate has to be what the section will RENDER,
   * not what exists in the data.
   */
  const forSaleShownInSection =
    expandedSection !== "summary" && offerablePackages.length > 0;
  const showPackageSection =
    ownedPackagesHere.length > 0 ||
    ownedPackagesBlocked.length > 0 ||
    forSaleShownInSection;

  const primaryBundleService = bundleServices[0] || service || null;

  // P1/P2 (F3): the primary's selector is available whenever its OWN pricing needs
  // a choice. A bundle or a group must NOT disable it - that was the bug where the
  // primary's units stopped affecting price/duration as soon as an add-on existed.
  const isChoiceBasedService = isSelectionDrivenService(primaryBundleService);
  const selectorEnabled = isChoiceBasedService;
  const serviceSelection = {
    quantity: serviceQuantity,
    hours: serviceHours,
    tierId: serviceTierId,
  };
  const resolvedPrimaryLine = primaryPricingLine;

  // The primary row/card is labelled with the PRIMARY service, so it must show
  // the primary's OWN price and duration - never the bundle totals. Add-ons are
  // itemised separately below, and the footer already prices the primary alone
  // via `primaryRowPrice`. Showing the bundle totals here double-counted the
  // add-ons and made the primary's duration look wrong.
  // `effectiveServicePrice` / `effectiveBundleDurationMinutes` stay the real
  // bundle totals - they still drive totals and the slot search.
  const primaryRowDuration =
    isChoiceBasedService && resolvedPrimaryLine
      ? resolvedPrimaryLine.totalMinutes
      : normalizeServiceDuration(primaryBundleService || service);
  const displayedPrimaryPrice =
    isChoiceBasedService && resolvedPrimaryLine
      ? resolvedPrimaryLine.totalPrice
      : normalizeServicePrice(primaryBundleService || service);
  const displayedPrimaryDuration = primaryRowDuration;
  // e.g. "Nail Art × 5", "Event Styling × 3", "Box Braids (Mid-back)"
  const primaryDisplayName =
    (resolvedPrimaryLine && resolvedPrimaryLine.label) ||
    primaryBundleService?.name ||
    "Service";
  // The summary lists the primary service and each add-on separately, so this
  // row needs the primary's OWN price - never the bundle total.
  const primaryRowPrice =
    isChoiceBasedService && resolvedPrimaryLine
      ? resolvedPrimaryLine.totalPrice
      : normalizeServicePrice(service);

  const handleServiceSelectionChange = (patch = {}) => {
    if (patch.quantity !== undefined) setServiceQuantity(patch.quantity);
    if (patch.hours !== undefined) setServiceHours(patch.hours);
    if (patch.tierId !== undefined) setServiceTierId(patch.tierId);
  };

  // P1/P2 (F3): each add-on keeps its own selection, keyed by service id, so
  // changing one line never disturbs another.
  const handleAddOnSelectionChange = (serviceKey, patch = {}) => {
    const key = String(serviceKey);
    setAddOnSelections((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...patch },
    }));
  };

  /**
   * P5: member times form a CHAIN - member #N starts when member #N-1 ends. So any
   * change to an earlier member's service or selection invalidates every LATER
   * member's chosen time. Clearing them is the honest response; leaving them would
   * silently keep a time that no longer follows the chain.
   */
  const clearDownstreamMemberTimes = (members, changedIndex) =>
    members.map((m, index) =>
      index > changedIndex
        ? { ...m, preferredTime: "", preferredTime12: "" }
        : m,
    );

  // P5: each group member keeps their own units / hours / option.
  const handleMemberSelectionChange = (memberId, patch = {}) => {
    setGroupMembers((prev) => {
      const changedIndex = prev.findIndex((m) => m.id === memberId);
      if (changedIndex === -1) return prev;

      const next = prev.map((m, index) =>
        index === changedIndex
          ? { ...m, selection: { ...(m.selection || {}), ...patch } }
          : m,
      );

      return clearDownstreamMemberTimes(next, changedIndex);
    });
  };

  const addonBundleServices = bundleServices.slice(1);
  const selectableBundleServices = [
    ...(service ? [service] : []),
    ...shopServices.filter((svc) => String(svc?._id) !== String(service?._id)),
  ];
  const hasAdditionalBundleServices =
    selectableBundleServices.filter(
      (svc) => String(svc?._id) !== String(service?._id),
    ).length > 0;

  const toggleBundleService = (svc) => {
    if (!svc?._id || String(svc._id) === String(service?._id)) {
      return;
    }

    setServiceBundle((prev) => {
      const alreadySelected = prev.some(
        (existing) => String(existing._id) === String(svc._id),
      );

      if (alreadySelected) {
        return prev.filter((existing) => String(existing._id) !== String(svc._id));
      }

      return [...prev, svc];
    });
  };
  const nextAvailableGroupedSlots = Object.values(
    nextAvailableSlots.reduce((groups, slot) => {
      const dateKey = String(slot?.date || "").trim();
      if (!dateKey) {
        return groups;
      }

      if (!groups[dateKey]) {
        groups[dateKey] = {
          dateKey,
          displayDate: slot.displayDate || formatDisplayDate(dateKey),
          slots: [],
        };
      }

      groups[dateKey].slots.push(slot);
      return groups;
    }, {}),
  );
  const renderSelectedServiceCard = () => {
    if (!service) {
      return null;
    }

    return (
      <div
        ref={serviceCardRef}
        style={{
          marginBottom: "14px",
          padding: isMobile ? "12px" : "14px 16px",
          borderRadius: "10px",
          border: "1px solid #d1d5db",
          background: "#f3f4f6",
        }}
      >
        <div style={{ fontSize: isMobile ? "14px" : "15px", fontWeight: "700", color: "#0f172a" }}>
          {service.name}
        </div>
        {service.description && (
          <div
            style={{
              marginTop: "4px",
              fontSize: isMobile ? "11px" : "13px",
              color: "#374151",
            }}
          >
            {service.description}
          </div>
        )}
        <div
          style={{
            marginTop: "6px",
            fontSize: isMobile ? "13px" : "14px",
            fontWeight: "600",
            color: "#1d4ed8",
          }}
        >
          {/* F2: hidden until the customer chooses, because the stored price is
              the lowest option and the stored duration the longest one. */}
          {!primarySelectionPending && (
            <>
              {/* P4: this line describes the WHOLE bundle - it is the one that
                  says "2 services" - so it must show the bundle totals. It used
                  to show the primary's own price and duration next to a
                  bundle-wide count, so a 5-unit primary + add-on read
                  "$45.00 • 30 minutes • 2 services" while the total was $55/45.
                  A group adds the members on top (they are listed right below),
                  using RESOLVED member lines only - never the catalog. */}
              {formatCurrency(
                (effectiveServicePrice || service.price || 0) +
                  resolvedMemberTotals.price,
                bookingCurrency,
              )}{" "}
              •{" "}
              {(effectiveBundleDurationMinutes ||
                normalizeServiceDuration(service)) +
                resolvedMemberTotals.minutes}{" "}
              minutes
              {bundleServiceCount + resolvedMemberTotals.count > 1
                ? ` • ${bundleServiceCount + resolvedMemberTotals.count} services`
                : ""}
            </>
          )}
        </div>

        {/* F1/F2: choice-based pricing (per-unit quantity, hours, or option) */}
        <ServicePricingSelector
          service={primaryBundleService}
          selection={serviceSelection}
          onChange={handleServiceSelectionChange}
          formatPrice={(value) => formatCurrency(value, bookingCurrency)}
          isMobile={isMobile}
          accentColor="#1d4ed8"
        />

        <div style={{ marginTop: "14px", display: "grid", gap: "10px" }}>
          {primaryBundleService && (
            <div
              key={`${primaryBundleService._id}-primary`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "10px 12px",
                borderRadius: "10px",
                background: "#ffffff",
                border: "1px solid #e5e7eb",
              }}
            >
              <div>
                <div style={{ fontWeight: "700", color: "#0f172a" }}>
                  Primary: {primaryDisplayName}
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                  {primarySelectionPending
                    ? "Choose an option above"
                    : `${displayedPrimaryDuration} min • ${formatCurrency(
                        displayedPrimaryPrice || 0,
                        bookingCurrency,
                      )}`}
                </div>
              </div>
            </div>
          )}

          {hasAdditionalBundleServices && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={handleManageAdditionalServices}
                style={{
                  border: "1px solid #c7d2fe",
                  background: "#eef2ff",
                  color: "#3730a3",
                  borderRadius: "999px",
                  padding: "4px 10px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {serviceBundle.length > 0 ? "Change service" : "Add additional service"}
              </button>
            </div>
          )}

          {addonBundleServices.length > 0 && (
            <div
              style={{
                fontSize: "12px",
                fontWeight: "700",
                color: "#0f172a",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Service bundle
            </div>
          )}
          {addonBundleServices.map((svc, index) => {
            const addOnSelection = addOnSelections[String(svc._id)] || {};
            const addOnLine = resolveServicePricing(svc, addOnSelection);
            const addOnNeedsChoice = Boolean(
              addOnLine && addOnLine.requiresSelection,
            );

            return (
              <div
                key={`${svc._id}-${index}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                  }}
                >
                  <div style={{ fontWeight: "700", color: "#0f172a" }}>
                    Add-on: {(addOnLine && addOnLine.label) || svc.name}
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    {addOnNeedsChoice
                      ? "Choose an option"
                      : `${(addOnLine && addOnLine.totalMinutes) ?? normalizeServiceDuration(svc)} min • ${formatCurrency(
                          (addOnLine && addOnLine.totalPrice) ??
                            normalizeServicePrice(svc),
                          bookingCurrency,
                        )}`}
                  </div>
                </div>
                {/* P2 (F3): each add-on owns its own selector - its units / hours /
                    option are independent of the primary's. */}
                <ServicePricingSelector
                  service={svc}
                  selection={addOnSelection}
                  onChange={(patch) =>
                    handleAddOnSelectionChange(svc._id, patch)
                  }
                  formatPrice={(value) => formatCurrency(value, bookingCurrency)}
                  isMobile={isMobile}
                  accentColor="#1d4ed8"
                />
              </div>
            );
          })}

          {showServiceSelector && hasAdditionalBundleServices && !selectedSlot && (
            <div
              style={{
                backgroundColor: "#f8fafc",
                padding: "14px 16px",
                borderRadius: "8px",
                marginTop: "8px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#334155",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Additional services
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#92400e",
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  marginBottom: "10px",
                }}
              >
                Selecting a different service may change the already selected time.
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(2, minmax(0, 1fr))"
                    : "repeat(4, minmax(0, 1fr))",
                  gap: "10px",
                }}
              >
                {selectableBundleServices
                  .filter((svc) => String(svc?._id) !== String(service?._id))
                  .map((svc) => {
                    const isChecked = serviceBundle.some(
                      (bundleSvc) => String(bundleSvc._id) === String(svc._id),
                    );

                    return (
                      <label
                        key={svc._id}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                          padding: "10px",
                          borderRadius: "8px",
                          border: isChecked ? "1px solid #2563eb" : "1px solid #cbd5e1",
                          background: isChecked ? "#eff6ff" : "#ffffff",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleBundleService(svc)}
                          style={{ marginTop: "2px" }}
                        />
                        <span style={{ fontSize: "12px", color: "#0f172a", fontWeight: "600" }}>
                          {svc.name}
                          <span style={{ display: "block", color: "#6b7280", fontWeight: "500" }}>
                            {normalizeServiceDuration(svc)} min • {formatCurrency(normalizeServicePrice(svc), bookingCurrency)}
                          </span>
                        </span>
                      </label>
                    );
                  })}
              </div>
            </div>
          )}

          {groupSize > 1 && groupMembers.length > 0 && (
            <>
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#0f172a",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  marginTop: "2px",
                }}
              >
                Group members
              </div>
              {groupMembers.map((member, index) => {
                const memberService = shopServices.find(
                  (s) => s._id === member.serviceId,
                );
                // P4: this row describes a CHOSEN member, so it must read the
                // member's resolved line rather than the catalog values (a
                // per-unit / hourly member service would show its single-unit
                // length and price). Falls back to the catalog only while a
                // required choice is outstanding - P5 adds the member selector.
                // P5: the member's resolved line (same array the slot chain uses).
                const listedMemberLine = resolvedMemberLines[index] || null;
                const listedMemberNeedsChoice = Boolean(
                  listedMemberLine && listedMemberLine.requiresSelection,
                );
                const listedMemberLabel =
                  listedMemberLine?.label || memberService?.name || "Service";
                const listedMemberMinutes = listedMemberNeedsChoice
                  ? normalizeServiceDuration(memberService)
                  : Number(listedMemberLine?.totalMinutes) ||
                    normalizeServiceDuration(memberService);
                const listedMemberPrice = listedMemberNeedsChoice
                  ? Number(memberService?.price) || 0
                  : Number(listedMemberLine?.totalPrice) || 0;

                return (
                  <div
                    key={`${member.email || member.name || "member"}-${index}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      padding: "10px 12px",
                      borderRadius: "10px",
                      background: "#ffffff",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "700", color: "#0f172a" }}>
                        {listedMemberLabel}
                      </div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>
                        {member.name || `Group Member ${index + 1}`} • {listedMemberMinutes} min • {formatCurrency(listedMemberPrice, bookingCurrency)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

        </div>
      </div>
    );
  };
  const handleSummaryBlockerClick = (blocker) => {
    if (blocker.requiresBillingAddressFocus) {
      setSummaryReturnSection("products");
      focusBillingAddressForProducts("summary");
      return;
    }

    setSummaryReturnSection(blocker.key);
    scrollToSection(blocker.ref);
  };
  const handleSubmitBlockerClick = (blocker) => {
    if (blocker.requiresPolicyFocus) {
      blocker.ref?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    handleSummaryBlockerClick(blocker);
  };

  const totals = calculateTotals();

  // 22D: whether the SELECTED package still covers this booking, asked again here because a member added after
  // the package was chosen can invalidate it. The summary must never show a credit that will not be taken.
  const selectedPackageCheck = selectedPackage
    ? packagePartyCheck(selectedPackage)
    : null;

  // A CHOSEN PACKAGE THAT CANNOT PAY FOR THIS BOOKING REFUSES THE CONFIRM BUTTON (user, 2026-10-11), and says
  // why underneath it. The way out is the "Pay Separately Instead" button in the packages section, which clears
  // the selection — stated in the message, because a disabled button with no way forward is a dead end.
  const packageCannotPay =
    Boolean(selectedPackage) && selectedPackageCheck?.ok === false;

  /**
   * HOW MANY PACKAGE SESSIONS THIS BOOKING SPENDS — one per PERSON the package pays for, because that is what the
   * server claims (`partySize`). The summary used to say "uses ONE session" unconditionally, so a group booking
   * paid with a package still claimed one session (reported 2026-10-19). Add-on services do not change it: the
   * claim is per attendee, not per line.
   *
   * AND SINCE SPLIT PAYMENT (2026-10-27) IT COUNTS ONLY THE PEOPLE THE PACKAGE COVERS: a party with one member
   * outside the package spends one session, not one per person, because the member outside it is paid by card.
   */
  const packageSessionsUsed = selectedPackage
    ? Math.max(
        1,
        Number(selectedPackageCheck?.coveredCount) ||
          1 + (selectedPackageCheck?.coveredMemberIndexes || []).length,
      )
    : Math.max(1, Number(groupSize) || 1);

  /**
   * DOES THIS SPLIT NEED AN EXPLICIT "YES, I KNOW"? — when the package is paying for some of the services and the
   * card is paying for others, the customer is being charged money on a booking they think of as "paid with my
   * package". Products and a tip are plainly additions they just chose; an uncovered SERVICE is not.
   */
  const splitNeedsAcknowledgement =
    Boolean(selectedPackage) && totals.cardServiceTotal > 0;
  // The signature of the figure they are acknowledging. A different package or a different amount is a different
  // question, so the tick goes away by itself — no effect, and no chance of a stale "yes".
  const splitSignature = `${
    selectedPackage?._id || ""
  }:${totals.cardServiceTotal}`;
  const splitAcknowledged =
    splitNeedsAcknowledgement && splitAckSignature === splitSignature;

  // Live tip validation.
  //
  // The submit handler already refuses a bad tip, but that only fires once the customer
  // presses Pay - so "5000" sat there looking accepted. Validate as they type instead, and
  // keep the submit guard as the backstop (a button is not a closed API).
  const customTipValue = parseCustomTip();
  const customTipCeiling = maxCustomTip(totals.tipBaseServiceTotal);
  const tipCurrency = isFreelancerBooking
    ? staff?.currency || freelancerBusiness?.currency || "USD"
    : shop?.currency || "USD";
  const customTipError = !tipIsCustom
    ? ""
    : String(customTipAmount).trim() === ""
      ? ""
      : customTipValue === null
        ? "Enter a valid amount."
        : customTipValue < 0
          ? "A tip cannot be negative."
          : customTipValue > customTipCeiling
            ? `Tip cannot be more than ${formatCurrency(customTipCeiling, tipCurrency)} (100% of the service).`
            : "";

  return (
    <>
      <CustomerHeader />
      {/* BACK FROM BUYING A PACKAGE: the form refilled itself from the draft. Say so, once. */}
      {draftRestoredNotice && (
        <div
          style={{
            background: "#eff6ff",
            borderBottom: "1px solid #bfdbfe",
            color: "#1e40af",
            padding: "10px 20px",
            fontSize: "14px",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            alignItems: "center",
          }}
        >
          <span>
            Welcome back — we kept everything you had entered for this booking.
          </span>
          <button
            type="button"
            onClick={() => setDraftRestoredNotice(false)}
            style={{
              background: "transparent",
              border: "none",
              color: "#1e40af",
              fontSize: "18px",
              lineHeight: 1,
              cursor: "pointer",
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {/* Preview Mode Banner */}
      {isPreviewMode && (
        <div
          style={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            color: "white",
            padding: "12px 20px",
            textAlign: "center",
            position: "fixed",
            top: "60px",
            left: 0,
            right: 0,
            zIndex: 999,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <span style={{ fontSize: "14px", fontWeight: "500" }}>
            🧪 <strong>Preview Mode</strong> - This is a test booking. No
            payment will be charged.
          </span>
        </div>
      )}
      <div
        className="customer-booking-container"
        style={{
          paddingTop: isPreviewMode ? "132px" : "92px",
          minHeight: "auto",
          background: "linear-gradient(to bottom, #f9fafb 0%, #ffffff 300px)",
        }}
      >
        <form onSubmit={handleSubmit} className="booking-form">

          {expandedSection !== "summary" && renderSelectedServiceCard()}

          {isAuthenticated && (
            <section
              className="form-section"
              ref={(node) => {
                contactSectionRef.current = node;
                customerInfoSectionRef.current = node;
              }}
              style={{
                ...getSectionBlockStyle(
                  customerInfoComplete ? "complete" : "attention",
                ),
                ...getCollapsibleSectionStyle(expandedSection, "customerInfo"),
              }}
            >
              <SectionStatusHeader
                title="Contact Information"
                state={customerInfoComplete ? "complete" : "attention"}
                isMobile={isMobile}
                hideTopControls
                sectionExpanded={expandedSection === "customerInfo"}
                actionLabel="Next"
                actionDisabled={!customerInfoComplete}
                onToggle={() =>
                  customerInfoComplete && advanceToNextSection("customerInfo")
                }
              />
              <SummaryReturnPrompt
                summaryReturnSection={summaryReturnSection}
                sectionKey="customerInfo"
                onBackToSummary={() => {
                  setSummaryReturnSection("");
                  scrollToSection(summarySectionRef);
                }}
              />
              <PackageJumpLink show={hasPackageSection} isMobile={isMobile} />
              {!customerInfoComplete && (
                <p style={{ margin: "0 0 10px 0", color: "#92400e", fontSize: "12px" }}>
                  Needs attention: phone number is required before checkout.
                </p>
              )}
              <p
                style={{
                  margin: "0 0 12px 0",
                  color: customerInfo.phone.trim() ? "#4b5563" : "#b91c1c",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                {customerInfo.phone.trim()
                  ? "We will use this phone number for booking updates and reminders."
                  : "A phone number is required to complete this booking. It will be saved to your account before payment."}
              </p>
              <div className="form-grid">
                <input type="text" value={customerInfo.firstName} readOnly />
                <input type="text" value={customerInfo.lastName} readOnly />
                <input type="email" value={customerInfo.email} readOnly />
                <input
                  ref={phoneInputRef}
                  type="tel"
                  placeholder="Phone"
                  value={customerInfo.phone}
                  onChange={(e) =>
                    setCustomerInfo({ ...customerInfo, phone: e.target.value })
                  }
                  required
                  style={{
                    borderColor: customerInfo.phone.trim() ? undefined : "#ef4444",
                    boxShadow: customerInfo.phone.trim()
                      ? undefined
                      : "0 0 0 1px rgba(239, 68, 68, 0.25)",
                  }}
                />
              </div>
              <div style={{ marginTop: "14px", display: "flex", justifyContent: "space-between", gap: "10px" }}>
                <button
                  type="button"
                  style={{
                    ...nextButtonStyle,
                    visibility: "hidden",
                  }}
                  disabled
                >
                  Back
                </button>
                <button
                  type="button"
                  style={{
                    ...nextButtonStyle,
                    opacity: customerInfoComplete ? 1 : 0.6,
                    cursor: customerInfoComplete ? "pointer" : "not-allowed",
                  }}
                  disabled={!customerInfoComplete}
                  onClick={() => advanceToNextSection("customerInfo")}
                >
                  Next
                </button>
              </div>
            </section>
          )}

          {showGuestPackageAccess && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1200,
                padding: "20px",
              }}
            >
              <div
                // ENTER BELONGS TO THE MODAL. This modal lives inside the booking <form>, so without
                // this the browser treats Enter here as an implicit form submission — the customer
                // presses Enter to confirm a code and the page underneath reacts instead. Keydown
                // bubbles from the inputs to here, so one handler covers the whole card.
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  e.stopPropagation();
                  if (guestChallenge.busy) return;
                  if (guestChallenge.step === "identity") {
                    // FOUR DIGITS ARE REQUIRED (user, 2026-10-09 — reversing the 2026-10-09 allowance).
                    //
                    // The blank was allowed because a package that had been GIVEN AWAY carried no holder
                    // phone: the transfer cleared the sender's. That is no longer how a transfer works — the
                    // recipient gives THEIR OWN number when they accept — so every holder has one on record
                    // and a blank can never match. Allowing it did not help a recipient; it only let somebody
                    // submit a value guaranteed to fail, and then read the failure as a fault in the package.
                    if (
                      guestChallenge.email.trim() &&
                      guestChallenge.phoneLast4.length === 4
                    ) {
                      startGuestPackageChallenge();
                    }
                    return;
                  }
                  if (guestChallenge.code.length === 6) {
                    verifyGuestPackageChallenge();
                  }
                }}
                style={{
                  background: "white",
                  borderRadius: "16px",
                  maxWidth: "440px",
                  width: "100%",
                  padding: "28px",
                }}
              >
                <h3 style={{ margin: "0 0 6px 0", fontSize: "20px", color: "#1e293b" }}>
                  Use my package
                </h3>
                <p style={{ margin: "0 0 18px 0", fontSize: "14px", color: "#64748b", lineHeight: 1.5 }}>
                  {guestChallenge.step === "identity"
                    ? "Enter the email the package belongs to, and the last 4 digits of the phone on it. If the package was given to you, that is the number you gave when you accepted it."
                    : "Enter the 6-digit code we emailed you."}
                </p>

                {guestChallenge.error && (
                  <div
                    style={{
                      padding: "10px 12px",
                      marginBottom: "14px",
                      background: "#fee2e2",
                      color: "#991b1b",
                      borderRadius: "8px",
                      fontSize: "13px",
                    }}
                  >
                    {guestChallenge.error}
                  </div>
                )}

                {guestChallenge.step === "identity" ? (
                  <>
                    <input
                      type="email"
                      placeholder="Email used at purchase"
                      value={guestChallenge.email}
                      onChange={(e) =>
                        // Clear a stale rejection the moment they start correcting it, so a red box does
                        // not sit under a now-correct address (2026-10-07).
                        setGuestChallenge((prev) => ({
                          ...prev,
                          email: e.target.value,
                          error: "",
                        }))
                      }
                      style={{ width: "100%", padding: "11px 12px", marginBottom: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
                    />
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="Last 4 digits of phone"
                      value={guestChallenge.phoneLast4}
                      onChange={(e) =>
                        setGuestChallenge((prev) => ({
                          ...prev,
                          phoneLast4: e.target.value.replace(/\D/g, "").slice(0, 4),
                          error: "",
                        }))
                      }
                      style={{ width: "100%", padding: "11px 12px", marginBottom: "16px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={19}
                      placeholder="16-digit package number (on your email)"
                      value={
                        // Shown 4-4-4-4 as they type, kept bare underneath. A 16-digit run is miscounted when
                        // it is read back off a screen, and this is the number they must not get wrong.
                        String(guestChallenge.accountNumber || "")
                          .replace(/\D/g, "")
                          .replace(/(\d{4})(?=\d)/g, "$1 ")
                      }
                      onChange={(e) =>
                        setGuestChallenge((prev) => ({
                          ...prev,
                          accountNumber: e.target.value.replace(/\D/g, "").slice(0, 16),
                          error: "",
                        }))
                      }
                      style={{ width: "100%", padding: "11px 12px", marginBottom: "16px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
                    />
                    <button
                      type="button"
                      onClick={startGuestPackageChallenge}
                      disabled={
                        guestChallenge.busy ||
                        !guestChallenge.email ||
                        guestChallenge.phoneLast4.length !== 4
                      }
                      style={{ width: "100%", padding: "13px", background: guestChallenge.busy ? "#9ca3af" : "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)", color: "white", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: "600", cursor: guestChallenge.busy ? "not-allowed" : "pointer" }}
                    >
                      {guestChallenge.busy ? "Sending…" : "Send code"}
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="6-digit code"
                      value={guestChallenge.code}
                      onChange={(e) =>
                        setGuestChallenge((prev) => ({
                          ...prev,
                          code: e.target.value.replace(/\D/g, "").slice(0, 6),
                        }))
                      }
                      style={{ width: "100%", padding: "11px 12px", marginBottom: "16px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "18px", letterSpacing: "4px", textAlign: "center", boxSizing: "border-box" }}
                    />
                    <button
                      type="button"
                      onClick={verifyGuestPackageChallenge}
                      disabled={guestChallenge.busy || guestChallenge.code.length !== 6}
                      style={{ width: "100%", padding: "13px", background: guestChallenge.busy ? "#9ca3af" : "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)", color: "white", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: "600", cursor: guestChallenge.busy ? "not-allowed" : "pointer" }}
                    >
                      {guestChallenge.busy ? "Verifying…" : "Verify"}
                    </button>
                    <button
                      type="button"
                      onClick={startGuestPackageChallenge}
                      disabled={guestChallenge.busy}
                      style={{ width: "100%", marginTop: "8px", padding: "10px", background: "none", border: "none", color: "#2d6da3", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
                    >
                      Resend code
                    </button>
                    {/* THE WAY BACK, which did not exist. Reported 2026-10-07: a wrong email was
                        typed, the code screen appeared, and the email was then unreachable — the step
                        could not be left and `Cancel` reopened on this same screen. Kept as a plain
                        text link so it reads as an exit, not as another action to take. */}
                    <button
                      type="button"
                      onClick={backToGuestIdentity}
                      disabled={guestChallenge.busy}
                      style={{ width: "100%", marginTop: "4px", padding: "10px", background: "none", border: "none", color: "#64748b", fontSize: "13px", fontWeight: "600", cursor: guestChallenge.busy ? "not-allowed" : "pointer", textDecoration: "underline" }}
                    >
                      Use a different email
                    </button>
                    {/* A code that never arrives must not read as "you typed it wrong". Delivery can
                        fail for reasons the customer cannot see (and on 2026-10-05 it did — every email
                        on the platform was failing), so the way out is stated up front rather than
                        discovered by retrying. It names no package and no purchase, so it discloses
                        nothing either way. */}
                    <p
                      style={{
                        margin: "12px 0 0 0",
                        fontSize: "13px",
                        color: "#64748b",
                        lineHeight: 1.5,
                      }}
                    >
                      No code arriving? You can still book this appointment and pay
                      with your card — just cancel this and continue.
                    </p>
                  </>
                )}

                <button
                  type="button"
                  onClick={closeGuestPackageAccess}
                  style={{ width: "100%", marginTop: "10px", padding: "11px", background: "#f1f5f9", color: "#475569", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Package Banner - Show if packages available.
              Phase 16 deliverable 2 (2026-10-04): these used to require `!isAuthenticated`, so a
              SIGNED-IN customer saw no packages for sale at all — only the ones they already owned.
              "Packages I can redeem" and "packages I could buy" are different lists and both belong
              on this screen. */}
          {/* ── THE TOP PACKAGE BANNER IS GONE (user, 2026-10-19) ─────────────────────────────────────────
              It carried the same message as the anchor on every step — *"packages for this booking, see below"* —
              and sat above a section that says it better, in more detail, and can actually show the packages.
              Two tiles for one message is the duplication the user asked to remove: the per-step anchor is the
              single entry point now, and the section itself still explains which packages cannot be used here
              and why. */}


          {/* Customer Information - Only show for guests */}
          {!isAuthenticated && (
            <section
              ref={customerInfoSectionRef}
              style={{
                marginBottom: "14px",
                padding: "16px",
                borderRadius: "8px",
                ...getSectionBlockStyle(
                  customerInfoComplete ? "complete" : "attention",
                ),
                ...getCollapsibleSectionStyle(expandedSection, "customerInfo"),
              }}
            >
              <SectionStatusHeader
                title="Your Information"
                state={customerInfoComplete ? "complete" : "attention"}
                isMobile={isMobile}
                hideTopControls
                sectionExpanded={expandedSection === "customerInfo"}
                actionLabel="Next"
                actionDisabled={!customerInfoComplete}
                onToggle={() =>
                  customerInfoComplete && advanceToNextSection("customerInfo")
                }
              />
              <SummaryReturnPrompt
                summaryReturnSection={summaryReturnSection}
                sectionKey="customerInfo"
                onBackToSummary={() => {
                  setSummaryReturnSection("");
                  scrollToSection(summarySectionRef);
                }}
              />
              <PackageJumpLink show={hasPackageSection} isMobile={isMobile} />
              {!customerInfoComplete && (
                <p style={{ margin: "0 0 10px 0", color: "#92400e", fontSize: "12px" }}>
                  Needs attention: complete all contact fields and accept SMS consent to continue.
                </p>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <input
                  type="text"
                  placeholder="First Name"
                  value={customerInfo.firstName}
                  onChange={(e) =>
                    setCustomerInfo({
                      ...customerInfo,
                      firstName: e.target.value,
                    })
                  }
                  required
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: isMobile ? "12px" : "13px",
                    fontFamily: "inherit",
                  }}
                />
                <input
                  type="text"
                  placeholder="Last Name"
                  value={customerInfo.lastName}
                  onChange={(e) =>
                    setCustomerInfo({
                      ...customerInfo,
                      lastName: e.target.value,
                    })
                  }
                  required
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: isMobile ? "12px" : "13px",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "16px",
                }}
              >
                <input
                  type="email"
                  placeholder="Email"
                  value={customerInfo.email}
                  onChange={(e) =>
                    setCustomerInfo({ ...customerInfo, email: e.target.value })
                  }
                  required
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: isMobile ? "12px" : "13px",
                    fontFamily: "inherit",
                  }}
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={customerInfo.phone}
                  onChange={(e) =>
                    setCustomerInfo({ ...customerInfo, phone: e.target.value })
                  }
                  required
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: isMobile ? "12px" : "13px",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              <CustomerNoteField
                value={customerNote}
                onChange={setCustomerNote}
                maxLength={500}
                isMobile={isMobile}
              />

              {/* SMS Consent Checkbox (TCPA Compliance) */}
              <div
                style={{
                  gridColumn: "1 / -1",
                  marginTop: "16px",
                  padding: "16px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    required
                    style={{
                      marginTop: "3px",
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  />
                  <span style={{ color: "#374151" }}>
                    I agree to receive SMS notifications from OGESTYLE about my
                    appointments, including booking confirmations, reminders,
                    and updates. Message frequency may vary. Message and data
                    rates may apply. Text HELP for support. Reply STOP to
                    opt-out anytime.{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#2563eb",
                        textDecoration: "underline",
                      }}
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>
              <div style={{ marginTop: "14px", display: "flex", justifyContent: "space-between", gap: "10px" }}>
                <button
                  type="button"
                  style={{
                    ...nextButtonStyle,
                    visibility: "hidden",
                  }}
                  disabled
                >
                  Back
                </button>
                <button
                  type="button"
                  style={{
                    ...nextButtonStyle,
                    opacity: customerInfoComplete ? 1 : 0.6,
                    cursor: customerInfoComplete ? "pointer" : "not-allowed",
                  }}
                  disabled={!customerInfoComplete}
                  onClick={() => advanceToNextSection("customerInfo")}
                >
                  Next
                </button>
              </div>
            </section>
          )}

          {/* Date & Time Selection */}
          <section
            className="form-section"
            ref={dateTimeSectionRef}
            style={{
              ...getSectionBlockStyle(scheduleComplete ? "complete" : "attention"),
              ...getCollapsibleSectionStyle(expandedSection, "dateTime"),
            }}
          >
            <SectionStatusHeader
              title="Select Date & Time"
              state={scheduleComplete ? "complete" : "attention"}
              isMobile={isMobile}
              hideTopControls
              sectionExpanded={expandedSection === "dateTime"}
              actionLabel="Next"
              actionDisabled={!scheduleComplete}
              onToggle={() => scheduleComplete && advanceToNextSection("dateTime")}
            />
            <SummaryReturnPrompt
              summaryReturnSection={summaryReturnSection}
              sectionKey="dateTime"
              onBackToSummary={() => {
                setSummaryReturnSection("");
                scrollToSection(summarySectionRef);
              }}
            />
            <PackageJumpLink show={hasPackageSection} isMobile={isMobile} />
            {!scheduleComplete && (
              <p style={{ margin: "0 0 10px 0", color: "#92400e", fontSize: "12px" }}>
                Needs attention: select a slot or join the waitlist when no availability exists.
              </p>
            )}
            {/* F2: explained when a service change invalidated the chosen slot */}
            {slotInvalidatedNotice && (
              <div
                style={{
                  margin: "0 0 12px 0",
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #fcd34d",
                  background: "#fffbeb",
                  color: "#92400e",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                {slotInvalidatedNotice}
              </div>
            )}
            {waitlistJoined && !selectedSlot && (
              <div
                style={{
                  margin: "0 0 12px 0",
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #86efac",
                  background: "#ecfdf5",
                  color: "#065f46",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                Waitlist request saved. Your waitlist is not active or guaranteed until you book an appointment.
              </div>
            )}
            {selectedSlot && (
              <div
                style={{
                  margin: "0 0 12px 0",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  border: "1px solid #fed7aa",
                  background: "#fff7ed",
                  color: "#9a3412",
                  fontSize: isMobile ? "11px" : "13px",
                  lineHeight: "1.5",
                }}
              >
                <strong>Availability check:</strong> {BOOKING_SLOT_HOLD_NOTICE}
              </div>
            )}
            {loading || !shop ? (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                Loading calendar...
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "24px",
                }}
              >
                <div>
                  <Calendar
                    onChange={(value) => {
                      const pickedDate = Array.isArray(value) ? value[0] : value;
                      if (!pickedDate) return;
                      const year = pickedDate.getFullYear();
                      const month = String(pickedDate.getMonth() + 1).padStart(2, "0");
                      const day = String(pickedDate.getDate()).padStart(2, "0");
                      setSelectedDate(`${year}-${month}-${day}`);
                      setSelectedSlot(null);
                    }}
                    value={
                      selectedDate
                        ? (() => {
                            const [year, month, day] = selectedDate.split("-").map(Number);
                            return new Date(year, month - 1, day);
                          })()
                        : null
                    }
                    minDate={new Date()}
                    style={{
                      width: "100%",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                    }}
                  />
                </div>

                <div>
                  {primarySelectionPending ? (
                    <div
                      style={{
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px dashed #f59e0b",
                        background: "#fffbeb",
                        color: "#92400e",
                        fontSize: isMobile ? "12px" : "13px",
                        fontWeight: "600",
                        lineHeight: 1.5,
                      }}
                    >
                      Pick a service option above to see available times — the
                      appointment length depends on the option you choose.
                    </div>
                  ) : selectedDate ? (
                    <div>
                      {slotsLoading ? (
                        <div
                          style={{
                            fontSize: isMobile ? "11px" : "12px",
                            color: "#666",
                            textAlign: "center",
                            padding: "20px",
                          }}
                        >
                          Loading available times...
                        </div>
                      ) : availableSlots && availableSlots.length > 0 ? (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
                            gap: "8px",
                          }}
                        >
                          {availableSlots.map((slot, index) => {
                            const slotKey =
                              typeof slot === "string"
                                ? slot
                                : slot.time24 || slot.time12 || index;
                            const slotDisplay =
                              typeof slot === "string"
                                ? slot
                                : slot.time12 || slot.time24;
                            const slotValue =
                              typeof slot === "string"
                                ? slot
                                : slot.time24 || slot.time12;
                            const slotEndTime =
                              typeof slot === "object" && slot?.endTime
                                ? slot.endTime
                                : "";

                            return (
                              <button
                                key={`${slotKey}-${index}`}
                                type="button"
                                onClick={() => {
                                  let time12 = slotDisplay;
                                  if (typeof slot === "string" || !slot?.time12) {
                                    const [hours, minutes] = String(slotValue).split(":");
                                    const parsedHours = parseInt(hours, 10);
                                    if (!Number.isNaN(parsedHours) && minutes !== undefined) {
                                      const ampm = parsedHours >= 12 ? "PM" : "AM";
                                      const h12 = parsedHours % 12 || 12;
                                      time12 = `${h12}:${minutes} ${ampm}`;
                                    }
                                  }

                                  setSelectedSlot({
                                    time24: slotValue,
                                    time12,
                                    endTime: slotEndTime,
                                    available: true,
                                  });
                                  setSlotInvalidatedNotice("");
                                  saveDirectBookingHold({
                                    shopId,
                                    staffId,
                                    serviceId,
                                    appointmentDate: selectedDate,
                                    startTime: slotValue,
                                  });
                                }}
                                style={{
                                  padding: "8px 12px",
                                  border:
                                    selectedSlot?.time24 === slotValue
                                      ? "2px solid #2d6da3"
                                      : "1px solid #d1d5db",
                                  borderRadius: "4px",
                                  backgroundColor:
                                    selectedSlot?.time24 === slotValue
                                      ? "#f0f8ff"
                                      : "#fff",
                                  fontSize: isMobile ? "10px" : "12px",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                }}
                              >
                                {slotDisplay}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: "12px" }}>
                          <div
                            style={{
                              fontSize: isMobile ? "11px" : "12px",
                              color: "#666",
                              textAlign: "center",
                              padding: "20px",
                            }}
                          >
                            No available times for this date
                          </div>

                          {nextAvailableSlots.length > 0 && (
                            <div
                              style={{
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                padding: "12px",
                                background: "#fff",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "700",
                                  color: "#0f172a",
                                  marginBottom: "8px",
                                }}
                              >
                                Next available dates and times
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gap: "10px",
                                }}
                              >
                                {nextAvailableGroupedSlots.map((group) => (
                                  <div
                                    key={group.dateKey}
                                    style={{
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "8px",
                                      padding: "10px",
                                      background: "#fff",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "700",
                                        color: "#0f172a",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      {group.displayDate}
                                    </div>
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                          "repeat(auto-fit, minmax(120px, 1fr))",
                                        gap: "8px",
                                      }}
                                    >
                                      {group.slots.map((slot, idx) => (
                                        <button
                                          key={`${group.dateKey}-${slot.time24 || slot.time12 || idx}`}
                                          type="button"
                                          onClick={() => handleSelectNextSlot(slot)}
                                          style={{
                                            padding: "10px 10px",
                                            background: "#f9fafb",
                                            border: "2px solid #e5e7eb",
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            fontSize: isMobile ? "12px" : "13px",
                                            fontWeight: "600",
                                            color: "#374151",
                                            textAlign: "center",
                                            lineHeight: 1.25,
                                          }}
                                        >
                                          {slot.time12 || formatTime12(slot.time24)}
                                          {slot.endTime ? ` - ${formatTime12(slot.endTime)}` : ""}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {nextAvailableError ? (
                            <div
                              style={{
                                border: "1px solid #fecaca",
                                background: "#fef2f2",
                                color: "#991b1b",
                                borderRadius: "8px",
                                padding: "10px 12px",
                                fontSize: "12px",
                              }}
                            >
                              {nextAvailableError}
                            </div>
                          ) : nextAvailableSlots.length === 0 ? (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                textAlign: "center",
                                padding: "4px 0 0",
                              }}
                            >
                              Looking for the next available openings...
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "12px",
                        color: "#666",
                        textAlign: "center",
                        padding: "20px",
                      }}
                    >
                      Please select a date first
                    </div>
                  )}
                </div>
              </div>
            )}
            {selectedDate &&
              availableSlots !== null &&
              availableSlots.length === 0 &&
              !loading &&
              !selectedSlot && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    background: "#fef3c7",
                    borderRadius: "8px",
                    border: "1px solid #fbbf24",
                    marginTop: "12px",
                  }}
                >
                  <p className="no-slots" style={{ marginBottom: "12px" }}>
                    No available slots for this date{" "}
                    {groupSize > 1 && `(group of ${groupSize})`}
                  </p>
                </div>
              )}

            {staff &&
              ["R3", "R4", "R7"].includes(staff.role) &&
              selectedDate &&
              availableSlots !== null &&
              availableSlots.length === 0 &&
              !loading &&
              !selectedSlot && (
              <div
                style={{
                  textAlign: "left",
                  padding: "16px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  marginTop: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "flex-start" : "center",
                    flexDirection: isMobile ? "column" : "row",
                    gap: "10px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <p
                      style={{
                        margin: 0,
                        color: "#0f172a",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      {isSelectedDateToday
                        ? sameDayWaitlistMessage
                        : "No slots are available for this date. Join the waitlist to receive alerts about open spots."}
                    </p>
                    {!isSelectedDateToday && (
                      <p
                        style={{
                          margin: 0,
                          color: "#b91c1c",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        Note: Joining waitlist does not activate or guarantee alerts until you complete a booking.
                      </p>
                    )}
                  </div>

                  {!isSelectedDateToday && (
                    <button
                      type="button"
                      disabled={
                        waitlistJoined ||
                        waitlistSubmitting ||
                        (!isAuthenticated && !waitlistContactReady)
                      }
                      onClick={handleJoinWaitlist}
                      style={{
                        padding: "10px 14px",
                        background: waitlistJoined ? "#16a34a" : "#111827",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor:
                          waitlistJoined || waitlistSubmitting
                            ? "not-allowed"
                            : "pointer",
                        fontWeight: "600",
                        fontSize: "14px",
                        opacity: waitlistJoined ? 0.9 : 1,
                      }}
                    >
                      {waitlistJoined
                        ? "Joined"
                        : waitlistSubmitting
                          ? "Joining..."
                          : "Join Waitlist"}
                    </button>
                  )}
                </div>

                {!isSelectedDateToday && !isAuthenticated && !waitlistContactReady && (
                  <p
                    style={{
                      marginTop: "8px",
                      marginBottom: 0,
                      color: "#92400e",
                      fontSize: "12px",
                    }}
                  >
                    Add your name and email in Your Information to join the waitlist.
                  </p>
                )}

                {waitlistMessage.text && (
                  <p
                    style={{
                      marginTop: "10px",
                      marginBottom: 0,
                      color:
                        waitlistMessage.type === "error" ? "#991b1b" : "#065f46",
                      fontSize: "13px",
                      fontWeight: "500",
                    }}
                  >
                    {waitlistMessage.text}
                  </p>
                )}

                {activeWaitlistConflict && (
                  <div
                    style={{
                      marginTop: "10px",
                      padding: "10px 12px",
                      border: "1px solid #fecaca",
                      borderRadius: "6px",
                      background: "#fff7ed",
                      color: "#7f1d1d",
                      fontSize: "13px",
                      lineHeight: 1.45,
                    }}
                  >
                    <div style={{ fontWeight: "700", marginBottom: "4px" }}>
                      Current waitlist request
                    </div>
                    <div>
                      <strong>Date:</strong>{" "}
                      {getWaitlistDateLabels(activeWaitlistConflict).join(", ") || "Date not available"}
                    </div>
                    <div>
                      <strong>Service(s):</strong>{" "}
                      {getWaitlistServiceLabels(activeWaitlistConflict).join(", ")}
                    </div>
                  </div>
                )}

                {pendingWaitlistReplacement && (
                  <div
                    style={{
                      marginTop: "10px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    <button
                      type="button"
                      disabled={waitlistSubmitting}
                      onClick={() => handleJoinWaitlist({ replaceExistingWaitlist: true })}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "none",
                        background: "#991b1b",
                        color: "white",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: waitlistSubmitting ? "not-allowed" : "pointer",
                      }}
                    >
                      Replace Waitlist
                    </button>
                    <button
                      type="button"
                      disabled={waitlistSubmitting}
                      onClick={handleKeepExistingWaitlist}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        background: "#fff",
                        color: "#374151",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: waitlistSubmitting ? "not-allowed" : "pointer",
                      }}
                    >
                      Keep Existing
                    </button>
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: "14px", display: "flex", justifyContent: "space-between", gap: "10px" }}>
              <button
                type="button"
                style={nextButtonStyle}
                onClick={() => goToPreviousSection("dateTime")}
              >
                Back
              </button>
              <button
                type="button"
                style={{
                  ...nextButtonStyle,
                  opacity: scheduleComplete ? 1 : 0.6,
                  cursor: scheduleComplete ? "pointer" : "not-allowed",
                }}
                disabled={!scheduleComplete}
                onClick={() => advanceToNextSection("dateTime")}
              >
                Next
              </button>
            </div>
          </section>

          {/* Group Booking */}
          <section
            className="form-section"
            style={{
              background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
              border: "1px solid #dbeafe",
              borderRadius: "16px",
              boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
              padding: isMobile ? "16px" : "20px 22px",
              ...getCollapsibleSectionStyle(expandedSection, "group"),
            }}
          >
            <SectionStatusHeader
              title="Group Booking"
              state={groupSize > 1 ? "complete" : "optional"}
              isMobile={isMobile}
              hideTopControls
              hideGuidance
              sectionExpanded={expandedSection === "group"}
              actionLabel="Next"
              onToggle={() => {
                if (showGroupForm && groupSize === 1) {
                  setShowGroupForm(false);
                  setGroupMembers([]);
                }
                advanceToNextSection("group");
              }}
            />
            <div style={{ height: "6px" }} />
            <PackageJumpLink show={hasPackageSection} isMobile={isMobile} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  minWidth: "0",
                }}
              >
                <div
                  style={{
                    fontSize: isMobile ? "14px" : "16px",
                    fontWeight: "700",
                    color: "#0f172a",
                  }}
                >
                  {groupSize} {groupSize === 1 ? "person" : "people"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (
                    !customerInfo.firstName ||
                    !customerInfo.email ||
                    !service ||
                    !selectedDate ||
                    !selectedSlot
                  ) {
                    return;
                  }
                  setShowGroupForm(!showGroupForm);
                  if (!showGroupForm && groupMembers.length === 0) {
                    const newMember = {
                      id: Date.now(),
                      name: "",
                      serviceId: "",
                      serviceName: "",
                      serviceDuration: null,
                      // P5: the member's own units / hours / option. Without this
                      // a per-unit or tiered member service had no price or
                      // length, so their slot was computed from the catalog.
                      selection: {},
                    };
                    setGroupMembers([newMember]);
                  }
                }}
                disabled={
                  !customerInfo.firstName ||
                  !customerInfo.email ||
                  !service ||
                  !selectedDate ||
                  !selectedSlot
                }
                style={{
                  padding: "10px 20px",
                  background:
                    !customerInfo.firstName ||
                    !customerInfo.email ||
                    !service ||
                    !selectedDate ||
                    !selectedSlot
                      ? "#e5e7eb"
                      : "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
                  color:
                    !customerInfo.firstName ||
                    !customerInfo.email ||
                    !service ||
                    !selectedDate ||
                    !selectedSlot
                      ? "#9ca3af"
                      : "white",
                  border: "none",
                  borderRadius: "999px",
                  cursor:
                    !customerInfo.firstName ||
                    !customerInfo.email ||
                    !service ||
                    !selectedDate ||
                    !selectedSlot
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "14px",
                  fontWeight: "700",
                  transition: "all 0.2s",
                  boxShadow:
                    !customerInfo.firstName ||
                    !customerInfo.email ||
                    !service ||
                    !selectedDate ||
                    !selectedSlot
                      ? "none"
                      : "0 8px 20px rgba(15, 23, 42, 0.18)",
                }}
              >
                {showGroupForm ? "− Hide Group Form" : "+ Add Group Members"}
              </button>
            </div>

            <div
              style={{
                marginTop: "-4px",
                marginBottom: "8px",
                padding: "10px 12px",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                color: "#334155",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              Would you like to bring an additional member? You can add up to 2 additional members.
            </div>

            {/* Inline Group Member Form */}
            {showGroupForm && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "20px",
                  background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
                }}
              >
                <div
                  style={{
                    padding: "16px 18px",
                    background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
                    borderRadius: "14px",
                    border: "none",
                    color: "white",
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.16)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      textTransform: "uppercase",
                      marginBottom: "8px",
                      fontWeight: "700",
                      letterSpacing: "0.08em",
                      opacity: 0.85,
                    }}
                  >
                    Your Booking
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    <strong style={{ fontSize: "14px" }}>
                      {customerInfo.firstName} {customerInfo.lastName}
                    </strong>
                    <span style={{ opacity: 0.7 }}>•</span>
                    <span style={{ fontSize: "14px" }}>{service?.name}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginTop: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.14)",
                        padding: "8px 14px",
                        borderRadius: "999px",
                        fontWeight: "700",
                        fontSize: "13px",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      📅 {new Date(`${selectedDate}T00:00:00`).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.14)",
                        padding: "8px 14px",
                        borderRadius: "999px",
                        fontWeight: "700",
                        fontSize: "13px",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      🕐 {selectedSlot?.time12 || selectedSlot?.time24 || "Time not selected"}
                    </div>
                  </div>
                  {groupMembers.length > 0 && (
                    <div
                      style={{
                        marginTop: "14px",
                        padding: "12px 14px",
                        background: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "10px",
                        fontSize: "12px",
                        fontWeight: "500",
                        lineHeight: 1.5,
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      Everyone arrives together at {selectedSlot?.time12 || selectedSlot?.time24}. Group members pick their individual services and times below.
                    </div>
                  )}
                </div>

                {/* Group Members List */}
                <div style={{ marginBottom: "20px" }}>
                  {groupMembers.map((member, index) => (
                    <div
                      key={member.id}
                      style={{
                        marginBottom: "16px",
                        padding: "16px",
                        background: "white",
                        borderRadius: "8px",
                        border: member.preferredTime
                          ? "2px solid #10b981"
                          : "1px solid #d1d5db",
                        position: "relative",
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        overflow: "visible",
                      }}
                    >
                      {member.preferredTime && (
                        <div
                          style={{
                            position: "absolute",
                            top: "10px",
                            right: "12px",
                            background: "#10b981",
                            color: "white",
                            padding: "4px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            zIndex: 2,
                          }}
                        >
                          ✓ Time Selected
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "8px",
                          marginBottom: "12px",
                          paddingRight: member.preferredTime ? "130px" : "0",
                        }}
                      >
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
                            borderRadius: "16px",
                            border: "1px solid #e2e8f0",
                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
                          }}
                        >
                          Group Member #{index + 1}{" "}
                          {member.name && `- ${member.name}`}
                        </h4>
                        <button
                          type="button"
                          onClick={() => {
                            const removedIndex = groupMembers.findIndex(
                              (m) => m.id === member.id,
                            );
                            if (removedIndex === -1) return;

                            const removed = groupMembers[removedIndex];
                            const remaining = groupMembers.filter(
                              (m) => m.id !== member.id,
                            );

                            // The member who moves up takes the removed member's
                            // own start time. They were both chained directly after
                            // the same predecessor, so that time is still exactly
                            // the earliest feasible one - the auto-adjustment the
                            // removal rules ask for, needing no re-pick.
                            // Members below keep their times: the chain only got
                            // shorter, so their stored times remain sequential and
                            // valid. Removing the LAST member changes nothing.
                            if (remaining[removedIndex]) {
                              remaining[removedIndex] = {
                                ...remaining[removedIndex],
                                preferredTime: removed.preferredTime,
                                preferredTime12: removed.preferredTime12,
                              };
                            }

                            setGroupMembers(remaining);
                            setGroupSize(remaining.length + 1);
                          }}
                          style={{
                            padding: "6px 12px",
                            background: "#fee2e2",
                            color: "#dc2626",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          Remove
                        </button>
                      </div>

                      <div style={{ display: "grid", gap: "12px" }}>
                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "6px",
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Who *
                          </label>
                          <input
                            type="text"
                            value={member.name}
                            onChange={(e) => {
                              const newMembers = groupMembers.map((m) =>
                                m.id === member.id
                                  ? { ...m, name: e.target.value }
                                  : m,
                              );
                              setGroupMembers(newMembers);
                            }}
                            placeholder="Enter name"
                            style={{
                              width: "100%",
                              maxWidth: "100%",
                              boxSizing: "border-box",
                              padding: "10px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "13px",
                            }}
                          />
                          <div
                            style={{
                              marginTop: "8px",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                            }}
                          >
                            {GROUP_RELATIONSHIP_OPTIONS.map((relationship) => (
                              <button
                                key={`${member.id}-${relationship}`}
                                type="button"
                                onClick={() => {
                                  const newMembers = groupMembers.map((m) =>
                                    m.id === member.id
                                      ? { ...m, name: relationship }
                                      : m,
                                  );
                                  setGroupMembers(newMembers);
                                }}
                                style={{
                                  border: "1px solid #cbd5e1",
                                  background: member.name === relationship ? "#dbeafe" : "#ffffff",
                                  color: member.name === relationship ? "#1d4ed8" : "#334155",
                                  borderRadius: "999px",
                                  padding: "4px 10px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                }}
                              >
                                {relationship}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "6px",
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Service *
                          </label>
                          <select
                            value={member.serviceId}
                            onChange={async (e) => {
                              const selectedService = shopServices.find(
                                (s) => s._id === e.target.value,
                              );
                              const changedIndex = groupMembers.findIndex(
                                (m) => m.id === member.id,
                              );
                              const newMembers = clearDownstreamMemberTimes(
                                groupMembers.map((m) =>
                                  m.id === member.id
                                    ? {
                                        ...m,
                                        serviceId: e.target.value,
                                        serviceName: selectedService?.name || "",
                                        serviceDuration: selectedService
                                          ? normalizeServiceDuration(selectedService)
                                          : null,
                                        // P5: a different service is a different
                                        // line - the old units/option do not
                                        // carry across.
                                        selection: {},
                                        preferredTime: "",
                                        preferredTime12: "",
                                      }
                                    : m,
                                ),
                                changedIndex,
                              );
                              setGroupMembers(newMembers);
                            }}
                            style={{
                              width: "100%",
                              maxWidth: "100%",
                              boxSizing: "border-box",
                              padding: "10px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "13px",
                              background: "white",
                            }}
                          >
                            <option value="">Select service...</option>
                            {shopServices.map((svc) => (
                              <option key={svc._id} value={svc._id}>
                                {svc.name} ({svc.duration} min) - ${svc.price}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* P5: a member's per-unit / hourly / option service needs
                            its own selector, exactly like the primary and the
                            add-ons. Renders nothing for a fixed-price service. */}
                        {member.serviceId && (
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "6px",
                                fontSize: "13px",
                                fontWeight: "500",
                              }}
                            >
                              Units &amp; options *
                            </label>
                            <ServicePricingSelector
                              service={shopServices.find(
                                (s) => s._id === member.serviceId,
                              )}
                              selection={member.selection || {}}
                              onChange={(patch) =>
                                handleMemberSelectionChange(member.id, patch)
                              }
                              formatPrice={(value) =>
                                formatCurrency(value, bookingCurrency)
                              }
                              isMobile={isMobile}
                              accentColor="#1d4ed8"
                            />
                          </div>
                        )}

                        {member.serviceName && selectedDate && (
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "6px",
                                fontSize: "13px",
                                fontWeight: "500",
                              }}
                            >
                              Preferred Time *
                            </label>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fill, minmax(100px, 1fr))",
                                gap: "8px",
                              }}
                            >
                              {(() => {
                                const timeToMinutes = (timeStr) => {
                                  const [hours, minutes] = String(timeStr)
                                    .split(":")
                                    .map(Number);
                                  return hours * 60 + minutes;
                                };

                                const minutesToTime24 = (minutes) => {
                                  const hours = Math.floor(minutes / 60);
                                  const mins = minutes % 60;
                                  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
                                };

                                const formatTime12 = (minutes) => {
                                  const hours24 = Math.floor(minutes / 60);
                                  const mins = String(minutes % 60).padStart(2, "0");
                                  const hours12 = hours24 % 12 || 12;
                                  const ampm = hours24 >= 12 ? "PM" : "AM";
                                  return `${hours12}:${mins} ${ampm}`;
                                };

                                const memberService = shopServices.find(
                                  (s) => s._id === member.serviceId,
                                );

                                // P5/S4: lengths come from the RESOLVED lines.
                                // `member.serviceDuration` is the catalog value and
                                // `bundleDurationMinutes` is the catalog sum, so a
                                // member with units/hours/an option used to be
                                // chained off the wrong numbers and was offered
                                // slots that overlapped the previous person.
                                const memberIndex = groupMembers.findIndex(
                                  (m) => m.id === member.id,
                                );
                                const memberResolvedLine =
                                  resolvedMemberLines[memberIndex] || null;

                                const memberDuration =
                                  Number(memberResolvedLine?.totalMinutes) ||
                                  Number(member.serviceDuration) ||
                                  normalizeServiceDuration(memberService) ||
                                  normalizeServiceDuration(service) ||
                                  0;

                                // Member #1 starts when the PRIMARY CUSTOMER is
                                // finished - and that customer's appointment is the
                                // whole bundle: their service PLUS its add-ons
                                // (everyone arrives together, so an add-on runs in
                                // the same sitting). This must therefore be the
                                // resolved BUNDLE total.
                                //   - the primary's own line alone ignores the
                                //     add-ons (member #1 was offered the add-on's
                                //     window, i.e. start + 30 instead of start + 60)
                                //   - the catalog `bundleDurationMinutes` sum is
                                //     wrong as soon as any line is per-unit /
                                //     hourly / option-based
                                const primaryDuration =
                                  Number(resolvedBundleLines.totalMinutes) ||
                                  Number(resolvedTotalMinutes) ||
                                  Number(
                                    service?.duration ||
                                      service?.durationMinutes,
                                  ) ||
                                  0;

                                // Compute the exact earliest start for this member:
                                // — For member #0: primary end time (primaryStart + primaryDuration)
                                // — For member #N: previous member's end time (prevStart + prevDuration)
                                let exactEarliestMinutes = null;
                                if (
                                  memberIndex === 0 &&
                                  selectedSlot?.time24 &&
                                  primaryDuration
                                ) {
                                  exactEarliestMinutes =
                                    timeToMinutes(selectedSlot.time24) +
                                    primaryDuration;
                                } else if (memberIndex > 0) {
                                  const prev = groupMembers[memberIndex - 1];
                                  const prevResolvedLine =
                                    resolvedMemberLines[memberIndex - 1] || null;
                                  const previousDuration =
                                    Number(prevResolvedLine?.totalMinutes) ||
                                    Number(prev?.serviceDuration) ||
                                    0;
                                  if (
                                    prev?.preferredTime &&
                                    previousDuration > 0
                                  ) {
                                    exactEarliestMinutes =
                                      timeToMinutes(prev.preferredTime) +
                                      previousDuration;
                                  }
                                }

                                const getSlotEndMinutes = (slot) => {
                                  const slotStart = timeToMinutes(
                                    slot?.time24 || slot?.start,
                                  );
                                  if (!Number.isFinite(slotStart)) {
                                    return null;
                                  }

                                  const explicitEnd = slot?.endTime || slot?.end;
                                  if (explicitEnd) {
                                    const parsedEnd = timeToMinutes(explicitEnd);
                                    return Number.isFinite(parsedEnd)
                                      ? parsedEnd
                                      : null;
                                  }

                                  const slotDuration =
                                    Number(slot?.durationMinutes) ||
                                    Number(slot?.duration) ||
                                    primaryDuration ||
                                    0;

                                  if (!slotDuration) {
                                    return null;
                                  }

                                  return slotStart + slotDuration;
                                };

                                const availableEndMinutes =
                                  availableDayClosingTime
                                    ? timeToMinutes(availableDayClosingTime)
                                    : Array.isArray(availableSlots) &&
                                        availableSlots.length > 0
                                      ? (() => {
                                          const latestEnd = availableSlots.reduce(
                                            (latest, slot) => {
                                              const slotEnd = getSlotEndMinutes(slot);
                                              return Number.isFinite(slotEnd)
                                                ? Math.max(latest, slotEnd)
                                                : latest;
                                            },
                                            Number.NEGATIVE_INFINITY,
                                          );
                                          return Number.isFinite(latestEnd)
                                            ? latestEnd
                                            : null;
                                        })()
                                      : null;

                                if (
                                  exactEarliestMinutes === null ||
                                  availableEndMinutes === null
                                ) {
                                  return [];
                                }

                                // Get all already-selected times
                                const selectedTimes = [
                                  selectedSlot?.time24, // Primary customer
                                  ...groupMembers
                                    .filter(
                                      (m) =>
                                        m.id !== member.id && m.preferredTime,
                                    )
                                    .map((m) => m.preferredTime),
                                ].filter(Boolean);
                                // Collect ALL occupied time ranges (primary + all other members)
                                const occupiedRanges = [];

                                // Primary customer's time range
                                if (selectedSlot?.time24 && primaryDuration) {
                                  const [hours, mins] = selectedSlot.time24
                                    .split(":")
                                    .map(Number);
                                  const startMinutes = hours * 60 + mins;
                                  const endMinutes =
                                    startMinutes + primaryDuration;
                                  occupiedRanges.push({
                                    start: startMinutes,
                                    end: endMinutes,
                                    name: "Primary",
                                  });
                                }

                                // Other group members' time ranges
                                groupMembers.forEach((m) => {
                                  const memberRangeDuration = Number(
                                    m?.serviceDuration,
                                  );
                                  if (
                                    m.id !== member.id &&
                                    m.preferredTime &&
                                    memberRangeDuration > 0
                                  ) {
                                    const [hours, mins] = m.preferredTime
                                      .split(":")
                                      .map(Number);
                                    const startMinutes = hours * 60 + mins;
                                    const endMinutes =
                                      startMinutes + memberRangeDuration;
                                    occupiedRanges.push({
                                      start: startMinutes,
                                      end: endMinutes,
                                      name: `Member ${m.id}`,
                                    });
                                  }
                                });

                                const finalSlots = [];
                                const slotStartMinutes = exactEarliestMinutes;
                                const slotEndMinutes =
                                  exactEarliestMinutes + memberDuration;
                                const time24 = minutesToTime24(slotStartMinutes);

                                const canFitBeforeClose =
                                  memberDuration > 0 &&
                                  slotEndMinutes <= availableEndMinutes;

                                if (
                                  canFitBeforeClose &&
                                  !selectedTimes.includes(time24)
                                ) {
                                  let overlaps = false;
                                  for (const range of occupiedRanges) {
                                    if (
                                      slotStartMinutes < range.end &&
                                      slotEndMinutes > range.start
                                    ) {
                                      overlaps = true;
                                      break;
                                    }
                                  }

                                  if (!overlaps) {
                                    finalSlots.push({
                                      time24,
                                      time12: formatTime12(slotStartMinutes),
                                      available: true,
                                    });
                                  }
                                }

                                if (finalSlots.length === 0) {
                                  return [
                                    {
                                      __empty: true,
                                      message:
                                        availableSlots.length === 0
                                          ? "Loading time slots..."
                                          : "Not enough time left before closing to fit this service. Choose another day, an earlier time or a shorter service.",
                                    },
                                  ];
                                }

                                return finalSlots;
                              })().map((slot, idx) => {
                                if (slot?.__empty) {
                                  return (
                                    <div
                                      key={`empty-${idx}`}
                                      style={{
                                        gridColumn: "1 / -1",
                                        padding: "14px 16px",
                                        textAlign: "center",
                                        color: "#374151",
                                        background: "#f8fafc",
                                        border: "1px dashed #cbd5e1",
                                        borderRadius: "10px",
                                        marginTop: "6px",
                                        boxSizing: "border-box",
                                      }}
                                    >
                                      {slot.message}
                                    </div>
                                  );
                                }

                                return (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => {
                                      const changedIndex =
                                        groupMembers.findIndex(
                                          (m) => m.id === member.id,
                                        );
                                      const newMembers =
                                        clearDownstreamMemberTimes(
                                          groupMembers.map((m) =>
                                            m.id === member.id
                                              ? {
                                                  ...m,
                                                  preferredTime: slot.time24,
                                                  preferredTime12:
                                                    slot.time12 || slot.time24,
                                                }
                                              : m,
                                          ),
                                          changedIndex,
                                        );
                                      setGroupMembers(newMembers);
                                    }}
                                    style={{
                                      padding: "10px 8px",
                                      background:
                                        member.preferredTime === slot.time24
                                          ? "#dc2626"
                                          : "white",
                                      color:
                                        member.preferredTime === slot.time24
                                          ? "white"
                                          : "#374151",
                                      border: `2px solid ${member.preferredTime === slot.time24 ? "#dc2626" : "#e5e7eb"}`,
                                      borderRadius: "8px",
                                      cursor: "pointer",
                                      fontSize: "13px",
                                      fontWeight:
                                        member.preferredTime === slot.time24
                                          ? "600"
                                          : "500",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    {slot.time12 || slot.time24}
                                  </button>
                                );
                              })}
                            </div>
                            {member.preferredTime && (
                              <div
                                style={{
                                  marginTop: "8px",
                                  padding: "8px",
                                  background: "#f0fdf4",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  color: "#166534",
                                }}
                              >
                                ✓ Selected:{" "}
                                {member.preferredTime12 || member.preferredTime}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {groupMembers.length < MAX_ADDITIONAL_GROUP_MEMBERS && (
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          groupMembers.length >=
                          MAX_ADDITIONAL_GROUP_MEMBERS
                        ) {
                          return;
                        }

                        const newMember = {
                          id: Date.now(),
                          name: "",
                          serviceId: "",
                          serviceName: "",
                          serviceDuration: null,
                          // P5: the member's own units / hours / option (see above).
                          selection: {},
                        };
                        setGroupMembers([...groupMembers, newMember]);
                      }}
                      style={{
                        width: "100%",
                        padding: "12px",
                        background: "white",
                        color: "#dc2626",
                        border: "2px dashed #dc2626",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        transition: "all 0.2s",
                      }}
                    >
                      ➕ Add Another Member
                    </button>
                  )}
                  {groupMembers.length >= MAX_ADDITIONAL_GROUP_MEMBERS && (
                    <div
                      style={{
                        marginTop: "10px",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                        background: "#f8fafc",
                        color: "#475569",
                        fontSize: "12px",
                        fontWeight: "600",
                        textAlign: "center",
                      }}
                    >
                      Maximum reached: up to 2 additional group members.
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowGroupForm(false);
                      setGroupMembers([]);
                      setGroupSize(1);
                    }}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: "white",
                      color: "#6b7280",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!groupMembersComplete) {
                        alert(
                          "Please complete all member details including name, service, and preferred time",
                        );
                        return;
                      }
                      setGroupSize(groupMembers.length + 1);
                      setShowGroupForm(false);
                    }}
                    disabled={!groupMembersComplete}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: groupMembersComplete ? "#dc2626" : "#e5e7eb",
                      color: groupMembersComplete ? "white" : "#9ca3af",
                      border: "none",
                      borderRadius: "8px",
                      cursor: groupMembersComplete ? "pointer" : "not-allowed",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Confirm Group ({groupMembers.length} members)
                  </button>
                </div>
              </div>
            )}

            {groupSize > 1 && !showGroupForm && (
              <>
                <div
                  style={{
                    background: "#fef3c7",
                    border: "1px solid #fbbf24",
                    borderRadius: "8px",
                    padding: "12px",
                    marginBottom: "16px",
                    fontSize: "14px",
                  }}
                >
                  ⚠️ <strong>One payment for entire group.</strong> No split
                  payment available. Products purchased apply to entire group.
                </div>

                <div className="group-members" style={{ marginTop: "16px" }}>
                  <h4
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Group Members:
                  </h4>
                  {groupMembers.map((member, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: "12px",
                        background: "#f3f4f6",
                        borderRadius: "6px",
                        marginBottom: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "4px",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: "600", fontSize: "13px" }}>
                            {member.name || `Person ${idx + 2}`}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              marginTop: "2px",
                            }}
                          >
                            {member.serviceName}
                          </div>
                        </div>
                        {member.preferredTime12 && (
                          <div
                            style={{
                              padding: "4px 10px",
                              background: "#dc2626",
                              color: "white",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "600",
                            }}
                          >
                            🕐 {member.preferredTime12}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setShowGroupForm(true)}
                    style={{
                      padding: "8px 16px",
                      background: "white",
                      color: "#dc2626",
                      border: "1px solid #dc2626",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: "600",
                      marginTop: "8px",
                    }}
                  >
                    Edit Group Members
                  </button>
                </div>
              </>
            )}
            <div style={{ marginTop: "14px", display: "flex", justifyContent: "space-between", gap: "10px" }}>
              <button
                type="button"
                style={nextButtonStyle}
                onClick={() => goToPreviousSection("group")}
              >
                Back
              </button>
              <button
                type="button"
                style={{
                  ...nextButtonStyle,
                  opacity: groupCanProceed ? 1 : 0.6,
                  cursor: groupCanProceed ? "pointer" : "not-allowed",
                }}
                disabled={!groupCanProceed}
                onClick={() => {
                  if (!groupCanProceed) {
                    return;
                  }
                  if (showGroupForm && groupSize === 1) {
                    setShowGroupForm(false);
                    setGroupMembers([]);
                  }
                  advanceToNextSection("group");
                }}
              >
                Next
              </button>
            </div>
          </section>

          {/* Recurring Booking (Phase E) - TEMPORARILY HIDDEN */}
          {false && isAuthenticated && selectedDate && selectedSlot && (
            <section className="form-section">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <h3 style={{ margin: 0 }}>Recurring Appointments</h3>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: "600" }}>
                    Make this recurring
                  </span>
                </label>
              </div>

              {isRecurring && (
                <div
                  style={{
                    padding: "20px",
                    background: "#f9fafb",
                    borderRadius: "12px",
                    border: "2px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      background: "#dbeafe",
                      border: "1px solid #3b82f6",
                      borderRadius: "8px",
                      padding: "12px",
                      marginBottom: "20px",
                      fontSize: "14px",
                      color: "#1e40af",
                    }}
                  >
                    ℹ️ <strong>Note:</strong> Recurring bookings are
                    pre-authorized and confirmed. You'll be charged per
                    appointment. Payment for all future appointments will be
                    processed individually.
                  </div>

                  <div style={{ display: "grid", gap: "16px" }}>
                    {/* Frequency */}
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "600",
                          marginBottom: "8px",
                          color: "#374151",
                        }}
                      >
                        Repeat Every
                      </label>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {recurringFrequency !== "biweekly" && (
                          <input
                            type="number"
                            min="1"
                            max="12"
                            value={recurringInterval}
                            onChange={(e) =>
                              setRecurringInterval(
                                Math.max(1, parseInt(e.target.value) || 1),
                              )
                            }
                            style={{
                              width: "70px",
                              padding: "10px",
                              border: "2px solid #e5e7eb",
                              borderRadius: "8px",
                              fontSize: "14px",
                            }}
                          />
                        )}
                        <select
                          value={recurringFrequency}
                          onChange={(e) => {
                            setRecurringFrequency(e.target.value);
                            if (e.target.value === "biweekly") {
                              setRecurringInterval(2);
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: "10px",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "500",
                            cursor: "pointer",
                            minWidth: "150px",
                          }}
                        >
                          <option value="daily">Day(s)</option>
                          <option value="weekly">Week(s)</option>
                          <option value="biweekly">Every 2 Weeks</option>
                          <option value="monthly">Month(s)</option>
                        </select>
                      </div>
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "13px",
                          color: "#6b7280",
                        }}
                      >
                        {recurringFrequency === "daily" &&
                          `Every ${recurringInterval} ${recurringInterval === 1 ? "day" : "days"}`}
                        {recurringFrequency === "weekly" &&
                          `Every ${recurringInterval} ${recurringInterval === 1 ? "week" : "weeks"} on ${new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long" })}`}
                        {recurringFrequency === "biweekly" &&
                          `Every 2 weeks on ${new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long" })}`}
                        {recurringFrequency === "monthly" &&
                          `Every ${recurringInterval} ${recurringInterval === 1 ? "month" : "months"} on the ${new Date(selectedDate + "T00:00:00").getDate()}${["th", "st", "nd", "rd"][new Date(selectedDate + "T00:00:00").getDate() % 10] || "th"}`}
                      </div>
                    </div>

                    {/* End Condition */}
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "600",
                          marginBottom: "8px",
                          color: "#374151",
                        }}
                      >
                        End Condition
                      </label>
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          marginBottom: "12px",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          <input
                            type="radio"
                            value="occurrences"
                            checked={recurringEndType === "occurrences"}
                            onChange={(e) =>
                              setRecurringEndType(e.target.value)
                            }
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                          <span style={{ fontSize: "14px" }}>
                            Number of appointments
                          </span>
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          <input
                            type="radio"
                            value="date"
                            checked={recurringEndType === "date"}
                            onChange={(e) =>
                              setRecurringEndType(e.target.value)
                            }
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                          <span style={{ fontSize: "14px" }}>End date</span>
                        </label>
                      </div>

                      {recurringEndType === "occurrences" ? (
                        <input
                          type="number"
                          min="1"
                          max="52"
                          value={recurringOccurrences}
                          onChange={(e) =>
                            setRecurringOccurrences(
                              Math.max(
                                1,
                                Math.min(52, parseInt(e.target.value) || 1),
                              ),
                            )
                          }
                          style={{
                            width: "120px",
                            padding: "10px",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "14px",
                          }}
                          placeholder="Max 52"
                        />
                      ) : (
                        <input
                          type="date"
                          value={recurringEndDate}
                          onChange={(e) => setRecurringEndDate(e.target.value)}
                          min={selectedDate}
                          style={{
                            padding: "10px",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "14px",
                          }}
                        />
                      )}
                    </div>

                    {/* Summary */}
                    <div
                      style={{
                        padding: "16px",
                        background: "white",
                        borderRadius: "8px",
                        border: "2px solid #10b981",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#059669",
                          marginBottom: "8px",
                        }}
                      >
                        📅 Booking Summary
                      </div>
                      <div style={{ fontSize: "14px", color: "#374151" }}>
                        <strong>
                          {recurringEndType === "occurrences"
                            ? recurringOccurrences
                            : "Multiple"}
                        </strong>{" "}
                        appointments starting{" "}
                        <strong>
                          {new Date(
                            selectedDate + "T00:00:00",
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </strong>{" "}
                        at <strong>{selectedSlot.time12}</strong>
                        {recurringEndType === "date" && recurringEndDate && (
                          <>
                            {" "}
                            until{" "}
                            <strong>
                              {new Date(
                                recurringEndDate + "T00:00:00",
                              ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </strong>
                          </>
                        )}
                      </div>
                      <div
                        style={{
                          marginTop: "12px",
                          fontSize: "13px",
                          color: "#6b7280",
                        }}
                      >
                        Total cost:{" "}
                        <strong style={{ color: "#059669" }}>
                          $
                          {(
                            (service?.price || 0) *
                            (recurringEndType === "occurrences"
                              ? recurringOccurrences
                              : 10)
                          ).toFixed(2)}
                        </strong>
                        {recurringEndType !== "occurrences" &&
                          " (estimated for 10 appointments)"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Recommended Products - Hidden in preview mode */}
          {!isPreviewMode &&
            recommendedProducts &&
            recommendedProducts.length > 0 && (
              <section
                className="form-section"
                ref={productsSectionRef}
                style={{
                  ...getSectionBlockStyle(
                    !isBillingAddressRequired(selectedProducts)
                      ? "optional"
                      : billingAddressComplete
                        ? "complete"
                        : "attention",
                  ),
                  ...getCollapsibleSectionStyle(expandedSection, "products"),
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "16px",
                    paddingBottom: "12px",
                    borderBottom: "2px solid #e5e7eb",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      color: "#1e40af",
                      fontSize: "14px",
                      fontWeight: "600",
                      maxWidth: "80%",
                    }}
                  >
                    {[
                      `Take home what your stylist uses — picked for your ${service?.name}`,
                      `Add now, collect at your appointment — no extra trip needed`,
                      `Customers who booked ${service?.name} also love these`,
                      `Handpicked by your stylist to extend your results at home`,
                      `Complete your look — curated for your ${service?.name} visit`,
                    ][productPitchIndex]}
                  </p>
                </div>
                <div
                  style={{
                    marginBottom: "16px",
                    textAlign: "right",
                  }}
                >
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#9ca3af",
                    }}
                  >
                    Swipe to see more →
                  </span>
                </div>

                {/* Horizontal Scrolling Container */}
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    overflowX: "auto",
                    overflowY: "hidden",
                    paddingBottom: "16px",
                    marginBottom: "16px",
                    scrollSnapType: "x mandatory",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#cbd5e1 #f1f5f9",
                  }}
                >
                  {recommendedProducts.slice(0, 10).map((product) => {
                    const hasSelectableVariants =
                      product?.hasVariants === true ||
                      Number(product?.variantCount) > 1 ||
                      (Array.isArray(product?.variants) &&
                        product.variants.length > 1);
                    const selected = selectedProducts.find(
                      (p) =>
                        p.productId === product._id ||
                        p.familyProductId === product._id,
                    );
                    const displayedProductPrice =
                      typeof selected?.price === "number"
                        ? selected.price
                        : typeof product.price === "number"
                          ? product.price
                          : Number(product.masterProductId?.basePrice) || 0;

                    return (
                      <div
                        key={product._id}
                        style={{
                          minWidth: "200px",
                          maxWidth: "200px",
                          background: selected ? "#f0fdf4" : "white",
                          border: selected
                            ? "2px solid #10b981"
                            : "2px solid #e5e7eb",
                          borderRadius: "12px",
                          padding: "12px",
                          scrollSnapAlign: "start",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.2s",
                          position: "relative",
                        }}
                      >
                        {/* Quick View Button Overlay */}
                        <div style={{ position: "relative" }}>
                          {product.images?.[0] && (
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              style={{
                                width: "100%",
                                height: "140px",
                                objectFit: "cover",
                                borderRadius: "8px",
                                marginBottom: "12px",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setQuickViewProduct(product);
                                setShowQuickViewModal(true);
                              }}
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setQuickViewProduct(product);
                              setShowQuickViewModal(true);
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              background: "rgba(255, 255, 255, 0.95)",
                              border: "1px solid #e5e7eb",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              cursor: "pointer",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#374151",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "white";
                              e.target.style.transform = "scale(1.05)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background =
                                "rgba(255, 255, 255, 0.95)";
                              e.target.style.transform = "scale(1)";
                            }}
                          >
                            👁️ Quick View
                          </button>
                        </div>
                        <h4
                          style={{
                            margin: "0 0 8px 0",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#374151",
                            lineHeight: "1.3",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setQuickViewProduct(product);
                            setShowQuickViewModal(true);
                          }}
                        >
                          {product.name}
                        </h4>
                        <p
                          style={{
                            margin: "0 0 12px 0",
                            fontSize: "14px",
                            fontWeight: "700",
                            color: "#2d6da3",
                          }}
                        >
                          ${displayedProductPrice.toFixed(2)}
                        </p>
                        {!selected ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (hasSelectableVariants) {
                                setQuickViewProduct(product);
                                setShowQuickViewModal(true);
                                return;
                              }
                              toggleProduct(product);
                            }}
                            style={{
                              padding: "8px 12px",
                              background: "#2d6da3",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontWeight: "600",
                              fontSize: "13px",
                              marginTop: "auto",
                            }}
                          >
                            {hasSelectableVariants ? "Choose Option" : "☐ Add"}
                          </button>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginTop: "auto",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                updateProductQuantity(
                                  selected.productId,
                                  selected.quantity - 1,
                                )
                              }
                              style={{
                                width: "28px",
                                height: "28px",
                                background: "#f3f4f6",
                                border: "1px solid #d1d5db",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "16px",
                                fontWeight: "bold",
                              }}
                            >
                              −
                            </button>
                            <span
                              style={{
                                flex: 1,
                                textAlign: "center",
                                fontWeight: "600",
                                fontSize: "14px",
                              }}
                            >
                              {selected.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                updateProductQuantity(
                                  selected.productId,
                                  selected.quantity + 1,
                                )
                              }
                              style={{
                                width: "28px",
                                height: "28px",
                                background: "#f3f4f6",
                                border: "1px solid #d1d5db",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "16px",
                                fontWeight: "bold",
                              }}
                            >
                              +
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedProducts((previous) =>
                                  previous.filter(
                                    (entry) =>
                                      entry.productId !== selected.productId,
                                  ),
                                )
                              }
                              style={{
                                padding: "6px 10px",
                                background: "#fee2e2",
                                color: "#dc2626",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {isBillingAddressRequired(selectedProducts) && !billingAddressComplete && (
                  <p style={{ margin: "0 0 10px 0", color: "#92400e", fontSize: "12px" }}>
                    Needs attention: billing address is required when products are selected.
                  </p>
                )}
                {isBillingAddressRequired(selectedProducts) && (
                  <div
                    ref={billingAddressSectionRef}
                    style={{
                      marginBottom: "16px",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #bfdbfe",
                      background: "#eff6ff",
                    }}
                  >
                    <h4
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "13px",
                        fontWeight: "700",
                        color: "#1d4ed8",
                      }}
                    >
                      Billing Address (Required for Product Tax)
                    </h4>
                    {shouldShowBillingAddressReturnPrompt(billingAddressReturnTarget) && (
                      <div
                        style={{
                          marginBottom: "12px",
                          padding: "10px 12px",
                          borderRadius: "6px",
                          background: "#dbeafe",
                          border: "1px solid #bfdbfe",
                          color: "#1d4ed8",
                          fontSize: "12px",
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "12px",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <span>Save your address here, then continue from the summary.</span>
                        <button
                          type="button"
                          onClick={() =>
                            summarySectionRef.current?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            })
                          }
                          style={{
                            padding: "8px 10px",
                            background: "#bfdbfe",
                            color: "#1d4ed8",
                            border: "1px solid #93c5fd",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "600",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Back to Summary
                        </button>
                      </div>
                    )}
                    <div className="form-grid">
                      <AddressAutocompleteInput
                        ref={billingStreetInputRef}
                        value={customerAddress.street}
                        onChange={(val) =>
                          setCustomerAddress({ ...customerAddress, street: val })
                        }
                        onAddressSelect={({ street, city, state, zip }) =>
                          setCustomerAddress((prev) => ({
                            ...prev,
                            street: street || prev.street,
                            city: city || prev.city,
                            state: state || prev.state,
                            zipCode: zip || prev.zipCode,
                          }))
                        }
                        placeholder="Street Address"
                        required
                      />
                      <input
                        type="text"
                        placeholder="City"
                        value={customerAddress.city}
                        onChange={(e) =>
                          setCustomerAddress({
                            ...customerAddress,
                            city: e.target.value,
                          })
                        }
                        required
                      />
                      <input
                        type="text"
                        placeholder="State"
                        value={customerAddress.state}
                        onChange={(e) =>
                          setCustomerAddress({
                            ...customerAddress,
                            state: e.target.value,
                          })
                        }
                        required
                      />
                      <input
                        type="text"
                        placeholder="ZIP Code"
                        value={customerAddress.zipCode}
                        onChange={(e) =>
                          setCustomerAddress({
                            ...customerAddress,
                            zipCode: e.target.value,
                          })
                        }
                        required
                      />
                      <input
                        type="text"
                        placeholder="Country (e.g. US)"
                        value={customerAddress.country}
                        onChange={(e) =>
                          setCustomerAddress({
                            ...customerAddress,
                            country: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                )}

                <div
                  style={{
                    background: "#eff6ff",
                    border: "1px solid #3b82f6",
                    borderRadius: "8px",
                    padding: "12px",
                    fontSize: "13px",
                    color: "#1e40af",
                  }}
                >
                  💡 Products available for pickup at your appointment
                </div>
                <div style={{ marginTop: "14px", display: "flex", justifyContent: "space-between", gap: "10px" }}>
                  <button
                    type="button"
                    style={nextButtonStyle}
                    onClick={() => goToPreviousSection("products")}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    style={{
                      ...nextButtonStyle,
                      opacity: billingAddressComplete ? 1 : 0.6,
                      cursor: billingAddressComplete ? "pointer" : "not-allowed",
                    }}
                    disabled={!billingAddressComplete}
                    onClick={() => advanceToNextSection("products")}
                  >
                    Next
                  </button>
                </div>
              </section>
            )}

          {/* Package Redemption. The section opens for what is SHOWN: the packages that can pay for this
              booking, the ones that cannot and say why, and the ones for sale. */}
          {showPackageSection && (
            <section
              className="form-section"
              // THE TILE AT THE TOP POINTS HERE (user, 2026-10-16), and a `div` reading "See below ⬇️" was not a
              // link: it did nothing when clicked, so "see below" meant "go and find it yourself". An id is what
              // makes the tile's button able to actually take them there.
              id="packages-for-this-service"
              style={{
                background: isAuthenticated ? "#f0fdf4" : "#eff6ff",
                border: isAuthenticated
                  ? "2px solid #10b981"
                  : "2px solid #3b82f6",
                borderRadius: "12px",
                padding: "20px",
              }}
            >
              {/* THE SECTION NO LONGER HAS A HEADING OF ITS OWN (user, 2026-10-19: *"remove all the info"* about
                  the section title and the sign-up reassurance). The groups below are titled separately —
                  "🎁 Your packages" and "💎 Packages you can buy" — so a heading here either repeated one of them
                  or, on the summary, repeated the payment step's own. */}

              {/* TWO PER ROW, AND NEVER MORE (user, 2026-10-09). One card per row meant a customer holding a
                  few packages scrolled past several screens of them before reaching the buttons that matter.
                  Two is the most that still fits a package's name, its remaining sessions and its expiry
                  without wrapping, and `minmax(0, 1fr)` stops a long name widening its own column and
                  pushing the card out of the panel — the reason `repeat(2, 1fr)` alone would have looked
                  right here and overflowed in production. On a narrow screen it falls back to one, because
                  two columns at phone width is not a card, it is a column of broken words. */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(2, minmax(0, 1fr))",
                  gap: "12px",
                  alignItems: "start",
                }}
              >
                {/* ── OWNED, AND FOR SALE, ARE TWO GROUPS AND MUST LOOK LIKE TWO GROUPS ──────────────

                    Both lists sat in ONE grid under ONE heading, so a card the customer had already paid
                    for and a card asking for their card sat side by side looking identical (reported
                    2026-10-11: *"can we clearly differentiate between packages owned by customer/guest and
                    packages available for purchase, because they're all lumped together"*).

                    `gridColumn: "1 / -1"` lets a heading span both columns inside the existing grid, so the
                    grouping costs no extra layout and the two-per-row rule above still holds. The for-sale
                    group also carries a divider, because a heading alone would not survive a scroll. */}
                {(ownedPackagesHere.length > 0 || ownedPackagesBlocked.length > 0) && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <h4 style={{ margin: 0, fontSize: isMobile ? "14px" : "15px", fontWeight: "700", color: "#065f46" }}>
                      🎁 Your packages
                    </h4>
                    <p style={{ margin: "2px 0 0 0", fontSize: "13px", color: "#6b7280" }}>
                      Already paid for — no price and no card needed. A card marked
                      "Not eligible" cannot be used for this particular booking.
                    </p>
                  </div>
                )}

                {/* Phase 18 — the OWNED packages, for a GUEST as well as a signed-in customer. This was
                    gated on `isAuthenticated`, so a guest who proved a purchase with the email + phone
                    challenge was shown nothing to select: verification succeeded, the page did not
                    change, and the booking could never be paid with the package they owned. */}
                {ownedPackagesHere.map((pkg) => {
                    // BOTH must hold: the people the package can cover must fit its LINES (22D) and its total
                    // sessions. Since split payment (2026-10-27) `creditsNeeded` is how many people the package
                    // actually pays for — the rest of the party is charged to the card — so a party of two with
                    // one session left is still selectable.
                    const partyCheck = packagePartyCheck(pkg);
                    const coveredPeople =
                      Number(partyCheck.coveredCount) ||
                      Math.max(1, 1 + (partyCheck.coveredMemberIndexes || []).length);
                    const creditsNeeded = Math.min(coveredPeople, groupSize);
                    const hasEnoughCredits =
                      partyCheck.ok && creditsNeeded <= (Number(pkg.sessionsRemaining) || 0);
                    const daysUntilExpiry = Math.ceil(
                      (new Date(pkg.validUntil) - new Date()) /
                        (1000 * 60 * 60 * 24),
                    );
                    const isExpiringSoon = daysUntilExpiry < 60;
                    // What this package saved them, from its own frozen numbers (`packageSavingAmount`).
                    const pkgSaving = packageSavingAmount(pkg);

                    return (
                      <div
                        key={pkg._id}
                        style={{
                          background:
                            selectedPackage?._id === pkg._id
                              ? "#d1fae5"
                              : "white",
                          border:
                            selectedPackage?._id === pkg._id
                              ? "2px solid #10b981"
                              : "1px solid #d1d5db",
                          borderRadius: "8px",
                          padding: "16px",
                          cursor: hasEnoughCredits ? "pointer" : "not-allowed",
                          opacity: hasEnoughCredits ? 1 : 0.6,
                        }}
                        onClick={() =>
                          hasEnoughCredits && handlePackageSelection(pkg)
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "8px",
                          }}
                        >
                          <div>
                            <h4
                              style={{
                                margin: "0 0 4px 0",
                                fontSize: "14px",
                                fontWeight: "600",
                              }}
                            >
                              {hasEnoughCredits ? "✅" : "☐"}{" "}
                              {pkg.packageSnapshot?.name || "Package"}
                            </h4>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#6b7280",
                              }}
                            >
                              {pkg.sessionsRemaining} session
                              {pkg.sessionsRemaining !== 1 ? "s" : ""} remaining
                            </p>

                            {/* THE SAVING, ON THE TILE (plan item 6). Only when the purchase's own snapshot can
                                state it — never an estimate. */}
                            {pkgSaving !== null && (
                              <p
                                style={{
                                  margin: "4px 0 0 0",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "#047857",
                                }}
                              >
                                💰 You saved ${pkgSaving.toFixed(2)} on this package
                              </p>
                            )}

                            {/* 22C — WHAT IS LEFT, PER SERVICE (plan §7.6, "shown per line to the customer").

                                ONLY WHEN THERE IS MORE THAN ONE LINE. On a single-service package "1 session
                                remaining" already IS the per-line number, and repeating it as "1 of 1 Women's
                                Haircut" is noise. On a mixed package it is the entire question: "3 sessions
                                remaining" does not tell the customer whether those three are the cuts they want or
                                the colours they do not. The numbers come from the server, which is also what
                                REFUSES the booking, so the screen and the refusal cannot disagree.

                                AND ONLY WHEN THE LINES AGREE WITH THE TOTAL (`linesMatchTotal`). A purchase
                                redeemed before 22C shipped has sessions its counters do not record, and the
                                breakdown then reads as "0 sessions remaining" beside "1 of 1 left" — the system
                                apparently reversing what is spent and what is left (reported 2026-10-11). The
                                total comes from the claim, so it is the figure to show; a breakdown that
                                contradicts it is withheld rather than presented as fact. */}
                            {pkg.quota?.applies &&
                              pkg.quota.linesMatchTotal !== false &&
                              pkg.quota.lines.length > 1 && (
                              <ul
                                style={{
                                  margin: "6px 0 0 0",
                                  paddingLeft: "18px",
                                  fontSize: "12px",
                                  color: "#4b5563",
                                }}
                              >
                                {pkg.quota.lines.map((line) => (
                                  <li
                                    key={line.serviceId || line.name}
                                    style={{
                                      color:
                                        line.remaining > 0 ? "#4b5563" : "#b91c1c",
                                    }}
                                  >
                                    {line.name || "Service"}: {line.remaining} of{" "}
                                    {line.quantity} left
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          {selectedPackage?._id === pkg._id && (
                            <span
                              style={{
                                background: "#10b981",
                                color: "white",
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              SELECTED
                            </span>
                          )}
                        </div>

                        <p
                          style={{
                            margin: "8px 0 0 0",
                            fontSize: "13px",
                            color: isExpiringSoon ? "#dc2626" : "#6b7280",
                          }}
                        >
                          {isExpiringSoon && "⚠️ "}Expires:{" "}
                          {new Date(pkg.validUntil).toLocaleDateString()}
                          {isExpiringSoon && ` (${daysUntilExpiry} days left)`}
                        </p>

                        {groupSize > 1 && (
                          <p
                            style={{
                              margin: "8px 0 0 0",
                              fontSize: "13px",
                              color: hasEnoughCredits ? "#10b981" : "#dc2626",
                              fontWeight: "600",
                            }}
                          >
                            {!partyCheck.ok
                              ? `❌ ${partyCheck.message}`
                              : `Will use ${creditsNeeded} credit${creditsNeeded === 1 ? "" : "s"} (${Math.max(0, (Number(pkg.sessionsRemaining) || 0) - creditsNeeded)} remaining after)${(partyCheck.cardMemberIndexes || []).length > 0 ? ` — the other ${(partyCheck.cardMemberIndexes || []).length} ${(partyCheck.cardMemberIndexes || []).length === 1 ? "person is" : "people are"} paid by card` : ""}`}
                          </p>
                        )}

                        {/* A single-person booking used to show nothing here, and the customer found out at
                            submit. 22D answers per LINE, so the same question now has an answer for one person
                            too — and 22C's per-line list above is the evidence for it. */}
                        {groupSize === 1 && !partyCheck.ok && (
                          <p
                            style={{
                              margin: "8px 0 0 0",
                              fontSize: "13px",
                              color: "#dc2626",
                              fontWeight: "600",
                            }}
                          >
                            ❌ {partyCheck.message}
                          </p>
                        )}
                      </div>
                    );
                  })}

                {/* OWNED, BUT NOT USABLE FOR THIS BOOKING — GREYED, WITH THE REASON (user, 2026-10-11:
                    "the Mother & Daughter's Day package is not showing as parts of the package the
                    customer/guest owns even though it can't pay for the booking. At least let it show among
                    the packages owned").

                    A package the customer paid for that simply is not on the page reads as a package that is
                    GONE. It is here instead, unselectable — no `onClick`, no cursor change, and no
                    "Use my package" anywhere near it, so there is nothing to press and nothing to mislead.
                    The per-line figures stay, because on this card they are the explanation: "Women's
                    Haircut: 0 of 2 left" IS why it cannot pay. */}
                {ownedPackagesBlocked.map(({ key, name, sessionsRemaining, validUntil, quota, reason }) => (
                  <div
                    key={`blocked-${key}`}
                    style={{
                      background: "#f8fafc",
                      border: "1px dashed #cbd5e1",
                      borderRadius: "8px",
                      padding: "16px",
                      opacity: 0.85,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "8px",
                      }}
                    >
                      <div>
                        <h4
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#475569",
                          }}
                        >
                          🚫 {name}
                        </h4>
                        <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
                          {sessionsRemaining} session
                          {sessionsRemaining !== 1 ? "s" : ""} remaining
                        </p>

                        {quota?.applies &&
                          quota.linesMatchTotal !== false &&
                          quota.lines.length > 1 && (
                            <ul
                              style={{
                                margin: "6px 0 0 0",
                                paddingLeft: "18px",
                                fontSize: "12px",
                                color: "#4b5563",
                              }}
                            >
                              {quota.lines.map((line) => (
                                <li
                                  key={line.serviceId || line.name}
                                  style={{
                                    color: line.remaining > 0 ? "#4b5563" : "#b91c1c",
                                  }}
                                >
                                  {line.name || "Service"}: {line.remaining} of{" "}
                                  {line.quantity} left
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>
                      <span
                        style={{
                          background: "#e2e8f0",
                          color: "#475569",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Not eligible
                      </span>
                    </div>

                    {validUntil && (
                      <p style={{ margin: "8px 0 0 0", fontSize: "13px", color: "#6b7280" }}>
                        Expires: {new Date(validUntil).toLocaleDateString()}
                      </p>
                    )}

                    <p
                      style={{
                        margin: "8px 0 0 0",
                        fontSize: "13px",
                        color: "#92400e",
                        fontWeight: "600",
                        lineHeight: 1.5,
                      }}
                    >
                      {reason}
                    </p>
                  </div>
                ))}

                {/* ── WHAT IS ON OFFER IS TOLD ON EVERY STEP, AND SOLD AT THE PAYMENT STEP ───────────────────
                    The full for-sale cards moved down beside the Pay button (user, 2026-10-19), which left this
                    section with ONLY owned packages — so a customer who owns none saw nothing here, and the
                    per-step anchor pointed at a section that appeared to say nothing (reported the same day:
                    *"the booking pages does not have the packages you can buy section on them"*).

                    So this section states WHAT IS ON OFFER (name, sessions, validity, price, saving) and the
                    payment step carries the same offers with their descriptions. Not a duplicate of the top
                    banner that was removed: that one repeated a LINK, this one is the catalogue the link promises,
                    and it is deliberately the compact form so it does not bury the owned cards. */}
                {forSaleShownInSection && (
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        marginTop: "8px",
                        paddingTop: "14px",
                        borderTop: "2px solid #e5e7eb",
                      }}
                    >
                      <h4 style={{ margin: 0, fontSize: isMobile ? "14px" : "15px", fontWeight: "700", color: "#1e40af" }}>
                        💎 Packages you can buy
                      </h4>
                      <p style={{ margin: "2px 0 0 0", fontSize: "13px", color: "#6b7280" }}>
                        Buy one now and save on this and later bookings — these show a
                        price, because you have not paid for them yet.
                      </p>
                      <div style={{ display: "grid", gap: "12px", marginTop: "12px" }}>
                        {offerablePackages.map((pkg) => {
                          const currency = isFreelancerBooking
                            ? staff?.currency ||
                              freelancerBusiness?.currency ||
                              "USD"
                            : shop?.currency || "USD";
                          const price = Number(pkg.packagePrice) || 0;
                          const regular = Number(pkg.regularPrice) || 0;
                          // THE SAME CARD THE PAYMENT STEP USES (user, 2026-10-19: *"display the packages on all the
                          // other pages with the same style you used for the summary page"*): name, sessions and
                          // validity, then the price with the struck-through regular price and the saving.
                          return (
                            <div
                              key={pkg._id}
                              style={{
                                background: "#f9fafb",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                padding: "16px",
                              }}
                            >
                              <div style={{ marginBottom: "8px" }}>
                                <h4
                                  style={{
                                    margin: "0 0 4px 0",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#374151",
                                  }}
                                >
                                  💎 {pkg.name}
                                </h4>
                                <p style={{ margin: 0, fontSize: "14px", color: "#6b7280" }}>
                                  {pkg.totalSessions} sessions
                                  {pkg.validityPeriod?.value > 0 &&
                                    ` • Valid ${pkg.validityPeriod.value} ${pkg.validityPeriod.unit || "days"}`}
                                </p>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: "8px",
                                  marginTop: "8px",
                                }}
                              >
                                <span style={{ fontSize: "16px", fontWeight: "bold", color: "#059669" }}>
                                  {formatCurrency(price, currency)}
                                </span>
                                {regular > price && (
                                  <>
                                    <span
                                      style={{
                                        fontSize: "14px",
                                        color: "#9ca3af",
                                        textDecoration: "line-through",
                                      }}
                                    >
                                      {formatCurrency(regular, currency)}
                                    </span>
                                    <span style={{ fontSize: "13px", color: "#10b981", fontWeight: "600" }}>
                                      Save {formatCurrency(regular - price, currency)}!
                                    </span>
                                  </>
                                )}
                              </div>

                              {pkg.description && (
                                <p
                                  style={{
                                    margin: "8px 0 0 0",
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {pkg.description}
                                </p>
                              )}
                              {/* ACTIONABLE, NOT ADVERTISING (2026-10-26). Quiet here; primary on the payment
                                  step. Buying saves the booking draft first, so returning loses nothing. */}
                              <button
                                type="button"
                                onClick={() => startPackagePurchase(pkg)}
                                style={{
                                  marginTop: "12px",
                                  padding: "10px 16px",
                                  background: "#ffffff",
                                  color: "#1d4ed8",
                                  border: "1.5px solid #1d4ed8",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                }}
                              >
                                Buy &amp; save — you&apos;ll come back here
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

              </div>

              {selectedPackage && (
                <div style={{ marginTop: "16px", textAlign: "center" }}>
                  <button
                    type="button"
                    onClick={handleClearPackage}
                    style={{
                      padding: "8px 16px",
                      background: "#f3f4f6",
                      color: "#374151",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Pay Separately Instead
                  </button>
                </div>
              )}
            </section>
          )}

          {/* THE SUMMARY STEP MUST NEVER BE AN EMPTY PANEL (reported 2026-10-26: "getting to the summary page
              shows nothing").

              Everything that makes the payment step meaningful — the rows, the tip, the Pay button, and the
              blockers saying what is missing — sits inside `showFinalCheckoutStep`, which requires the booking to
              be READY as well as the step to be open. So a customer who lands here with one field outstanding got
              a heading and nothing else, with no clue what to fix. (Reachable in practice after buying a package
              from inside the booking: the draft restores the appointment but deliberately NOT the SMS consent, so
              the booking is one tick short of ready.)

              This panel shows the SAME blocker list the ready state shows, and each row takes them to the step that
              needs them. It renders only when the step is open and the booking is not ready, so the two can never
              both appear. */}
          {expandedSection === "summary" && !summaryReady && (
            <section
              className="form-section price-summary"
              ref={summarySectionRef}
              style={{ ...getSectionBlockStyle("attention") }}
            >
              <SectionStatusHeader
                title="Tip, Summary & Policy"
                state="attention"
                isMobile={isMobile}
                hideTopControls
                sectionExpanded
              />
              <BlockerChipsPanel
                title="Before submitting, resolve:"
                blockers={submitBlockers}
                onBlockerClick={handleSubmitBlockerClick}
              />
            </section>
          )}

          {showFinalCheckoutStep && (
            <>
              {/* Tip Selection — SHOWN EVEN WHEN A PACKAGE PAYS (split payment, 2026-10-27).
                  It used to be hidden whenever `selectedPackage` was set, because a tip has to be charged to a
                  card and a package booking took no card payment at all — so the tip was silently impossible.
                  A package booking can now have a card half (an uncovered service, products, or the tip itself),
                  so the tip is charged to that card. When the package covers everything and no tip is given,
                  there is nothing to charge and no card step is created. */}
              {(!selectedPackage || selectedPackageCheck?.ok !== false) && (
              <section className="form-section">
                <h3>Add a Tip</h3>
                <p
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "14px",
                    color: "#6b7280",
                  }}
                >
                  Tip amount calculated on service only (not products)
                </p>
                <div className="tip-options">
                  {[0, 12, 15, 18, 20].map((percent) => (
                    <label key={percent} className="tip-option">
                      <input
                        type="radio"
                        name="tip"
                        value={percent}
                        checked={!tipIsCustom && tipPercentage === percent}
                        onChange={() => {
                          setTipIsCustom(false);
                          setTipPercentage(percent);
                        }}
                      />
                      <span className="tip-label">
                        {percent === 0 ? "No tip" : `${percent}%`}
                        <small>
                          {percent === 0
                            ? "$0.00"
                            : formatCurrency(
                                (totals.tipBaseServiceTotal * percent) / 100,
                                isFreelancerBooking
                                  ? staff?.currency ||
                                      freelancerBusiness?.currency ||
                                      "USD"
                                  : shop?.currency || "USD",
                              )}
                        </small>
                      </span>
                    </label>
                  ))}
                  <label className="tip-option">
                    <input
                      type="radio"
                      name="tip"
                      value="custom"
                      checked={tipIsCustom}
                      onChange={() => setTipIsCustom(true)}
                    />
                    <span className="tip-label">
                      Custom
                      <small>Your amount</small>
                    </span>
                  </label>
                </div>

                {tipIsCustom && (
                  <div style={{ marginTop: "12px" }}>
                    <label
                      htmlFor="custom-tip-amount"
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        color: "#374151",
                      }}
                    >
                      Tip amount
                    </label>
                    <input
                      id="custom-tip-amount"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={customTipAmount}
                      onChange={(e) => setCustomTipAmount(e.target.value)}
                      placeholder="0.00"
                      style={{
                        width: "100%",
                        maxWidth: "220px",
                        padding: "10px 12px",
                        fontSize: "16px",
                        border: customTipError
                          ? "2px solid #dc2626"
                          : "2px solid #e0e0e0",
                        borderRadius: "8px",
                      }}
                    />
                    {customTipError ? (
                      <p
                        role="alert"
                        style={{
                          margin: "6px 0 0 0",
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#dc2626",
                        }}
                      >
                        {customTipError}
                      </p>
                    ) : (
                      <p
                        style={{
                          margin: "6px 0 0 0",
                          fontSize: "13px",
                          color: "#6b7280",
                        }}
                      >
                        Enter amount.
                      </p>
                    )}
                  </div>
                )}
              </section>
              )}

              {/* Promo Code Section intentionally hidden until finalized */}

              {/* Price Summary */}
              <section
                className="form-section price-summary"
                ref={summarySectionRef}
                style={{
                  ...getSectionBlockStyle(summaryReady ? "complete" : "attention"),
                }}
              >
            <SectionStatusHeader
              title="Tip, Summary & Policy"
              state={summaryReady ? "complete" : "attention"}
              isMobile={isMobile}
              hideTopControls
              sectionExpanded={expandedSection === "summary"}
            />
            <NotesPanel warnings={summarySoftWarnings} />
            {/* Primary customer service */}
            <div className="summary-row">
              <span>
                {primaryDisplayName} (You)
              </span>
              {selectedPackage ? (
                <span
                  style={{ textDecoration: "line-through", color: "#9ca3af" }}
                >
                  {formatCurrency(
                    primaryRowPrice || 0,
                    isFreelancerBooking
                      ? staff?.currency || freelancerBusiness?.currency || "USD"
                      : shop?.currency || "USD",
                  )}
                </span>
              ) : (
                <span>
                  {primarySelectionPending
                    ? "—"
                    : formatCurrency(
                        primaryRowPrice || 0,
                        isFreelancerBooking
                          ? staff?.currency ||
                            freelancerBusiness?.currency ||
                            "USD"
                          : shop?.currency || "USD",
                      )}
                </span>
              )}
            </div>
            {/* Primary customer add-on services */}
            {bundleServices.slice(1).map((bundleSvc) => {
              // P4: the summary must show the add-on's RESOLVED line - its own
              // units / hours / option - not the catalog values. A 5-unit add-on
              // was itemised at its single-unit price here.
              const summaryAddOnSelection =
                addOnSelections[String(bundleSvc?._id)] || {};
              const summaryAddOnLine = resolveServicePricing(
                bundleSvc,
                summaryAddOnSelection,
              );
              const summaryAddOnNeedsChoice = Boolean(
                summaryAddOnLine && summaryAddOnLine.requiresSelection,
              );
              // A package can cover some add-ons and not others (split payment), so each row says which it is.
              // The set is a display reading of the purchase; the server decides the split and the charge.
              const summaryAddOnCoveredByPackage = serviceIsCoveredByPackage(
                packageCoveredServiceIdSet(selectedPackage),
                bundleSvc,
              );

              return (
                <div
                  key={bundleSvc?._id}
                  className="summary-row"
                  style={{ paddingLeft: "16px", fontSize: "14px" }}
                >
                  <span>
                    {summaryAddOnLine?.label ||
                      bundleSvc?.name ||
                      "Add-on service"}{" "}
                    (Add-on)
                    {selectedPackage && selectedPackageCheck?.ok !== false && (
                      <span
                        style={{
                          marginLeft: "6px",
                          fontSize: "11px",
                          fontWeight: "600",
                          color: summaryAddOnCoveredByPackage ? "#059669" : "#1d4ed8",
                        }}
                      >
                        {summaryAddOnCoveredByPackage ? "covered by package" : "by card"}
                      </span>
                    )}
                  </span>
                  <span>
                    {summaryAddOnNeedsChoice
                      ? "—"
                      : formatCurrency(
                          Number(summaryAddOnLine?.totalPrice) || 0,
                          isFreelancerBooking
                            ? staff?.currency ||
                                freelancerBusiness?.currency ||
                                "USD"
                            : shop?.currency || "USD",
                        )}
                  </span>
                </div>
              );
            })}
            {/* Group members' services */}
            {groupSize > 1 &&
              groupMembers.length > 0 &&
              groupMembers.map((member, idx) => {
                const memberService = shopServices.find(
                  (s) => s._id === member.serviceId,
                );
                // P4: resolve the member's own line (same rule as calculateTotals)
                // so a per-unit / hourly member service is itemised at the right
                // amount. Falls back to the catalog price only while a required
                // choice is outstanding - P5 adds the member selector + the gate.
                // P5: the member's resolved line (same array the slot chain and
                // calculateTotals use).
                const summaryMemberLine = resolvedMemberLines[idx] || null;
                const summaryMemberNeedsChoice = Boolean(
                  summaryMemberLine && summaryMemberLine.requiresSelection,
                );
                const summaryMemberLabel =
                  summaryMemberLine?.label || memberService?.name || "Service";
                const summaryMemberPrice = summaryMemberNeedsChoice
                  ? Number(memberService?.price) || 0
                  : Number(summaryMemberLine?.totalPrice) || 0;

                return (
                  <div
                    key={idx}
                    className="summary-row"
                    style={{ paddingLeft: "16px", fontSize: "14px" }}
                  >
                    <span>
                      {summaryMemberLabel} ({member.name})
                    </span>
                    <span>
                      {summaryMemberNeedsChoice
                        ? "—"
                        : formatCurrency(
                            summaryMemberPrice,
                            isFreelancerBooking
                              ? staff?.currency ||
                                  freelancerBusiness?.currency ||
                                  "USD"
                              : shop?.currency || "USD",
                          )}
                    </span>
                  </div>
                );
              })}
            {/* Service subtotal if group */}
            {groupSize > 1 && (
              <div
                className="summary-row"
                style={{
                  fontWeight: "600",
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: "8px",
                  marginTop: "4px",
                }}
              >
                <span>Services Subtotal</span>
                <span>
                  {formatCurrency(
                    totals.serviceTotal,
                    isFreelancerBooking
                      ? staff?.currency || freelancerBusiness?.currency || "USD"
                      : shop?.currency || "USD",
                  )}
                </span>
              </div>
            )}
            {selectedPackage &&
              selectedPackageCheck?.ok !== false &&
              totals.cardServiceTotal > 0 && (
                <div className="summary-row" style={{ color: "#1d4ed8", fontWeight: "600" }}>
                  <span>Paid by card (services your package does not include)</span>
                  <span>
                    {formatCurrency(
                      totals.cardServiceTotal,
                      isFreelancerBooking
                        ? staff?.currency || freelancerBusiness?.currency || "USD"
                        : shop?.currency || "USD",
                    )}
                  </span>
                </div>
              )}
            {selectedPackage && selectedPackageCheck?.ok !== false && (
              <div
                className="summary-row"
                style={{ color: "#10b981", fontWeight: "600" }}
              >
                <span>
                  Package Credit ({groupSize} session
                  {groupSize !== 1 ? "s" : ""})
                </span>
                <span>
                  -
                  {formatCurrency(
                    totals.packageCoveredAmount || 0,
                    isFreelancerBooking
                      ? staff?.currency || freelancerBusiness?.currency || "USD"
                      : shop?.currency || "USD",
                  )}
                </span>
              </div>
            )}
            {/* 22D: A CHOSEN PACKAGE THAT NO LONGER COVERS THE BOOKING IS SAID ONCE, under the disabled
                button — NOT here as well. This row used to print the same sentence as the notice beside the
                button, and the user's screenshot showed three boxes saying one thing (2026-10-11).
                The credit row above is already absent in that case, so the price underneath is correct. */}
            {totals.productsTotal > 0 && (
              <div className="summary-row" style={{ flexDirection: "column", alignItems: "stretch", gap: "6px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                  <span>Products ({selectedProducts.length})</span>
                  <span>
                    {formatCurrency(
                      totals.productsTotal,
                      isFreelancerBooking
                        ? staff?.currency || freelancerBusiness?.currency || "USD"
                        : shop?.currency || "USD",
                    )}
                  </span>
                </div>
                <div style={{ display: "grid", gap: "6px", paddingLeft: "8px" }}>
                  {selectedProducts.map((product) => (
                    <div
                      key={product.productId}
                      style={{ display: "flex", justifyContent: "space-between", gap: "12px", fontSize: "12px", color: "#475569" }}
                    >
                      <span>
                        {product.name}
                        {getProductVariantSummary(product)
                          ? ` • ${getProductVariantSummary(product)}`
                          : ""}
                        {` x${product.quantity}`}
                      </span>
                      <span>
                        {formatCurrency(
                          product.price * product.quantity,
                          isFreelancerBooking
                            ? staff?.currency || freelancerBusiness?.currency || "USD"
                            : shop?.currency || "USD",
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {totals.productsTotal > 0 && (
              <div
                className="summary-row"
                style={{ fontSize: "12px", color: "#6b7280" }}
              >
                <span>
                  {taxPreview.calculatedAtCheckout === false && Number(taxPreview.taxRate) > 0
                    ? `Tax (${Number(taxPreview.taxRate).toFixed(2)}%)`
                    : "Tax"}
                </span>
                <span>
                  {taxLoading
                    ? "Calculating..."
                    : taxPreview.calculatedAtCheckout === false
                      ? formatCurrency(
                          totals.taxAmount,
                          isFreelancerBooking
                            ? staff?.currency ||
                                freelancerBusiness?.currency ||
                                "USD"
                            : shop?.currency || "USD",
                        )
                      : "Calculated at checkout"}
                </span>
              </div>
            )}
            {appliedPromo && (
              <div
                className="summary-row"
                style={{ color: "#10b981", fontWeight: "600" }}
              >
                <span>Promo Code ({appliedPromo.code})</span>
                <span>
                  -
                  {formatCurrency(
                    totals.promoDiscount,
                    isFreelancerBooking
                      ? staff?.currency || freelancerBusiness?.currency || "USD"
                      : shop?.currency || "USD",
                  )}
                </span>
              </div>
            )}
            <div className="summary-row">
              <span>
                {tipIsCustom
                  ? "Tip"
                  : tipPercentage === 0
                    ? "Tip"
                    : `Tip (${tipPercentage}%)`}
              </span>
              <span>
                {formatCurrency(
                  totals.tipAmount,
                  isFreelancerBooking
                    ? staff?.currency || freelancerBusiness?.currency || "USD"
                    : shop?.currency || "USD",
                )}
              </span>
            </div>
            <div className="summary-row total">
              <h3 style={{ fontSize: "12px", margin: 0, fontWeight: "700" }}>
                Total
              </h3>
              <h3 style={{ fontSize: "12px", margin: 0, fontWeight: "700" }}>
                {formatCurrency(
                  totals.total,
                  isFreelancerBooking
                    ? staff?.currency || freelancerBusiness?.currency || "USD"
                    : shop?.currency || "USD",
                )}
              </h3>
            </div>
            <div
              style={{
                marginTop: "14px",
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <button
                type="button"
                style={nextButtonStyle}
                onClick={() => goToPreviousSection("summary")}
              >
                Back
              </button>
              <button
                type="button"
                style={{
                  ...nextButtonStyle,
                  visibility: "hidden",
                }}
                disabled
              >
                Next
              </button>
            </div>
              </section>
            </>
          )}

          {error && <div className="form-error">{error}</div>}

          {showFinalCheckoutStep && (
            <>
              {/* Cancellation Policy Acknowledgement */}
              <div
                ref={policySectionRef}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  marginBottom: "16px",
                  padding: "12px 14px",
                  background: policyAccepted ? "#f0fdf4" : "#fff7ed",
                  border: `1px solid ${policyAccepted ? "#bbf7d0" : "#fed7aa"}`,
                  borderRadius: "10px",
                  cursor: "pointer",
                }}
                onClick={() => setPolicyAccepted((v) => !v)}
              >
                <input
                  type="checkbox"
                  id="cancellation-policy-accept"
                  checked={policyAccepted}
                  onChange={(e) => {
                    e.stopPropagation();
                    setPolicyAccepted(e.target.checked);
                  }}
                  style={{ marginTop: "2px", flexShrink: 0, cursor: "pointer" }}
                />
                <label
                  htmlFor="cancellation-policy-accept"
                  style={{
                    fontSize: "13px",
                    color: "#374151",
                    lineHeight: "1.5",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  I acknowledge and accept the{" "}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPolicyModal(true);
                    }}
                    style={{
                      color: "#2563eb",
                      textDecoration: "underline",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      fontSize: "inherit",
                      fontFamily: "inherit",
                    }}
                  >
                    Cancellation Policy
                  </button>
                  {
                    " — see fee schedule for details, including non-refundable processing fees."
                  }
                </label>
              </div>

              {/* ── WHAT THE PACKAGE DOES NOT COVER, ACKNOWLEDGED BEFORE PAYING (2026-10-27) ────────
                  Asked for by the user: *"We need to get an acknowledgement from the users when a package will not
                  be able to pay for a service that's uncovered."* It names the figure and what it is for, because
                  "some of this is not covered" is not something a customer can check against anything. */}
              {splitNeedsAcknowledgement && (
                <div
                  ref={splitAcknowledgementRef}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    marginBottom: "16px",
                    padding: "12px 14px",
                    background: splitAcknowledged ? "#f0fdf4" : "#eff6ff",
                    border: `1px solid ${splitAcknowledged ? "#bbf7d0" : "#bfdbfe"}`,
                    borderRadius: "10px",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setSplitAckSignature((current) =>
                      current === splitSignature ? "" : splitSignature,
                    )
                  }
                >
                  <input
                    type="checkbox"
                    id="split-payment-accept"
                    checked={splitAcknowledged}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSplitAckSignature(
                        e.target.checked ? splitSignature : "",
                      );
                    }}
                    style={{ marginTop: "2px", flexShrink: 0, cursor: "pointer" }}
                  />
                  <label
                    htmlFor="split-payment-accept"
                    style={{
                      fontSize: "13px",
                      color: "#374151",
                      lineHeight: 1.5,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    I understand my package does not cover everything in this booking.{" "}
                    <strong>
                      {formatCurrency(
                        totals.cardServiceTotal,
                        isFreelancerBooking
                          ? staff?.currency || freelancerBusiness?.currency || "USD"
                          : shop?.currency || "USD",
                      )}
                    </strong>{" "}
                    for the service it does not cover
                    {selectedProducts.length > 0 ? (
                      <>
                        {", and "}
                        <strong>
                          {formatCurrency(
                            totals.productsTotal,
                            isFreelancerBooking
                              ? staff?.currency || freelancerBusiness?.currency || "USD"
                              : shop?.currency || "USD",
                          )}
                        </strong>{" "}
                        for the {selectedProducts.length} product
                        {selectedProducts.length === 1 ? "" : "s"}
                      </>
                    ) : null}
                    , will be charged to my card now.
                  </label>
                </div>
              )}

              {/* The repeated "Payment processing fees are non-refundable…" line that sat here is GONE (user,
                  2026-10-17: *"Its redundant, not needed"*). The acknowledgement above already says *"see fee
                  schedule for details, including non-refundable processing fees"*, and the policy modal states
                  the rule in full — saying it a third time under the checkbox adds nothing and reads as a
                  second, separate warning the customer has to decode. */}

              {(waitlistCompatibilityLoading ||
                waitlistCompatibility?.hasPendingWaitlist) && (
                <div
                  ref={waitlistMismatchSectionRef}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    marginBottom: "16px",
                    padding: "12px 14px",
                    background:
                      waitlistCompatibility?.compatible === false
                        ? "#fff7ed"
                        : "#ecfdf5",
                    border: `1px solid ${
                      waitlistCompatibility?.compatible === false
                        ? "#fed7aa"
                        : "#86efac"
                    }`,
                    borderRadius: "10px",
                  }}
                >
                  {waitlistCompatibility?.compatible === false && (
                    <input
                      type="checkbox"
                      id="waitlist-mismatch-accept"
                      checked={waitlistMismatchAcknowledged}
                      onChange={(e) =>
                        setWaitlistMismatchAcknowledged(e.target.checked)
                      }
                      style={{ marginTop: "2px", flexShrink: 0, cursor: "pointer" }}
                    />
                  )}
                  <label
                    htmlFor="waitlist-mismatch-accept"
                    style={{
                      fontSize: "13px",
                      color:
                        waitlistCompatibility?.compatible === false
                          ? "#9a3412"
                          : "#065f46",
                      lineHeight: "1.5",
                      cursor:
                        waitlistCompatibility?.compatible === false
                          ? "pointer"
                          : "default",
                      userSelect: "none",
                    }}
                  >
                    {waitlistCompatibilityLoading
                      ? "Checking pending waitlist compatibility..."
                      : waitlistCompatibility?.compatible === false
                        ? "I understand this booking does not match my pending waitlist request. If I continue, that waitlist request will be cancelled."
                        : waitlistCompatibility?.message}
                  </label>
                </div>
              )}

              {/* Phase 18 — a package is a PAYMENT MEANS. Its entry point is rendered next to the
                  button that takes payment, below the blockers the customer must clear first. */}

              {guestPackageNotice && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "12px",
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    borderRadius: "10px",
                    padding: "12px 14px",
                    marginBottom: "12px",
                    fontSize: "13px",
                    color: "#9a3412",
                  }}
                >
                  <span style={{ flex: 1 }}>{guestPackageNotice}</span>
                  <button
                    type="button"
                    onClick={() => setGuestPackageNotice("")}
                    aria-label="Dismiss package notice"
                    style={{
                      background: "none",
                      border: "none",
                      color: "#9a3412",
                      cursor: "pointer",
                      fontSize: "16px",
                      lineHeight: 1,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              {waitlistOnlyPath ? (
                <>
                  <div
                    style={{
                      padding: "12px 14px",
                      borderRadius: "10px",
                      border: "1px solid #86efac",
                      background: "#ecfdf5",
                      color: "#065f46",
                      fontSize: "13px",
                      fontWeight: "600",
                      marginBottom: "10px",
                    }}
                  >
                    Waitlist request complete. No payment is required until a time becomes available.
                  </div>
                  <p className="payment-note" style={{ marginTop: 0 }}>
                    We will notify you when a matching appointment opens.
                  </p>
                </>
              ) : (
                <>
                  <BlockerChipsPanel
                    title="Before submitting, resolve:"
                    blockers={submitBlockers}
                    onBlockerClick={handleSubmitBlockerClick}
                  />
                  {/* ── WHY A PACKAGE IS NOT BEING OFFERED, SAID WHERE THE DECISION IS (user, 2026-10-11) ──

                      This sat inside the packages section, far above the button the customer actually
                      presses, so somebody could reach the payment step without ever passing it. It sits here
                      instead, immediately above "Use my package" and the Pay button — the same place for a
                      signed-in customer, who has no such button, as for a guest who has just verified.

                      TWO REASONS, TWO SENTENCES, because they have different answers: the professional does
                      not offer the package (choose another professional), or the package has nothing left for
                      THIS service (book something else, or pay by card). A customer who owns packages and
                      sees none must never be left wondering whether they were lost. */}
                  {(ownedPackagesWithheld > 0 || ownedPackagesUnusable > 0) &&
                    ownedPackagesHere.length === 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        background: "#fef3c7",
                        border: "1px solid #f59e0b",
                        borderRadius: "10px",
                        padding: "12px 14px",
                        marginBottom: "12px",
                        fontSize: "13px",
                        color: "#92400e",
                        lineHeight: 1.5,
                      }}
                    >
                      <span style={{ flex: 1 }}>
                        ⚠️{" "}
                        {ownedPackagesWithheld > 0 ? (
                          <>
                            <strong>
                              {ownedPackagesWithheld === 1
                                ? "Your package can't be used with"
                                : "Your packages can't be used with"}{" "}
                              {professionalDisplayName}.
                            </strong>{" "}
                            A package is offered only by the professionals who
                            subscribed to it, so it has to be booked with one of them.
                            Choose a professional who offers it, or pay for this
                            appointment by card.
                          </>
                        ) : (
                          <>
                            <strong>Your package can't pay for this booking.</strong>{" "}
                            {ownedPackageUnusableReason ||
                              "It has no sessions left for what you are booking."}{" "}
                            You can still book this appointment by card.
                          </>
                        )}
                      </span>
                    </div>
                  )}

                  {!isAuthenticated && !selectedPackage && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        flexWrap: "wrap",
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        borderRadius: "10px",
                        padding: "12px 14px",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "700", color: "#1e3a8a", fontSize: "14px" }}>
                          💎 Bought a package already?
                        </div>
                        <div style={{ fontSize: "13px", color: "#1e40af" }}>
                          Verify it with the email and phone you used at purchase, and use it to pay for this booking.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowGuestPackageAccess(true)}
                        style={{ padding: "10px 16px", background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)", color: "white", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
                      >
                        Use my package
                      </button>
                    </div>
                  )}

                  {/* ── THE GREEN "PAYING WITH YOUR PACKAGE" TILE IS GONE (plan item 6, 2026-10-18) ────────
                      It repeated what the summary already says twice — the "Package Credit" row states the amount
                      covered, and the submit button itself switches to the package wording — while taking a block
                      of the payment panel to do it. Its two FACTS are kept, as one line beside the button rather
                      than a panel: what the package covers, and what the card will take. */}

                  {/* THE PRODUCTS BOX IS GONE (split payment, 2026-10-27).
                      It said "the N products in this booking are paid by card" in a blue panel, while the summary
                      a few lines above already lists each product and shows a "Paid by card" row with the figure.
                      Two statements of one fact beside a third was exactly the repetition the user reported:
                      *"too many information about the same thing"*. The summary is the one place that states it. */}

                  {/* ── THE PACKAGE OFFERS, AT THE POINT OF PAYMENT (user, 2026-10-19) ────────────────
                      *"move the lost-sessions offer and 'packages you can buy' down beside the Pay button"*.
                      They were inside the package section, several screens above the decision; here they are the
                      last thing the customer reads before choosing how to pay. Nothing about them changed — the
                      figures, the "does it help THIS appointment" line and the pay-now/pay-later note all come
                      from the same server data as before.

                      It renders even when the package has NOTHING left to spend, because a package whose last
                      session was lost is exactly the case where the customer most needs to see that they can get
                      it back — and this is the only place they can. */}
                  {(redeemablePackageOffers.length > 0 ||
                    offerablePackages.length > 0) && (
                    <div style={{ marginBottom: "16px" }}>
                      {redeemablePackageOffers.map(
                        ({ key, name, units, amount, sessionsRemaining, coversThisBooking }) => (
                          <div
                            key={`redeem-${key}`}
                            style={{
                              background: "#fffbeb",
                              border: "1px solid #fcd34d",
                              borderRadius: "8px",
                              padding: "14px",
                              marginBottom: "12px",
                            }}
                          >
                            <h4
                              style={{
                                margin: "0 0 4px 0",
                                fontSize: "14px",
                                fontWeight: "700",
                                color: "#92400e",
                              }}
                            >
                              💛 {units} session{units === 1 ? "" : "s"} lost from “{name}”
                            </h4>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#78350f",
                                lineHeight: 1.5,
                              }}
                            >
                              A late cancellation lost {units} session
                              {units === 1 ? "" : "s"} from this package.
                              {sessionsRemaining > 0
                                ? ` Your other ${sessionsRemaining} session${
                                    sessionsRemaining === 1 ? "" : "s"
                                  } still work as normal — nothing here is blocked.`
                                : " That was the last of them, and this is how to get it back."}
                              {" Pay $"}
                              {Number(amount).toFixed(2)} and {"they go straight back on the package."}
                            </p>

                            <p
                              style={{
                                margin: "6px 0 0 0",
                                fontSize: "12px",
                                fontWeight: "600",
                                color: coversThisBooking ? "#065f46" : "#6b7280",
                                lineHeight: 1.5,
                              }}
                            >
                              {coversThisBooking
                                ? "✅ Your package covers everything in this appointment, so once these are back you can book it with the package."
                                : "ℹ️ These sessions are for other services, so they will still be here for a future booking — this appointment is not one your package can pay for."}
                            </p>
                            <button
                              type="button"
                              disabled={paymentProcessing}
                              onClick={() => redeemHeldPackageSessions(key)}
                              style={{
                                marginTop: "10px",
                                padding: "8px 14px",
                                background: paymentProcessing ? "#9ca3af" : "#b45309",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                fontSize: "13px",
                                fontWeight: "600",
                                cursor: paymentProcessing ? "not-allowed" : "pointer",
                              }}
                            >
                              {paymentProcessing
                                ? "Opening payment…"
                                : `Pay $${Number(amount).toFixed(2)} to get ${
                                    units === 1 ? "it" : "them"
                                  } back`}
                            </button>

                            <p
                              style={{
                                margin: "8px 0 0 0",
                                fontSize: "12px",
                                color: "#92400e",
                                lineHeight: 1.5,
                              }}
                            >
                              Paying later is fine — this offer stays on your booking page,
                              and your other sessions work normally in the meantime.
                            </p>

                            {paymentUrl && (
                              <p style={{ margin: "8px 0 0 0", fontSize: "12px" }}>
                                <a
                                  href={paymentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: "#b45309", fontWeight: "600" }}
                                >
                                  Open the payment page
                                </a>
                              </p>
                            )}
                          </div>
                        ),
                      )}

                      {offerablePackages.length > 0 && (
                        <div
                          style={{
                            marginTop: "6px",
                            paddingTop: "14px",
                            borderTop: "2px solid #e5e7eb",
                          }}
                        >
                          <h4 style={{ margin: 0, fontSize: isMobile ? "14px" : "15px", fontWeight: "700", color: "#1e40af" }}>
                            💎 Packages you can buy
                          </h4>
                          <p style={{ margin: "2px 0 0 0", fontSize: "13px", color: "#6b7280" }}>
                            Buy one now and save on this and later bookings — these show a
                            price, because you have not paid for them yet.
                          </p>
                        </div>
                      )}
                      {offerablePackages.map((pkg) => (
                        <div
                          key={pkg._id}
                          style={{
                            background: "#f9fafb",
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            padding: "16px",
                            position: "relative",
                            marginTop: "12px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "8px",
                            }}
                          >
                            <div>
                              <h4
                                style={{
                                  margin: "0 0 4px 0",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  color: "#374151",
                                }}
                              >
                                💎 {pkg.name}
                              </h4>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "14px",
                                  color: "#6b7280",
                                }}
                              >
                                {pkg.totalSessions} sessions
                                {pkg.validityPeriod?.value > 0 &&
                                  ` • Valid ${pkg.validityPeriod.value} ${pkg.validityPeriod.unit || "days"}`}
                              </p>
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: "8px",
                              marginTop: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "bold",
                                color: "#059669",
                              }}
                            >
                              {formatCurrency(
                                pkg.packagePrice || 0,
                                isFreelancerBooking
                                  ? staff?.currency ||
                                      freelancerBusiness?.currency ||
                                      "USD"
                                  : shop?.currency || "USD",
                              )}
                            </span>
                            {pkg.regularPrice &&
                              pkg.regularPrice > (pkg.packagePrice || 0) && (
                                <>
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: "#9ca3af",
                                      textDecoration: "line-through",
                                    }}
                                  >
                                    {formatCurrency(
                                      pkg.regularPrice || 0,
                                      isFreelancerBooking
                                        ? staff?.currency ||
                                            freelancerBusiness?.currency ||
                                            "USD"
                                        : shop?.currency || "USD",
                                    )}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      color: "#10b981",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Save{" "}
                                    {formatCurrency(
                                      (pkg.regularPrice || 0) -
                                        (pkg.packagePrice || 0),
                                      isFreelancerBooking
                                        ? staff?.currency ||
                                            freelancerBusiness?.currency ||
                                            "USD"
                                        : shop?.currency || "USD",
                                    )}
                                    !
                                  </span>
                                </>
                              )}
                          </div>

                          <p
                            style={{
                              margin: "8px 0 0 0",
                              fontSize: "12px",
                              color: "#6b7280",
                              fontStyle: "italic",
                            }}
                          >
                            {pkg.description}
                          </p>
                          {/* THE PRIMARY PLACE TO BUY FROM: they are at the payment step, so the amount it saves
                              is about to matter. Same draft-then-return rule as the section above. */}
                          <button
                            type="button"
                            onClick={() => startPackagePurchase(pkg)}
                            style={{
                              marginTop: "12px",
                              width: "100%",
                              padding: "11px 16px",
                              background: "#1d4ed8",
                              color: "#ffffff",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: "14px",
                              fontWeight: "700",
                              cursor: "pointer",
                            }}
                          >
                            Buy this package &amp; return to this booking
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* IMMEDIATELY ABOVE THE BUTTON, so it is the last thing read before pressing it.
                      ONE SENTENCE FOR BOTH TENDERS (split payment, 2026-10-27). It used to end "no card payment is
                      taken", which stopped being true the moment a package could leave an uncovered service, a
                      product or a tip for the card — and the customer would have read that directly above a button
                      that sends them to pay. The figure is the same one the summary totals. */}
                  {selectedPackage && !packageCannotPay && (
                    <p
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "13px",
                        color: "#065f46",
                        textAlign: "center",
                        lineHeight: 1.5,
                      }}
                    >
                      ✅ This booking uses {packageSessionsUsed} session
                      {packageSessionsUsed === 1 ? "" : "s"} of your package
                      {totals.total > 0
                        ? ` — ${formatCurrency(
                            totals.total,
                            isFreelancerBooking
                              ? staff?.currency || freelancerBusiness?.currency || "USD"
                              : shop?.currency || "USD",
                          )} is paid by card in the same step.`
                        : " — no card payment is taken."}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="submit-booking-btn"
                    disabled={
                      paymentProcessing ||
                      primarySelectionPending ||
                      !selectedSlot ||
                      !policyAccepted ||
                      !customerInfo.phone.trim() ||
                      // A CHOSEN PACKAGE THAT CANNOT PAY FOR THIS BOOKING REFUSES THE BUTTON (user,
                      // 2026-10-11: "grey out the confirm booking with package when its not possible").
                      // Leaving it live would send a booking the server refuses, or — worse, once the
                      // selection is not sent — charge by card behind a button that reads "with package".
                      packageCannotPay ||
                      // …and a split the customer has not acknowledged is not payable either.
                      (splitNeedsAcknowledgement && !splitAcknowledged)
                    }
                  >
                    {paymentProcessing
                      ? "Processing..."
                      : selectedPackage
                        ? totals.total > 0
                          ? `Confirm — pay ${formatCurrency(totals.total, isFreelancerBooking ? staff?.currency || freelancerBusiness?.currency || "USD" : shop?.currency || "USD")} by card`
                          : "Confirm booking with package"
                        : `Pay ${formatCurrency(totals.total, isFreelancerBooking ? staff?.currency || freelancerBusiness?.currency || "USD" : shop?.currency || "USD")} with Stripe`}
                  </button>

                  {/* STRIPE'S OWN WORDS ONLY WHEN STRIPE IS PAYING. With a package selected this line
                      said "Paid with your package — one session is used…" directly under the green box
                      that says the same thing, and while a package was REFUSING the booking it still
                      said "Paid with your package", which is the opposite of what the page was about to
                      do. The green box carries the package case; the blocked note carries the refusal. */}
                  {!selectedPackage && (
                    <p className="payment-note">
                      Secure payment via Stripe • Required to confirm reservation • 10-minute slot hold
                    </p>
                  )}

                  {/*
                    WHY THE PAY BUTTON IS DEAD, SAID OUT LOUD.

                    The button is `disabled` on four conditions, and a disabled button runs NO handler —
                    so nothing sets `error`, nothing opens, and nothing looks wrong: the customer presses
                    Pay and the page simply does not respond. Reported 2026-10-07 ("clicking the pay with
                    stripe button does nothing"), and it cost a real debugging session because the screen
                    offered no clue which of the four was unmet.

                    The guard is right; the silence was not. Any condition that can refuse an action must
                    also be able to say so — that is the same rule already applied to disabled labels and
                    instruction text elsewhere in this codebase.

                    Evaluated inline, in the same scope as `disabled` itself, so it can never disagree
                    with the button and cannot be hit by a temporal-dead-zone read of a later `const`.
                  */}
                  {!paymentProcessing &&
                    (packageCannotPay ||
                      !selectedSlot ||
                      !policyAccepted ||
                      !customerInfo.phone.trim() ||
                      primarySelectionPending) && (
                      <p className="payment-note payment-note--blocked" role="status">
                        {packageCannotPay
                          ? `${selectedPackageCheck?.message || "This package cannot pay for this booking."} Use "Pay Separately Instead" above to book it by card.`
                          : !selectedSlot
                            ? "No time is selected, so there is nothing to pay yet. Go back to the date and time step and choose a slot."
                            : !policyAccepted
                              ? "Tick the cancellation policy box above to continue."
                              : !customerInfo.phone.trim()
                                ? "A phone number is required to confirm a booking. Add it in your details above."
                                : "Choose your service options above — the price is not settled yet, so this booking cannot be paid for."}
                      </p>
                    )}
                </>
              )}
            </>
          )}
        </form>

        <CancellationPolicyModal
          open={showPolicyModal}
          onClose={() => setShowPolicyModal(false)}
          policy={cancellationPolicy}
        />

        {showCartWarningModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "16px",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                maxWidth: "520px",
                width: "100%",
                padding: "20px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#111827",
                }}
              >
                You Have Cart Items
              </h3>
              <p
                style={{
                  margin: "0 0 16px 0",
                  color: "#4b5563",
                  fontSize: "13px",
                  lineHeight: 1.45,
                }}
              >
                You currently have {itemCount} item{itemCount > 1 ? "s" : ""} in
                your cart. Cart items are not included in this booking payment.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setCartWarningAcknowledged(true);
                    setShowCartWarningModal(false);
                  }}
                  style={{
                    padding: "10px 14px",
                    background: "#111827",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  Continue Booking Only
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/cart")}
                  style={{
                    padding: "10px 14px",
                    background: "#e5e7eb",
                    color: "#111827",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  Review Cart
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/checkout")}
                  style={{
                    padding: "10px 14px",
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  Go To Cart Checkout
                </button>
                <button
                  type="button"
                  onClick={() => setShowCartWarningModal(false)}
                  style={{
                    padding: "10px 14px",
                    background: "transparent",
                    color: "#6b7280",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "13px",
                    cursor: "pointer",
                    marginLeft: "auto",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Authentication Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode={authModalMode}
          showGuestOption={true}
          onGuestCheckout={() => {
            setShowAuthModal(false);
            setHasChosenGuestCheckout(true);
            setIsAuthenticated(false);
            // Guest - customerInfo stays empty for manual entry
          }}
          onSuccess={async (data) => {
            setIsAuthenticated(true);
            setHasChosenGuestCheckout(false);
            setShowAuthModal(false);

            // Fetch user profile and auto-fill customer info
            try {
              const token = localStorage.getItem("ogestyle_token");
              const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/profile`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                },
              );

              if (response.ok) {
                const user = await response.json();
                setCustomerInfo({
                  firstName: user.firstName || user.name?.split(" ")[0] || "",
                  lastName: user.lastName || user.name?.split(" ")[1] || "",
                  email: user.email || "",
                  phone: user.phone || "",
                });
              }
            } catch (error) {
            }

            // Refresh packages after login
            fetchCustomerPackages();
          }}
        />

        {/* Product Quick View Modal */}
        {showQuickViewModal && (
          <ProductQuickViewModal
            product={quickViewProduct}
            onClose={() => {
              setShowQuickViewModal(false);
              setQuickViewProduct(null);
            }}
            onAddToBooking={(product, selectedVariant) => {
              toggleProduct(product, selectedVariant);
            }}
          />
        )}
      </div>
      <CustomerFooter />
    </>
  );
};

export default CustomerBooking;


